import { css, LitElement, html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';

const sharedStyles = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  :host {
    display: block;
    width: 100%;
    line-height: var(--default-line-height);
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
  button:focus,
  a:focus,
  .fwc-toggle input:focus + label,
  .fwc-select-list select:focus,
  .fwc-radio input:focus,
  .fwc-input input:focus,
  input:focus + label,
  .fwc-checkbox input:focus + label:before,
  .fwc-carousel-generic-wrapper .fwc-list-cont:focus,
  div.tip-btn:focus {
    outline: var(--fwc-focus);
  }
`;

class FwcAlertComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          overflow: hidden;
          position: relative;
        }
        .fwc-alert-flex svg {
          flex-shrink: 0;
          margin-right: calc(1.5 * var(--fwcu));
        }
        .fwc-alert-highlight {
          position: absolute;
          box-sizing: content-box;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }
        .fwc-alert-highlight.show {
          animation-name: fwcHighlight;
          animation-delay: 0ms;
          animation-duration: 2s;
          animation-timing-function: ease-out;
          animation-fill-mode: both;
          transform-origin: 50% 50%;
        }

        .fwc-alert-row {
          margin-top: 0;
          transition: all 1s ease-in;
          margin-bottom: calc(0.25 * var(--fwcu));
        }

        .fwc-alert-row-full {
          animation-name: fwcGrow;
          animation-delay: 0ms;
          animation-duration: 2s;
          animation-timing-function: ease-out;
          animation-fill-mode: none;
          transform-origin: 0% 50%;
        }
        .fwc-alert-row-full .fwc-alert-wrapper {
          max-width: calc(144 * var(--fwcu));
          margin: 0 auto;
        }
        .fwc-alert-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          line-height: 1;
          padding: calc(var(--fwcu)) calc(1.5 * var(--fwcu));
          font-size: calc(2 * var(--fwcu));
        }

        @media screen and (max-width: 600px) {
          .fwc-alert-wrapper {
            /*
              There is no col padding to add to:
              4*fwcu = 32px for mobile
            */
            padding-right: calc(4 * var(--fwcu));
            padding-left: calc(4 * var(--fwcu));
          }
        }

        .fwc-alert-row.remove {
          margin-top: -100%;
          overflow: hidden;
        }
        .fwc-alert-flex {
          display: flex;
          align-items: center;
        }

        .success-fill {
          fill: var(--color-alert-success);
        }
        .success-color {
          color: var(--color-alert-success);
        }

        .info-fill {
          fill: var(--color-alert-info);
        }
        .info-color {
          color: var(--color-alert-info);
        }

        .red-fill {
          fill: var(--color-alert-error);
        }
        .red-color {
          color: var(--color-alert-error);
        }

        .close-fill {
          fill: var(--color-gray-two);
        }

        .bg-white {
          background-color: var(--color-white);
        }
        .bg-default {
          background-color: var(--color-gray-five);
        }

        button {
          border: none;
          background: transparent;
          cursor: pointer;
          right: var(--fwcu);
          padding: calc(0.5 * var(--fwcu));
          display: flex;
          align-items: center;
        }

        button svg {
          width: calc(2 * var(--fwcu));
          height: calc(2 * var(--fwcu));
        }

        @keyframes fwcHighlight {
          from {
            background: var(--color-dviz-eight);
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @keyframes fwcGrow {
          from {
            max-height: 0;
          }
          to {
            max-height: 25rem;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      type: { type: String }, // urgent | success | info | error
      area: { type: String }, // global | inline
      labelAriaCloseBtn: { attribute: "label-aria-close-btn", type: String },
      bgColor: { attribute: "bg-color", type: String }, // white | null
    };
  }

  constructor() {
    super();
    this._refocusElem = null;
    this.type = "info";
    this.area = "inline";
    this.labelAriaCloseBtn = "Close Alert";
    this.bgColor = "";
    this.closing = false;
    this._onEnd = () => {
      if (this.parentElement && this.closing) {
        this.parentElement.removeChild(this);
        this.closing = false;
        const event = new CustomEvent("fwc-alert-closed", {
          detail: this,
          bubbles: true,
        });
        this.dispatchEvent(event);
        this._returnFocus();
      }
    };
  }

  firstUpdated() {
    const wrapper = this.shadowRoot.getElementById("fwcAlertRow");
    wrapper.addEventListener("transitionend", this._onEnd);
    if (this.area === "global") this.classList.add("fwc-container");
  }

  _xIcon(color = "") {
    const style = color === "red" ? "red-fill" : "close-fill";
    return html`
      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 50 50" focusable="false">
        <polygon class="${style}" points="3.523 50 0 46.477 21.477 25 0 3.523 3.523 0 25 21.477 46.477 0 50 3.523 28.523 25 50 46.477 46.477 50 25 28.523" />
      </svg>
    `;
  }

  _setBackground() {
    let bg = "";
    switch (this.bgColor) {
      case "white":
        bg = "bg-white";
        break;
      default:
        bg = "bg-default";
    }
    return bg;
  }

  _buildIcon() {
    const success = html`
      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 50 50" focusable="false">
        <polygon class="success-fill" points="47.38 43.674 15.888 12.182 2.62 25.451 0 22.831 14.579 8.253 15.888 7 17.198 8.253 50 41.055" transform="matrix(1 0 0 -1 0 50.674)" />
      </svg>
    `;
    const info = html`
      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 50 50" focusable="false">
        <path class="info-fill" d="M25,50 C11.2154447,50 0,38.7845553 0,25 C0,11.2154447 11.2154447,0 25,0 C38.7845553,0 50,11.2154447 50,25 C50,38.7845553 38.7845553,50 25,50 Z M25,46.1538462 C36.703726,46.1538462 46.1538462,36.703726 46.1538462,25 C46.1538462,13.296274 36.703726,3.84615385 25,3.84615385 C13.296274,3.84615385 3.84615385,13.296274 3.84615385,25 C3.84615385,36.703726 13.296274,46.1538462 25,46.1538462 Z M23.0769231,36.5384615 L23.0769231,32.6923077 L26.9230769,32.6923077 L26.9230769,36.5384615 L23.0769231,36.5384615 Z M23.0769231,28.8461538 L23.0769231,13.4615385 L26.9230769,13.4615385 L26.9230769,28.8461538 L23.0769231,28.8461538 Z" transform="matrix(1 0 0 -1 0 50)" />
      </svg>
    `;
    const error = this._xIcon("red");
    const urgent = html`
      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 50 50" focusable="false">
        <path class="red-fill" d="M25,46.3371298 L23.405467,43.6036446 L1.53758542,5.73348519 L0,3 L50,3 L48.4624146,5.73348519 L26.594533,43.6036446 L25,46.3371298 Z M25,39.047836 L43.6788155,6.64464692 L6.32118451,6.64464692 L25,39.047836 Z M23.1776765,26.690205 L23.1776765,15.7562642 L26.8223235,15.7562642 L26.8223235,26.690205 L23.1776765,26.690205 Z M23.1776765,13.9339408 L23.1776765,10.2892938 L26.8223235,10.2892938 L26.8223235,13.9339408 L23.1776765,13.9339408 Z" transform="matrix(1 0 0 -1 0 49.337)" />
      </svg>
    `;
    const icons = {
      success: success,
      info: info,
      error: error,
      urgent: urgent,
    };
    return icons[this.type];
  }

  _onCloseClick() {
    this.closing = true;
    this.shadowRoot.getElementById("fwcAlertRow").classList.add("remove");
  }

  // a public func allowing projects to close the alert
  closeAlert() {
    this._onCloseClick();
  }

  // a public function allowing projects to put focus on the alert close button when opened
  focusOnCloseBtn() {
    setTimeout(() => {
      this.shadowRoot.getElementById("fwc-alert-close-button").focus();
    }, 250);
  }

  //private method to return focus to a element
  _returnFocus() {
    if (this._refocusElem) {
      // Return focus to calling element
      this._refocusElem.focus();
    }
  }

  // a public func allowing projects to set the refocus element can call private _returnfocus method
  setReFocus(elem) {
    this._refocusElem = elem;
  }

  render() {
    return html`
      <div class="fwc-alert-row ${this.area === "global" ? "fwc-alert-row-full" : ""} ${this._setBackground()}" id="fwcAlertRow" role="alert">
        <div class="fwc-alert-wrapper">
          <div class="fwc-alert-flex">
            ${this._buildIcon()}
            <slot id="alertMessage"></slot>
          </div>
          <button id="fwc-alert-close-button" type="button" role="button" aria-label="${this.labelAriaCloseBtn}" aria-describedby="alertMessage" @click="${this._onCloseClick}">${this._xIcon()}</button>
        </div>
      </div>
      <div class="fwc-alert-highlight ${this.area === "global" ? "show" : ""}"></div>
    `;
  }
}

class FwcBase {
  constructor(name, component) {
    this.element = customElements.get(name);
    this.name = name;
    this.component = component;
  }

  build() {
    if (!this.element) {
      customElements.define(this.name, this.component);
    }
  }
}

class FwcAlert extends FwcBase {
  constructor() {
    super("fwc-alert", FwcAlertComponent);
  }
}

class FwcAlertSnackbarItemComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-alert-snackbar {
          margin-top: calc(1.5 * var(--fwcu));
          border: 1px solid var(--color-gray-four);
          border-left: 5px solid var(--color-gray-four);
          color: var(--color-gray-one);
          font-size: calc(1.25 * var(--fwcu));
          background: var(--color-white);
          animation-name: show-snackbar;
          animation-duration: 500ms;
          box-shadow: var(--box-shadow);
        }
        .fwc-alert-snackbar.remove {
          animation-name: close-snackbar;
          animation-duration: 500ms;
          animation-fill-mode: forwards;
        }
        .fwc-alert-snackbar.info {
          border-left-color: var(--color-alert-info);
        }
        .fwc-alert-snackbar.success {
          border-left-color: var(--color-alert-success);
        }
        .fwc-alert-snackbar.error {
          border-left-color: var(--color-alert-error);
        }
        .fwc-alert-snackbar-body {
          padding: calc(3.75 * var(--fwcu)) calc(3.5 * var(--fwcu));
          position: relative;
        }
        .dismiss-button {
          padding: 0px;
          position: absolute;
          top: calc(1.25 * var(--fwcu));
          right: calc(1.75 * var(--fwcu));
          background: none;
          font-size: calc(4.5 * var(--fwcu));
          font-weight: 300;
          color: gray;
          border: none;
          line-height: calc(0.75 * var(--fwcu));
          margin: auto;
        }
        .dismiss-button:hover {
          cursor: pointer;
        }
        @keyframes show-snackbar {
          0% {
            opacity: 0;
            transform: scale(0.5);
            offset: 0;
          }
          100% {
            opacity: 1;
            transform: scale(1);
            offset: 1;
          }
        }
        @keyframes close-snackbar {
          0% {
            opacity: 1;
            offset: 0;
          }
          100% {
            opacity: 0;
            offset: 1;
          }
        }
      `,
    ];
  }

  // TODO: Aria live / region, ets
  static get properties() {
    return {
      type: { type: String },
      labelAriaCloseBtn: { attribute: "label-aria-close-btn", type: String },
      labelAriaLive: { attribute: "label-aria-live", type: String },
      timer: { attribute: "timer", type: Number },
    };
  }

  constructor() {
    super();
    this.type = "info";
    this.labelAriaCloseBtn = "close snack bar";
    this.timer = 30000;
    this.labelAriaLive = "assertive";
    this.closeSnackbar = () => {
      if (this.parentNode) {
        this.parentNode.removeChild(this);
      }
    };
  }

  firstUpdated() {
    //listen for animation end
    const wrapper = this.shadowRoot.getElementById("fwc-alert-snackbar-item");
    wrapper.addEventListener("animationend", (e) => {
      // on setTimout auto close snackbar
      if (e.animationName.indexOf("show-snackbar") > -1) {
        setTimeout(() => {
          this._onCloseClick();
        }, this.timer);
      }
      // if animation close, trigger close function
      if (e.animationName.indexOf("close-snackbar") > -1) {
        this.closeSnackbar();
      }
    });
  }

  // click close function which outputs an event and triggers close animation
  _onCloseClick() {
    this.shadowRoot.getElementById("fwc-alert-snackbar-item").classList.add("remove");
    const event = new CustomEvent("fwc-alert-snackbar-removed", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  render() {
    return html`
      <div class="fwc-alert-snackbar ${this.type}" id="fwc-alert-snackbar-item" aria-live="${this.labelAriaLive}">
        <div class="fwc-alert-snackbar-body">
          <slot></slot>
          <button type="button" class="dismiss-button" aria-label="${this.labelAriaCloseBtn}" @click="${this._onCloseClick}">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 50 50" focusable="false">
              <polygon points="3.523 50 0 46.477 21.477 25 0 3.523 3.523 0 25 21.477 46.477 0 50 3.523 28.523 25 50 46.477 46.477 50 25 28.523" />
            </svg>
          </button>
        </div>
      </div>
    `;
  }
}

class FwcAlertSnackbarComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-alert-snackbar-container {
          display: flex;
          flex-direction: column-reverse;
          position: fixed;
          left: 0px;
          bottom: 0;
          z-index: 5;
          padding: 0px;
          margin: 0 calc(3 * var(--fwcu)) calc(3 * var(--fwcu)) calc(3 * var(--fwcu));
          background: transparent;
        }
        @media screen and (min-width: 900px) {
          .fwc-alert-snackbar-container {
            width: calc(41.75 * var(--fwcu));
            margin: 0 0 calc(3 * var(--fwcu)) calc(3 * var(--fwcu));
          }
        }
      `,
    ];
  }

  constructor() {
    super();
  }

  render() {
    return html`
      <div class="fwc-alert-snackbar-container">
        <slot></slot>
      </div>
    `;
  }
}

class FwcAlertSnackbar extends FwcBase {
  constructor() {
    super("fwc-alert-snackbar", FwcAlertSnackbarComponent);
    const FwcAlertSnackbarItem = new FwcBase("fwc-alert-snackbar-item", FwcAlertSnackbarItemComponent);
    FwcAlertSnackbarItem.build();
  }
}

class FwcAnimBlobComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          display: block;
        }
        .slot-wrapper {
          position: relative;
          z-index: 1;
        }
        .fwc-anim-wrapper {
          position: relative;
          width: 100%;
          overflow: hidden;
        }
        [class*="fwc-blob-"] {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          overflow: visible;
        }
        .fwc-blob-5 {
          left: 85%;
          opacity: 1;
          width: 41%;
          padding-top: 43%;
        }
        .fwc-blob-4 {
          left: 8.5%;
          opacity: 0.78;
          width: 38%;
          padding-top: 38%;
        }
        .fwc-blob-3 {
          left: 54%;
          opacity: 0.63;
          width: 60%;
          padding-top: 38%;
        }
        .fwc-blob-2 {
          left: -20%;
          opacity: 0.73;
          width: 43%;
          padding-top: 40%;
        }
        .fwc-blob-1 {
          left: 31%;
          opacity: 0.9;
          width: 50%;
          padding-top: 41%;
        }
        [class*="fwc-blob-"] svg {
          position: absolute;
          top: 0;
          left: 0;
          animation: rotation 12s infinite linear;
        }
        @keyframes rotation {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(359deg);
          }
        }
        @media (max-width: 768px) {
          .fwc-anim-wrapper {
            min-height: 512px;
            background: var(--color-gray-five);
          }
          [class*="fwc-blob-"] {
            width: 150%;
            left: 50%;
            transform: translateY(0%) translateX(-50%);
          }
          .fwc-blob-1 {
            top: 50%;
          }
          .fwc-blob-2 {
            top: 35%;
          }
          .fwc-blob-3 {
            display: none;
          }
          .fwc-blob-4 {
            top: 9%;
          }
          .fwc-blob-5 {
            top: 7%;
          }
        }
      `,
    ];
  }

  constructor() {
    super();
  }

  render() {
    return html`
      <div class="fwc-anim-wrapper">
        <div class="fwc-blob-5">
          <svg width="100%" viewBox="0 0 627 673" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
            <path opacity="0.05" fill-rule="evenodd" clip-rule="evenodd" d="M329.175 0.0542232C397.298 -2.27704 440.409 71.0981 490.729 117.077C534.726 157.277 580.859 195.147 603.172 250.41C626.669 308.601 634.518 372.764 618.466 433.433C601.802 496.418 563.04 550.688 512.764 592.125C459.986 635.624 397.457 667.964 329.175 671.866C258.316 675.915 185.848 657.406 129.681 614.017C74.4426 571.347 46.6163 504.625 26.2512 437.863C6.22109 372.198 -15.0133 299.136 14.7607 237.277C43.5707 177.421 122.431 167.875 175.459 127.866C229.988 86.7239 260.906 2.39047 329.175 0.0542232Z" fill="#E54C80" />
          </svg>
        </div>
        <div class="fwc-blob-4">
          <svg width="100%" viewBox="0 0 523 592" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
            <path opacity="0.05" fill-rule="evenodd" clip-rule="evenodd" d="M277.948 15.0032C353.422 20.2842 437.12 13.6358 486.242 71.1794C536.697 130.286 521.626 213.778 513.615 291.078C503.356 390.067 519.634 511.141 435.189 563.802C346.494 619.112 226.406 582.698 139.775 524.207C65.8688 474.308 53.7962 379.916 34.0393 292.959C14.3216 206.173 -29.1134 106.12 29.5866 39.2266C87.3079 -26.552 190.648 8.89466 277.948 15.0032Z" fill="#804CE6" />
          </svg>
        </div>
        <div class="fwc-blob-3">
          <svg width="100%" viewBox="0 0 585 583" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
            <path opacity="0.05" fill-rule="evenodd" clip-rule="evenodd" d="M289.771 0.0541782C394.697 -1.38101 512.454 25.4011 565.681 115.835C614.827 199.335 553.464 297.778 518.093 387.98C491.691 455.311 458.57 519.758 394.293 552.911C327.732 587.241 248.849 593.436 180.929 561.88C111.891 529.804 72.3643 463.002 46.5997 391.369C14.6548 302.554 -26.6525 203.423 23.1651 123.256C77.5289 35.7729 186.782 1.46288 289.771 0.0541782Z" fill="#E54C4D" />
          </svg>
        </div>
        <div class="fwc-blob-2">
          <svg width="100%" viewBox="0 0 629 617" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
            <path opacity="0.05" fill-rule="evenodd" clip-rule="evenodd" d="M275.349 0.448552C366.623 3.80122 448.347 45.746 510.264 112.891C579.452 187.923 642.99 279.594 625.474 380.143C607.133 485.43 525.805 576.497 423.896 608.69C333.581 637.22 244.299 579.859 167.139 524.931C108.356 483.084 83.3975 417.607 59.7239 349.444C30.0293 263.946 -29.8228 171.798 17.7498 94.8003C68.3186 12.9532 179.205 -3.08302 275.349 0.448552Z" fill="#55BD55" />
          </svg>
        </div>
        <div class="fwc-blob-1">
          <svg width="100%" viewBox="0 0 649 631" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
            <path opacity="0.05" fill-rule="evenodd" clip-rule="evenodd" d="M311.605 0.0275336C407.042 1.89478 457.166 104.346 516.738 178.932C576.403 253.634 665.635 325.279 646.141 418.876C626.462 513.355 520.003 553.13 431.018 590.483C349.728 624.607 262.184 648.022 180.755 614.232C93.7943 578.147 20.7728 505.41 3.13194 412.927C-13.9178 323.542 41.6916 243.602 96.1534 170.703C153.846 93.4803 215.229 -1.85806 311.605 0.0275336Z" fill="#42AADE" />
          </svg>
        </div>
        <div class="slot-wrapper">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

class FwcAnimBlob extends FwcBase {
  constructor() {
    super("fwc-anim-blob", FwcAnimBlobComponent);
  }
}

const breakpoints = {
  md: 900,
  sm: 600,
};

const baseUnit = 8;

const fwcConfig = {
  assetsPath: "/",
};

class FwcAnimColDropComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          display: block;
        }
        .fwc-anim-wrapper {
          position: relative;
          width: 100%;
          padding-bottom: var(--fwcu2);
          overflow: hidden;
        }
        .fwc-anim-bar {
          width: 100%;
          height: 2px;
          margin: 0 auto;
          margin-bottom: calc(var(--fwcu) + var(--fwcu05));
          background: var(--color-gray-five, #f7f7f7);
        }
        .fwc-anim-bar.prep-animate {
          width: 0%;
        }
        .fwc-anim-bar.animate {
          width: 100%;
          transition: width 500ms ease-in-out;
        }
        .fwc-anim-content {
          padding: calc(1.5 * var(--fwcu)) 0;
        }
        .fwc-anim-content.prep-animate {
          transform: translateY(-125%);
        }
        .fwc-anim-content.animate {
          animation: slideIn 1s ease-in-out 1;
          transform: translateY(0%);
        }

        @keyframes slideIn {
          0% {
            transform: translateY(-125%);
          }
          33% {
            transform: translateY(20%);
          }
          100% {
            transform: translateY(0%);
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      delay: { type: Number },
      triggerAnimate: { attribute: "trigger-animate", type: Boolean },
      disableAnimateMobile: { attribute: "disable-animate-mobile", type: String }, // small, medium
    };
  }

  constructor() {
    super();
    this.delay = 0;
    this.bar = null;
    this.content = null;
    this.disableAnimateMobile = ""; // small, medium
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);
    if (name === "trigger-animate") {
      this.startAnimation();
    }
  }

  firstUpdated() {
    const isAnimationDisabled = this.checkAnimationDisabled();
    this.bar = this.shadowRoot.querySelector(".fwc-anim-bar");
    this.content = this.shadowRoot.querySelector(".fwc-anim-content");
    this.bar.addEventListener("transitionend", (e) => {
      this.content.classList.add("animate");
    });

    if (!isAnimationDisabled) {
      this.bar.classList.add("prep-animate");
      this.content.classList.add("prep-animate");
    }

    if (this.triggerAnimate) {
      this.startAnimation();
    }
  }

  checkAnimationDisabled() {
    if (window.innerWidth <= breakpoints.md && this.disableAnimateMobile === "medium") {
      return true;
    }
    if (window.innerWidth <= breakpoints.sm && this.disableAnimateMobile === "small") {
      return true;
    }
    return false;
  }

  startAnimation() {
    setTimeout(() => {
      this.bar.classList.add("animate");
    }, this.delay);
  }

  render() {
    return html`
      <div class="fwc-anim-wrapper">
        <div class="fwc-anim-bar"></div>
        <div class="fwc-anim-content">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

class FwcAnimColDrop extends FwcBase {
  constructor() {
    super("fwc-anim-col-drop", FwcAnimColDropComponent);
  }
}

function debounce(func, wait, immediate) {
  var timeout;
  return function () {
    var context = this,
      args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

class FwcBackToTopComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .back-to-top {
          font-family: var(--font-sans-serif);
          position: fixed;
          bottom: 5%;
          left: 50%;
          transform: translateX(-50%);
          color: white;
          opacity: 0.85;
          background: var(--color-util-four);
          display: flex;
          line-height: calc(2 * var(--fwcu));
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          padding: calc(1.25 * var(--fwcu)) calc(2 * var(--fwcu));
          text-decoration: none;
          border-radius: calc(3.25 * var(--fwcu));
          z-index: 1;
          font-weight: 700;
          font-size: var(--default-font-size);
          border: none;
        }
        .back-to-top:hover,
        .back-to-top:focus,
        .back-to-top:active,
        .back-to-top:active:focus {
          color: var(--color-white);
        }
        .back-to-top:hover {
          opacity: 1;
          background: var(--color-util-four);
          cursor: pointer;
        }
        .fwc-icon-arrow {
          transform: rotate(-90deg);
          font-size: calc(2.25 * var(--fwcu));
          fill: var(--color-white);
        }
        .back-to-top.hidden {
          display: none;
        }
        span {
          display: block;
          margin-left: calc(1.25 * var(--fwcu));
        }
        @media screen and (max-width: 600px) {
          .back-to-top {
            bottom: 10%;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      labelBtn: { attribute: "label-btn", type: String },
      showButtonAtPx: { attribute: "show-button-at-px", type: Number },
      _hideButton: {},
    };
  }

  constructor() {
    super();
    this._bodyEle = document.documentElement;
    this._hideButton = true;
    this.labelBtn = "Back to Top";
    this.showButtonAtPx = 1000;
    //listen for scroll every 250ms
    this._handleScroll = debounce(() => {
      this.checkScrollPos();
    }, 250);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("scroll", () => {
      this._handleScroll();
    });
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", () => {
      this._handleScroll();
    });
    super.disconnectedCallback();
  }

  // check if we show or hide btn based on if page is scrolled past showButtonAtPx
  checkScrollPos() {
    this._hideButton = window.pageYOffset >= this.showButtonAtPx ? false : true;
  }

  scrollToTopOfWindow() {
    this._hideButton = true;
    //passing in reference of (this) since setInterval takes scope of this keyword
    this.fadeOut(this._bodyEle, this.fadeIn, this);
  }

  //fade out body
  fadeOut(element, callback, _this) {
    let op = 1; // initial opacity
    let increment = 0.05;
    let timer = setInterval(() => {
      if (op <= 0) {
        clearInterval(timer);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        callback(element, _this);
      }
      element.style.opacity = op;
      element.style.filter = "alpha(opacity=" + op + ")";
      op = op - increment;
    }, 15);
  }

  //fade in body
  fadeIn(element, _this) {
    let op = 0; // initial opacity
    let increment = 0.05;
    let timer = setInterval(() => {
      if (op >= 1) {
        clearInterval(timer);
        /* c8 ignore next 2 */
        _this._returnFocus();
      }
      element.style.opacity = op;
      element.style.filter = "alpha(opacity=" + op + ")";
      op = op + increment;
    }, 15);
  }

  _returnFocus() {
    if (this._refocusElem) {
      // Return focus to calling element
      this._refocusElem.focus();
    }
  }

  // a public func allowing projects to set the refocus element
  setReFocus(elem) {
    this._refocusElem = elem;
  }

  render() {
    return html`
      <button role="button" class="back-to-top ${this._hideButton ? "hidden" : ""} " @click="${this.scrollToTopOfWindow}">
        <svg class="fwc-icon-arrow" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 50 50" focusable="false">
          <polygon id="Path" points="30.7843137 5 27.7777778 8.00653595 41.9607843 22.1895425 0 22.1895425 0 26.372549 41.9607843 26.372549 27.7777778 40.5555556 30.7843137 43.5620915 48.5620915 25.7843137 50 24.2810458 48.5620915 22.7777778"></polygon>
        </svg>
        <span>${this.labelBtn}</span>
      </button>
    `;
  }
}

class FwcBackToTop extends FwcBase {
  constructor() {
    super("fwc-back-to-top", FwcBackToTopComponent);
  }
}

class FwcBarsComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-cb-flex {
          width: 100%;
          display: flex;
          flex-direction: row;
        }
        .fwc-cb-container {
          width: calc(100% - 1.5 * var(--fwcu));
          height: calc(2.75 * var(--fwcu));
          display: flex;
          align-items: center;
          justify-content: flex-start;
          overflow: hidden;
          border: 1px solid transparent;
          border-radius: calc(2.75 * var(--fwcu));
          background-color: var(--color-gray-five);
        }
        .fwc-cb-bar {
          height: calc(2.75 * var(--fwcu));
          border: 1px solid transparent;
          border-radius: calc(2.75 * var(--fwcu));
          transition: width 500ms cubic-bezier(0.2, -0.02, 0.8, 1.4);
          background-color: var(--color-dviz-one);
        }
        dl {
          color: var(--color-gray-one);
          font-size: var(--default-font-size);
        }

        dl .fwc-cb-flex:nth-of-type(2n) .fwc-cb-bar {
          background-color: var(--color-dviz-four);
        }
        dl .fwc-cb-flex:nth-of-type(3n) .fwc-cb-bar {
          background-color: var(--color-dviz-eight);
        }
        dl .fwc-cb-flex:nth-of-type(4n) .fwc-cb-bar {
          background-color: var(--color-dviz-seven);
        }

        dl,
        dt,
        dd {
          margin: 0;
          padding: 0;
        }
        strong {
          padding-left: calc(1.5 * var(--fwcu));
        }
        @media screen and (max-width: 900px) {
          .fwc-cb-flex {
            flex-direction: column;
          }
          .fwc-cb-container {
            width: 100%;
          }
          strong {
            padding-left: 0;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      bars: { type: Array },
    };
  }

  constructor() {
    super();
    this.bars = [];
  }

  attributeChangedCallback(name, oldval, newval) {
    if (name === "bars" && oldval) {
      this.bars = JSON.parse(newval);
      this._buildBars();
    }
    super.attributeChangedCallback(name, oldval, newval);
  }

  _buildBars() {
    // TODO: Num types: $, %, etc
    // TODO: Aria

    const bars = html`${this.bars.map((bar) => {
      const w = Math.floor((bar.value / bar.max) * 100);
      return html`
        <dt>${bar.title}</dt>
        <dd class="fwc-cb-flex">
          <div class="fwc-cb-container">
            <div class="fwc-cb-bar" style="width:${w}%"></div>
          </div>
          <div><strong>${bar.value}</strong><span>/${bar.max}</span></div>
        </dd>
      `;
    })}`;

    return bars;
  }

  render() {
    return html` <dl>${this._buildBars()}</dl> `;
  }
}

class FwcBars extends FwcBase {
  constructor() {
    super("fwc-bars", FwcBarsComponent);
  }
}

class FwcBarsStackedComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          --bar-size: calc(2.75 * var(--fwcu));
          --bar-size-mobile: calc(1.75 * var(--fwcu));
          --legend-size: calc(7.75 * var(--fwcu));
          height: 100%;
          padding-bottom: calc(2 * var(--fwcu));
        }

        .wrapper {
          position: relative;
          height: calc(100% - var(--legend-size));
        }
        .wrapper.vertical {
          min-height: calc((5 * var(--fwcu)) + var(--legend-size));
        }

        .horizontal section {
          position: relative;
          height: 100%;
        }
        .vertical section {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
        }

        .legend {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          align-items: flex-start;
          align-content: flex-start;
          flex-wrap: wrap;
          height: var(--legend-size);
          z-index: 2;
          opacity: 0;
          transition: opacity 200ms ease-out 200ms;
        }
        .legend li {
          padding-right: calc(2 * var(--fwcu));
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }
        .legend li div,
        .caret .d-color {
          height: calc(1.5 * var(--fwcu));
          width: calc(1.5 * var(--fwcu));
          margin-right: var(--fwcu);
          border: 1px solid rgba(255, 255, 255, 0);
          border-radius: 50%;
        }

        .graph {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          opacity: 0;
          transition: opacity 200ms ease-out;
        }

        .vert-cont {
          position: absolute;
          z-index: 1;
          height: 100%;
        }
        .vert-cont:first-of-type .vert-val {
          left: 0;
        }
        .vert-cont:last-of-type .vert-val {
          left: -100%;
        }
        .vert-line {
          width: 1px;
          background-color: var(--color-gray-five);
          height: 100%;
        }
        .vert-val {
          position: relative;
          left: -50%;
          font-size: calc(1.5 * var(--fwcu));
          font-weight: bold;
          color: var(--color-gray-one);
        }
        @media screen and (max-width: 600px) {
          .vert-cont:nth-child(2n) {
            display: none;
          }
        }
        .minimal .vert-cont:nth-child(2n) {
          display: none;
        }

        .horz-cont {
          position: absolute;
          display: flex;
          align-items: flex-end;
          z-index: 1;
          width: 100%;
        }
        @media screen and (max-width: 600px) {
          .horz-cont:nth-child(2n) {
            display: none;
          }
        }
        .minimal .horz-cont:nth-child(2n) {
          display: none;
        }

        .horz-line {
          height: 1px;
          background-color: var(--color-gray-five);
          width: 100%;
        }
        .horz-val {
          text-align: right;
          flex: 1 0 calc(5 * var(--fwcu));
          padding-right: var(--fwcu);
          font-weight: bold;
          font-size: calc(1.5 * var(--fwcu));
          transform: translateY(calc(var(--default-line-height) / 2));
          color: var(--color-gray-one);
        }

        .horizontal .def {
          padding-bottom: var(--bar-size);
        }
        .vertical .def {
          display: flex;
          flex-direction: column-reverse;
          align-items: center;
          width: var(--bar-size);
          position: relative;
        }

        @media screen and (max-width: 600px) {
          .horizontal .def {
            padding-bottom: var(--bar-size-mobile);
          }
          .vertical .def {
            width: var(--bar-size-mobile);
          }
        }
        .minimal.horizontal .def {
          padding-bottom: var(--bar-size-mobile);
        }
        .minimal.vertical .def {
          width: var(--bar-size-mobile);
        }
        .transparent .def:not(.active) {
          opacity: 0.5;
        }
        .transparent .def {
          pointer-events: none;
        }

        .bar-container {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .bar {
          position: absolute;
          border: 1px solid transparent;
          background-color: var(--color-dviz-four);
          list-style-type: none;
          opacity: 0;
          height: 0;
        }
        .bar.zero {
          display: none;
        }

        .horizontal .bar {
          border-radius: 0 calc(2.75 * var(--fwcu)) calc(2.75 * var(--fwcu)) 0;
          height: var(--bar-size);
          width: 0;
          transition: width 500ms cubic-bezier(0.2, -0.02, 0.8, 1.4), opacity 300ms ease-in 300ms;
        }
        .vertical .bar {
          height: auto;
          width: var(--bar-size);
          border-radius: calc(2.75 * var(--fwcu)) calc(2.75 * var(--fwcu)) 0 0;
          transition: height 300ms cubic-bezier(0.2, -0.02, 0.8, 1.4), opacity 300ms ease-in 300ms;
          bottom: 0;
        }
        @media screen and (max-width: 600px) {
          .horizontal .bar {
            height: var(--bar-size-mobile);
          }
          .vertical .bar {
            width: var(--bar-size-mobile);
          }
        }
        .minimal.horizontal .bar {
          height: var(--bar-size-mobile);
        }
        .minimal.vertical .bar {
          width: var(--bar-size-mobile);
        }

        dl {
          color: var(--color-gray-one);
          font-size: var(--default-font-size);
          position: relative;
          z-index: 2;
        }
        .vertical dl {
          position: absolute;
          top: 0;
          bottom: 0;
          right: 0;
          left: 0;
          height: 100%;
          display: flex;
          justify-content: space-around;
          margin-left: calc(6 * var(--fwcu));
        }
        .horizontal dl {
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
        }

        dt {
          font-weight: bold;
          opacity: 0;
        }
        dt.opaque {
          opacity: 1;
        }
        .vertical dd {
          width: var(--bar-size);
        }
        .vertical dt {
          position: absolute;
          top: 100%;
        }

        .horizontal dd {
          height: var(--bar-size);
        }

        dd:hover {
          opacity: 0.85;
          transition: transform 200ms cubic-bezier(0.28, -0.01, 0.62, 1.96), opacity 200ms ease-out;
          cursor: pointer;
        }
        .vertical dd:hover {
          height: 100%;
          transform: scaleY(1.05);
          transform-origin: bottom;
        }
        .horizontal dd:hover {
          transform: scaleX(1.025);
          transform-origin: left;
        }
        @media screen and (max-width: 900px) and (hover: none) and (pointer: coarse) {
          dd:hover,
          .horizontal dd:hover,
          .vertical dd:hover {
            transition: none;
            transform: none;
            opacity: 1;
          }
        }

        @media screen and (max-width: 600px) {
          .vertical dt {
            writing-mode: vertical-lr;
            white-space: nowrap;
            padding-top: 1rem;
          }
          .vertical dd {
            width: var(--bar-size-mobile);
          }
          .horizontal dd {
            height: var(--bar-size-mobile);
          }
        }
        .minimal.vertical dd {
          width: var(--bar-size-mobile);
        }
        .minimal.vertical dt {
          writing-mode: vertical-lr;
          white-space: nowrap;
          padding-top: 1rem;
        }
        .minimal.horizontal dd {
          height: var(--bar-size-mobile);
        }

        .caret {
          position: absolute;
          background-color: var(--color-white);
          border: 2px solid var(--color-gray-four);
          border-radius: var(--border-radius);
          min-width: 200px;
          height: auto;
          z-index: 3;
          padding: calc(2.5 * var(--fwcu)) calc(2 * var(--fwcu));
          box-shadow: var(--box-shadow);
          display: none;
          top: calc(-1 * var(--legend-size));
          opacity: 0;
          transition: opacity 250ms ease-in;
        }
        .caret.show {
          display: block;
        }
        .caret::before {
          position: absolute;
          top: calc(100% - (1.5 * var(--fwcu)) + 2px); /* match border size */
          right: calc(50% - (1.5 * var(--fwcu)));
          content: " ";
          width: calc(3 * var(--fwcu));
          height: calc(3 * var(--fwcu));
          border-right: 2px solid var(--color-gray-four);
          border-bottom: 2px solid var(--color-gray-four);
          transform: rotate(45deg);
          background-color: var(--color-white);
        }
        .caret div {
          padding-bottom: calc(0.5 * var(--fwcu));
        }
        .caret button {
          position: absolute;
          top: calc(1.5 * var(--fwcu));
          right: calc(1.5 * var(--fwcu));
        }
        .caret .d-color {
          display: inline-block;
        }

        dl,
        dt,
        dd {
          margin: 0;
          padding: 0;
        }

        .dviz-one {
          background: var(--color-dviz-one);
        }
        .dviz-two {
          background: var(--color-dviz-two);
        }
        .dviz-three {
          background: var(--color-dviz-three);
        }
        .dviz-four {
          background: var(--color-dviz-four);
        }
        .dviz-five {
          background: var(--color-dviz-five);
        }
        .dviz-six {
          background: var(--color-dviz-six);
        }
        .dviz-seven {
          background: var(--color-dviz-seven);
        }
        .dviz-eight {
          background: var(--color-dviz-eight);
        }

        .opaque {
          opacity: 1;
        }
      `,
    ];
  }

  static get properties() {
    return {
      _animate: {},
      bars: { type: Array },
      colors: { type: Array },
      config: { type: Object },
      _detail: {},
      _detailPos: {},
      _detailShow: {},
      endMultiple: { attribute: "end-multiple", type: Number },
      holdAnim: { attribute: "hold-anim", type: Boolean },
      labelAriaChart: { attribute: "label-aria-chart", type: String },
      minimal: { type: Boolean },
      orientation: { type: String }, // vertical | horizontal
      symbol: { type: String },
    };
  }

  /*
  data structure (this.bars)
    [{
      title: 'Prod',
      data: [
        {
          key: "Success",
          value: 1251
        },
        {
          key: "Fail",
          value: 0
        }
      ]
    }],
  */

  constructor() {
    super();
    this._animate = false;
    this.bars = [];
    this._caretRef = null;
    this.colors = ["dviz-four", "dviz-five", "dviz-seven", "dviz-six", "dviz-three", "dviz-eight", "dviz-two", "dviz-one"];
    this.config = fwcConfig;
    this._detail = [];
    this._detailPos = 0;
    this._detailShow = false;
    this.endMultiple = 50;
    this.labelAriaChart = "Stacked Bar Chart";
    this._maxLines = 10;
    this._maxVal = 0;
    this.orientation = "horizontal";
    this.symbol = "";
    this._handleCaretClickOut = this._handleCaretClickOut.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("click", this._handleCaretClickOut);
  }
  disconnectedCallback() {
    window.removeEventListener("click", this._handleCaretClickOut);
    super.disconnectedCallback();
  }

  firstUpdated() {
    this._maxVal = this._getMaxValue();
    if (!this.holdAnim) {
      this._initAnimate();
    }
    this._caretRef = this.shadowRoot.getElementById("Caret");
  }

  attributeChangedCallback(name, oldval, newval) {
    if (name === "bars" && oldval) {
      this.bars = JSON.parse(newval);
      this._initGraph();
    }
    super.attributeChangedCallback(name, oldval, newval);
  }

  async _initAnimate() {
    const result = await this._startAnimation();
    this._animate = result;
  }

  _startAnimation() {
    return new Promise((res) => {
      setTimeout(() => {
        res(true);
      }, 100);
    });
  }

  triggerAnimation() {
    this.holdAnim = false;
    this._initAnimate();
  }

  _initGraph() {
    this._maxVal = this._getMaxValue();
    this._buildGraph();
  }

  _closestMultipleEnd(n, x) {
    // multiple of x closest to a given n
    // add x if n < maxVal
    const org = n;
    if (x > n) return x;
    n = n + parseInt(x / 2, 10);
    n = n - (n % x);
    n = n < org ? n + x : n;
    // returns graph endpoint / max value
    return Math.ceil(n);
  }

  _getMaxValue() {
    let totals = [];
    for (let bar of this.bars) {
      let barTotal = 0;
      for (let val of bar.data) {
        barTotal += val.value;
      }
      totals.push(barTotal);
    }
    return totals.sort((a, b) => a - b)[totals.length - 1];
  }

  _buildLegend() {
    const legend = this.bars[0].data.map((bar, i) => {
      return html`<li>
        <div class=${this.colors[i]}></div>
        ${bar.key}
      </li>`;
    });
    return html`
      <ul class="legend ${this._animate ? "opaque" : ""}" aria-hidden="true">
        ${legend}
      </ul>
    `;
  }

  _buildGraph() {
    let graphLines = null;
    const max = this._closestMultipleEnd(this._maxVal, this.endMultiple);
    const step = Math.ceil(max / this._maxLines);
    let steps = [];
    for (let i = 0; i < this._maxLines + 1; i++) {
      steps.push(step * i);
    }
    if (this.orientation === "horizontal") {
      graphLines = steps.map((step, count) => this._verticalLines(step, count));
    } else {
      graphLines = steps.map((step, count) => this._horizontalLines(step, count));
    }
    return graphLines;
  }

  _abrvNumbers(num) {
    const check = Math.floor(num / 100000);

    if (check < 1) {
      return this.symbol + num;
    }

    if (check >= 1 && check < 10) {
      const k = num / 100000;
      // 100k -> 999k
      return this.symbol + Math.round(k * 100) + "k";
    }
    if (check >= 10 && check < 100) {
      let m = num / 1000000;
      // 1.0m -> 99.9m
      return this.symbol + Math.round(m * 100) / 100 + "m";
    }
    if (check >= 100 && check < 1000) {
      const b = num / 1000000000;
      // 1.0b -> 99.9b
      return this.symbol + Math.round(b * 0.1) / 100 + "b";
    }

    return this.symbol + num;
  }

  _verticalLines(step, count) {
    const left = Math.ceil((count / this._maxLines) * 100);
    return html`
      <div class="vert-cont" style="left:${left}%">
        <div class="vert-line"></div>
        <div class="vert-val">${this._abrvNumbers(step)}</div>
      </div>
    `;
  }

  _horizontalLines(step, count) {
    const bottom = Math.ceil((count / this._maxLines) * 100);
    return html`
      <div class="horz-cont" style="bottom:${bottom}%">
        <div class="horz-val">${this._abrvNumbers(step)}</div>
        <div class="horz-line"></div>
      </div>
    `;
  }

  _setDetailPos(target) {
    let pos;
    const side = this.orientation === "vertical" ? "width" : "height";
    const bar = this.shadowRoot.querySelectorAll(".bar");

    // get actual px val of bar size to use to center caret
    const barSize = window.getComputedStyle(bar[0]).getPropertyValue(side).replace("px", "");
    const caretSize = this._caretRef.getBoundingClientRect();
    const targetBox = target.getBoundingClientRect();
    const bounding = this.getBoundingClientRect();

    switch (this.orientation) {
      case "vertical":
        pos = targetBox.left - bounding.left - (caretSize.width / 2 - barSize / 2);
        break;
      case "horizontal":
        pos = targetBox.top - bounding.top - caretSize.height - (targetBox.height - barSize / 2);
        break;
    }
    this._detailPos = pos;
  }

  _showDetail(e, data) {
    const defs = this.shadowRoot.querySelectorAll(".def.active");
    const target = e.currentTarget;
    for (let def of defs) {
      def.classList.remove("active");
    }

    const bars = [...data];
    const total = bars.reduce((a, b) => a + b.value, 0);
    bars.push({ key: "Total", value: total });
    const details = bars.map((d, i) => {
      const c = i < bars.length - 1 ? this.colors[i] : "";
      return html` <div><span class="d-color ${c}"></span> <strong>${d.key}:</strong> <span>${this.symbol + d.value}</span></div>`;
    });

    e.currentTarget.classList.add("active");
    this._detail = details;
    this._setDetailPos(target);

    setTimeout(() => {
      // position again with correct width value before showing.
      this._setDetailPos(target);
      this._detailShow = true;
    }, 0);
  }

  _closeCaret() {
    this._detail = [];
    this._detailShow = false;
  }

  _handleCaretClickOut(e) {
    // To get actual target inside shadow, must use:
    // https://developer.mozilla.org/en-US/docs/Web/API/Event/composedPath
    if (!this._caretRef.contains(e.composedPath()[0]) && this._detailShow) {
      this._closeCaret();
    }
  }

  _caret() {
    const prop = this.orientation === "horizontal" ? "top" : "left";
    return html`
      <div id="Caret" aria-hidden="true" class="caret ${this._detail.length > 0 ? "show" : ""} ${this._detailShow ? "opaque" : ""}" style="${prop}: ${this._detailPos}px">
        <button class="fwc-link fwc-close" @click="${this._closeCaret}}">
          <span class="fwc-icon fwc-icon-delete"></span>
        </button>
        ${this._detail}
      </div>
    `;
  }

  _bar(bar, dir, pad, count) {
    const delay = Math.ceil((count.set + count.bar / 10) * 100);
    return html`
      <div class="bar ${this.colors[count.bar]} ${bar.value <= 0 ? "zero" : ""} ${this._animate ? "opaque" : ""}" style=${`${dir}: ${this._animate ? pad : 0}%; transition-delay: ${delay}ms;`}>
        <span class="sr-only">${bar.key + ": " + bar.value}</span>
      </div>
    `;
  }

  _barContainer(title, bars, data) {
    return html`
      <div class="def" @click=${(e) => this._showDetail(e, data)}>
        <dt class=${this._animate ? "opaque" : ""}>${title}</dt>
        <dd id="BarContainer">${bars.reverse()}</dd>
      </div>
    `;
  }

  _buildBars() {
    const direction = this.orientation === "vertical" ? "height" : "width";
    const max = this._closestMultipleEnd(this._maxVal, this.endMultiple);
    const chart = this.bars.map((set, s) => {
      let pad = 0;
      const bars = set.data.map((bar, b) => {
        const w = Math.floor((bar.value / max) * 100);
        const dom = this._bar(bar, direction, w + pad, { set: s, bar: b });
        pad += w;
        return dom;
      });

      return this._barContainer(set.title, bars, set.data);
    });
    return chart;
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/buttons-links.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/icons.css" />
      ${this._buildLegend()}
      <div class="wrapper ${this.orientation} ${this.minimal ? "minimal" : ""}" id="Wrapper">
        <section>
          <div class="graph ${this._animate ? "opaque" : ""}" aria-hidden="true">${this._buildGraph()}</div>
          ${this._caret()}
          <dl aria-label=${this.labelAriaChart} class="${this._detail.length > 0 ? "transparent" : ""}">${this._buildBars()}</dl>
        </section>
      </div>
    `;
  }
}

class FwcBarsStacked extends FwcBase {
  constructor() {
    super("fwc-bars-stacked", FwcBarsStackedComponent);
  }
}

class FwcCardCircleComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          position: relative;
          padding-top: calc(16 * var(--fwcu));
          padding-bottom: calc(4 * var(--fwcu));
          height: 100%;
        }
        ::slotted(button) {
          margin-bottom: calc(2 * var(--fwcu));
        }
        p {
          padding-bottom: calc(3 * var(--fwcu));
        }
        h3 {
          text-align: center;
          margin: 2rem 0;
        }
        .circle-bg,
        .circle-white,
        .circle-img {
          border-radius: 50%;
          position: absolute;
          background-color: var(--color-white);
        }
        .circle-bg {
          border: solid 2px rgba(var(--color-rgb-black), 0.1);
          z-index: 0;
          width: calc(30 * var(--fwcu));
          height: calc(30 * var(--fwcu));
          left: calc(50% - (15 * var(--fwcu)));
          top: 0;
          box-shadow: 0 0 12px 4px rgba(var(--color-rgb-black), 0.08);
        }
        .circle-white {
          z-index: 1;
          background-color: var(--color-white);
          width: calc(30 * var(--fwcu) - 2px);
          height: calc(30 * var(--fwcu) - 2px);
          left: calc(50% - (15 * var(--fwcu)) + 1px);
          top: 2px;
        }
        .circle-img {
          width: calc(28 * var(--fwcu));
          height: calc(28 * var(--fwcu));
          left: calc(50% - (14 * var(--fwcu)));
          top: var(--fwcu);
          background-color: var(--color-gray-four);
          background-repeat: no-repeat;
          background-size: cover;
          background-position: 50%;
          z-index: 1;
        }

        .fwc-card-generic {
          padding: calc(6 * var(--fwcu));
          position: relative;
          min-height: calc(16 * var(--fwcu));
          height: 100%;
        }

        .fwc-card-generic .content {
          padding-top: calc(7 * var(--fwcu));
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .actions {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex-wrap: wrap;
        }

        @media screen and (max-width: 600px) {
          h3 {
            margin-top: 0;
          }

          .fwc-card-generic {
            padding: calc(3 * var(--fwcu));
          }
          .circle-bg {
            width: calc(25 * var(--fwcu));
            height: calc(25 * var(--fwcu));
            left: calc(50% - (11.5 * var(--fwcu)));
            top: 0;
          }
          .circle-white {
            width: calc(25 * var(--fwcu) - 2px);
            height: calc(25 * var(--fwcu) - 2px);
            left: calc(50% - (11.5 * var(--fwcu)) + 1px);
            top: 2px;
          }
          .circle-img {
            width: calc(23 * var(--fwcu));
            height: calc(23 * var(--fwcu));
            left: calc(50% - (10.5 * var(--fwcu)));
            top: var(--fwcu);
          }
          .fwc-card-generic .content {
            padding-top: calc(10.5 * var(--fwcu));
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      image: { type: String },
      title: { type: String },
      content: { type: String },
      config: { type: Object },
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this.image = "";
    this.title = "";
    this.content = "";
  }

  contentFromProps() {
    return html`
      <h3>${this.title}</h3>
      <p>${this.content}</p>
    `;
  }

  contentFromSlot() {
    return html`
      <h3>${this.title}</h3>
      <slot name="fwc-card-circle-content"></slot>
    `;
  }

  renderProps() {
    const content = this.content ? this.contentFromProps() : this.contentFromSlot();
    return content;
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/components/fwc-cards.css" />
      <div class="circle-bg"></div>
      <div class="circle-white"></div>
      <div class="circle-img" style=${`background-image: url(${this.image})`}></div>
      <div class="fwc-card-generic">
        <div class="content">
          <div>${this.renderProps()}</div>
          <div>
            <slot class="actions" name="fwc-card-circle-actions"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

class FwcCardCircle extends FwcBase {
  constructor() {
    super("fwc-card-circle", FwcCardCircleComponent);
  }
}

class FwcCardMediaImageComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          height: 100%;
          display: block;
        }
        .card {
          background: var(--color-white);
          border-radius: var(--border-radius);
          box-shadow: var(--box-shadow);
          overflow: hidden;
          height: 100%;
        }
        .card.full {
          display: flex;
          flex-direction: row-reverse;
          align-items: stretch;
          max-height: calc(34 * var(--fwcu)); /* 272px */
        }

        .wrapper {
          position: relative;
        }

        .optional {
          position: absolute;
          display: flex;
          align-items: center;
          height: calc(4 * var(--fwcu));
          width: 100%;
          bottom: calc(-1 * var(--fwcu));
          padding: 0 calc(3 * var(--fwcu));
        }
        .optional.end {
          justify-content: flex-end;
        }
        .optional.between {
          justify-content: space-between;
        }

        .content {
          padding: calc(3 * var(--fwcu));
        }

        .card.md .content {
          padding: calc(4 * var(--fwcu));
        }
        .card.md .optional {
          padding: 0 calc(4 * var(--fwcu));
        }

        .card.lg .content {
          padding: calc(4 * var(--fwcu)) calc(6 * var(--fwcu));
        }
        .card.full .content {
          padding: calc(4 * var(--fwcu)) calc(6 * var(--fwcu));
        }

        .card.lg .optional {
          padding: 0 calc(6 * var(--fwcu));
        }

        .content p {
          padding: calc(1.5 * var(--fwcu)) 0 calc(2.5 * var(--fwcu));
        }

        /* Full size cards */
        .content .optional {
          position: static;
          height: auto;
          padding: 0 0 calc(3.25 * var(--fwcu)) 0;
        }

        .bookmark {
          height: calc(5 * var(--fwcu));
          width: calc(5 * var(--fwcu));
          border: 1px solid var(--color-primary);
          border-radius: 50%;
          background-color: var(--color-white);
          font-size: calc(2.75 * var(--fwcu));
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          padding: 0;
        }
        .bookmark.active {
          background-color: var(--color-primary);
        }
        .bookmark svg {
          fill: var(--color-primary);
          height: calc(2.25 * var(--fwcu));
          width: calc(2.25 * var(--fwcu));
        }
        .bookmark.active svg {
          fill: var(--color-white);
        }

        slot[name="links"] {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          flex-wrap: wrap;
        }
        slot[name="links"]::slotted(a) {
          flex: 0 0 auto;
          padding-right: calc(2 * var(--fwcu));
          padding-bottom: var(--fwcu);
        }
        slot[name="body"] {
          padding: calc(1.5 * var(--fwcu)) 0 calc(2.5 * var(--fwcu));
          display: block;
        }

        .placeholder {
          background: var(--color-gray-six);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .placeholder svg {
          fill: var(--color-gray-four);
        }

        img {
          opacity: 0;
          transition: opacity 250ms ease-out;
        }
        img.load {
          opacity: 1;
        }
        img,
        .placeholder {
          object-fit: cover;
          object-position: top center;
          width: 100%;
          height: auto;
        }
        img.full,
        .placeholder.full {
          aspect-ratio: 1.68 / 1;
          height: 100%;
        }

        img.sm,
        .placeholder.sm {
          aspect-ratio: 1.6 / 1;
        }
        img.md,
        .placeholder.md {
          aspect-ratio: 2.45 / 1;
        }
        img.lg,
        .placeholder.lg {
          aspect-ratio: 2 / 1;
        }

        @media screen and (max-width: 600px) {
          .card.md .content,
          .card.lg .content,
          .card.full .content {
            padding: calc(3 * var(--fwcu));
          }

          .card.md .optional,
          .card.lg .optional {
            padding: 0 calc(3 * var(--fwcu));
          }

          .card.full {
            display: block;
            max-height: none;
          }
          .card.full .content {
            padding-top: 0;
          }
          .content .optional {
            position: relative;
            top: calc(-3 * var(--fwcu));
            padding-bottom: 0;
          }

          img.md,
          img.lg,
          img.full,
          .placeholder.md,
          .placeholder.lg,
          .placeholder.full {
            aspect-ratio: 1.6 / 1;
          }
        }

        /* Safari doesn't support aspect-raitio yet */
        @supports not (aspect-ratio: 1.6 / 1) {
          img.sm,
          img.md,
          .placeholder.sm,
          .placeholder.md {
            height: calc(28 * var(--fwcu)); /* 224px */
          }
          img.lg,
          .placeholder.lg {
            height: calc(46.5 * var(--fwcu)); /* 372px */
          }
          img.full,
          .placeholder.full {
            height: calc(34 * var(--fwcu)); /* 272px */
          }
          @media screen and (max-width: 600px) {
            img.lg,
            img.full,
            .placeholder.lg,
            .placeholder.full {
              height: calc(28 * var(--fwcu)); /* 224px */
            }
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      badge: { type: String },
      badgeClass: { attribute: "badge-class", type: String },
      body: { type: String },
      bookmark: { type: String, reflect: true },
      config: { type: Object },
      image: { type: Object },
      labelAriaBookmark: { attribute: "label-aria-bookmark", type: String },
      size: { type: String },
      title: { type: String },
    };
  }

  constructor() {
    super();
    this.badge = null;
    this.badgeClass = "success";
    this.bookmark = null;
    this.bookmarkStates = {
      checked: "checked",
      unchecked: "unchecked",
    };
    this.config = fwcConfig;
    this.sizes = {
      sm: "sm",
      md: "md",
      lg: "lg",
      full: "full",
    };
    this.image = null;
    this.labelAriaBookmark = "Bookmark";
    this.size = this.sizes.sm;
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "bookmark" && oldValue) {
        this._updateBookmarkState();
      }
      if (propName === "image") {
        this._initImage();
        this._onImageLoad();
      }
    });
  }

  _updateBookmarkState() {
    const btn = this.shadowRoot.querySelector("#Bookmark");
    switch (this.bookmark) {
      case this.bookmarkStates.unchecked:
        btn.classList.remove("active");
        break;
      case this.bookmarkStates.checked:
        btn.classList.add("active");
        break;
    }
  }

  _setBookmarkState() {
    const oldVal = this.shadowRoot.querySelector("#Bookmark").classList.contains("active");
    this.bookmark = oldVal ? this.bookmarkStates.unchecked : this.bookmarkStates.checked;
  }

  _onBookmarkClick(e) {
    this._setBookmarkState();
    const event = new CustomEvent("fwc-card-media-image-bookmark", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  _initBookmark() {
    return html`
      <button id="Bookmark" class="bookmark ${this.bookmark === this.bookmarkStates.checked ? "active" : ""}" @click=${this._onBookmarkClick} aria-label="${this.labelAriaBookmark}" aria-pressed="${this.bookmark === this.bookmarkStates.checked}">
        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="50px" height="50px" viewBox="0 0 50 50">
          <path d="M5,0 L5,50 L8.46467391,47.4184783 L24.5652174,35.326087 L40.6657609,47.4184783 L44.1304348,50 L44.1304348,0 L5,0 Z M9.34782609,4.34782609 L39.7826087,4.34782609 L39.7826087,41.3043478 L25.8559783,30.8423913 L24.5652174,29.8913043 L23.2744565,30.8423913 L9.34782609,41.3043478 L9.34782609,4.34782609 Z" />
        </svg>
      </button>
    `;
  }

  _initOptional() {
    return html` <div class="optional ${this.badge ? "between" : "end"}" id="Optional">${this._initBadge()} ${ifDefined(this.bookmark ? this._initBookmark() : null)}</div> `;
  }

  _initBadge() {
    let badge = this.badge ? html`<span class="fwc-badge ${this.badgeClass}">${this.badge}</span>` : null;
    return badge;
  }

  _initBody() {
    const slot = html`<slot name="body"></slot>`;
    const body = html`<p>${this.body}</p>`;
    return this.body ? body : slot;
  }

  _initTitle() {
    const h2 = html`<h2>${this.title}</h2>`;
    const h3 = html`<h3>${this.title}</h3>`;
    const title = this.size === this.sizes.lg ? h2 : h3;
    return title;
  }

  _onImageLoad() {
    const img = this.shadowRoot.querySelector("#Image");
    if (img) {
      img.addEventListener("load", (e) => {
        e.target.classList.add("load");
      });
    }
  }

  _initImage() {
    if (this.image) {
      const source = this.image.source ? this.image.source : [];
      const srcset = source.map((src) => {
        return html` <source srcset="${src.srcset}" media="${ifDefined(src.media ? src.media : undefined)}" type="${ifDefined(src.type ? src.type : undefined)}" /> `;
      });
      return html`
        <picture>
          ${srcset}
          <img id="Image" src="${this.image.img.src}" alt="${this.image.img.alt}" width="${this.image.img.width}" height="${this.image.img.height}" class="${this.size}" @loadstart=${this._onImageLoad} />
        </picture>
      `;
    }
    return html`
      <div id="Placeholder" class="placeholder ${this.size}">
        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="51" viewBox="0 0 50 51">
          <path d="M0,5 L0,43.4615385 L50,43.4615385 L50,5 L0,5 Z M3.84615385,8.84615385 L46.1538462,8.84615385 L46.1538462,34.9278846 L31.1899038,19.9639423 L29.8076923,18.6418269 L28.4254808,19.9639423 L24.0384615,24.3509615 L16.7668269,17.0793269 L15.3846154,15.7572115 L14.0024038,17.0793269 L3.84615385,27.2355769 L3.84615385,8.84615385 Z M37.5,10.7692308 C34.8707942,10.7692308 32.6923077,12.9477173 32.6923077,15.5769231 C32.6923077,18.2061308 34.8707942,20.3846154 37.5,20.3846154 C40.1292077,20.3846154 42.3076923,18.2061308 42.3076923,15.5769231 C42.3076923,12.9477173 40.1292077,10.7692308 37.5,10.7692308 Z M37.5,14.6153846 C38.0558904,14.6153846 38.4615385,15.0210346 38.4615385,15.5769231 C38.4615385,16.1328135 38.0558904,16.5384615 37.5,16.5384615 C36.9441115,16.5384615 36.5384615,16.1328135 36.5384615,15.5769231 C36.5384615,15.0210346 36.9441115,14.6153846 37.5,14.6153846 Z M15.3846154,21.2259615 L21.2740385,27.1153846 L8.77403846,39.6153846 L3.84615385,39.6153846 L3.84615385,32.7644231 L15.3846154,21.2259615 Z M29.8076923,24.1105769 L45.3125,39.6153846 L14.3028846,39.6153846 L29.8076923,24.1105769 Z" transform="translate(0 .23)" />
        </svg>
      </div>
    `;
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/components/fwc-badges.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />

      <div class="card ${this.size}">
        <div class="wrapper">${this._initImage()} ${this.size !== this.sizes.full ? this._initOptional() : ""}</div>
        <div class="content" id="Content">
          ${this.size === this.sizes.full ? this._initOptional() : ""} ${this._initTitle()} ${this.body ? html`<p>${this.body}</p>` : null} ${!this.body ? html`<slot name="body" id="SlotBody"></slot>` : null}
          <slot name="links"></slot>
        </div>
      </div>
    `;
  }
}

class FwcCardMediaImage extends FwcBase {
  constructor() {
    super("fwc-card-media-image", FwcCardMediaImageComponent);
  }
}

class FwcCarouselGenericComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          display: block;
        }
        .fwc-carousel-generic-wrapper {
          position: relative;
        }

        .fwc-carousel-generic-wrapper:focus {
          outline: none;
        }

        .fwc-list-cont {
          flex-grow: 1;
          flex-shrink: 1;
          flex-basis: calc(100% - (9 * var(--fwcu)));
          padding: 0;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          scrollbar-width: none;
          display: flex;
          max-width: calc(144 * var(--fwcu));
          margin: 0 auto;
          align-items: stretch;
          justify-content: flex-start;
          flex-wrap: nowrap;
        }
        .fwc-list-cont::-webkit-scrollbar,
        .fwc-list-cont::-webkit-scrollbar-track,
        .fwc-list-cont::-webkit-scrollbar-thumb {
          width: 0;
          height: 0;
          display: none;
        }

        .fwc-carousal-wrapper {
          background-color: transparent;
          padding: calc(8 * var(--fwcu)) 0;
          display: flex;
          align-items: center;
          width: 100%;
          margin: 0 auto;
        }

        button {
          display: flex;
          align-items: center;
          justify-content: center;
          height: calc(4.5 * var(--fwcu));
          width: calc(4.5 * var(--fwcu));
          background: var(--color-white);
          border-radius: 50%;
          border: 1px solid var(--color-gray-four);
          flex-basis: calc(4.5 * var(--fwcu));
          box-shadow: var(--box-shadow);
          padding: 0;
          cursor: pointer;
        }
        button:disabled {
          border-color: var(--color-gray-five);
          pointer-events: none;
          color: var(--color-gray-three);
          box-shadow: none;
        }
        button:disabled svg {
          fill: var(--color-gray-three);
        }
        button.hidden {
          visibility: hidden;
          opacity: 0;
        }
        button svg {
          width: calc(1.75 * var(--fwcu));
          height: calc(1.75 * var(--fwcu));
          fill: var(--color-primary);
        }
        button.fwc-prev svg {
          transform: rotate(90deg);
          margin-left: calc(-0.25 * var(--fwcu));
        }
        button.fwc-next svg {
          transform: rotate(-90deg);
        }
        .fwc-pager {
          display: flex;
          position: absolute;
          bottom: calc(0.75 * var(--fwcu));
          left: 0;
          right: 0;
          justify-content: center;
        }
        .fwc-pager span {
          width: calc(0.5 * var(--fwcu));
          height: calc(0.5 * var(--fwcu));
          background: var(--color-gray-four);
          display: inline-block;
          margin: 0 calc(0.25 * var(--fwcu));
          border-radius: var(--border-radius);
          transition: all 0.25s ease-in-out;
        }
        .fwc-pager span.active {
          animation-name: carouselSlide;
          animation-duration: 250ms;
          background: var(--color-primary);
          width: calc(1.75 * var(--fwcu));
        }
        @keyframes carouselSlide {
          from {
            width: calc(0.5 * var(--fwcu));
          }
          to {
            width: calc(1.75 * var(--fwcu));
          }
        }
        @media screen and (max-width: 600px) {
          .fwc-carousal-wrapper {
            padding: calc(4.5 * var(--fwcu)) 0;
          }
          .fwc-list-cont {
            width: 100%;
          }
        }

        @media screen and (hover: none) and (pointer: coarse) {
          button {
            display: none;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      grouping: { type: Number },
      labelAria: { attribute: "label-aria", type: String },
      labelAriaPrevBtn: { attribute: "label-aria-prev-btn", type: String },
      labelAriaNextBtn: { attribute: "label-aria-next-btn", type: String },
      _active: {},
      _nextDisabled: {},
      _prevDisabled: {},
    };
  }

  constructor() {
    super();
    this._active = 0;
    this.carouselItems = [];
    this.grouping = 1;
    this.labelAria = "Carousel";
    this.labelAriaNextBtn = "Next Slide";
    this.labelAriaPrevBtn = "Previous Slide";
    this.wrapper = null;
    this._nextDisabled = false;
    this._prevDisabled = false;
    this._timeout = false;
    this._groupingStore = 1;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", () => this._handleResize());
  }
  disconnectedCallback() {
    window.removeEventListener("resize", () => this._handleResize());
    super.disconnectedCallback();
  }

  firstUpdated() {
    const pagerEl = this.shadowRoot.getElementById("Pagination");
    const animationEndEvent = new CustomEvent("fwc-carousel-generic-animation-end", {
      detail: this,
      bubbles: true,
      composed: false,
    });

    this.carouselItems = this._getSlotted();
    this._hideBtns = this.carouselItems.length > 1 ? false : true;
    this._handleScroll();
    this.requestUpdate();

    this._setDisabled();
    this._groupingStore = this.grouping;
    this._setPagerView();

    pagerEl.onanimationend = () => {
      this.dispatchEvent(animationEndEvent);
    };
  }

  _handleScroll() {
    const scrollContainer = this.shadowRoot.getElementById("Container");
    let ticking = false;
    let last_known_x_pos = 0;

    scrollContainer.addEventListener("scroll", () => {
      last_known_x_pos = Math.round(scrollContainer.scrollLeft);
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this._setActivePageFromScroll(last_known_x_pos);
          ticking = false;
        });

        ticking = true;
      }
    });
  }

  _setActivePageFromScroll(posLeft) {
    if (posLeft === 0) {
      this._active = 0;
      return;
    }
    this._active = Math.ceil(Math.round(posLeft / this.carouselItems[0].offsetWidth));
    this._setDisabled();
  }

  _getPager(cards) {
    const numPagers = this.grouping > 1 ? Math.ceil(cards.length / this.grouping) : cards.length;
    const active = this.grouping > 1 ? Math.ceil(this._active / this.grouping) : this._active;
    const items = [];
    for (let i = 0; i < numPagers; i++) {
      items.push(html`<span id="pager_${i}" class="${i === active ? "active" : ""}"></span>`);
    }
    return items;
  }

  _setPagerView() {
    if (window.innerWidth <= breakpoints.sm) {
      this.grouping = 1;
    } else {
      this.grouping = this._groupingStore;
    }
  }

  _handleResize() {
    clearTimeout(this._timeout);
    this._timeout = setTimeout(() => {
      this._setPagerView();
    }, 250);
  }

  _setEventAndScroll(slide, name) {
    const event = new CustomEvent(name, {
      detail: this,
      bubbles: true,
      composed: false,
    });

    this.carouselItems[slide].scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
    this.dispatchEvent(event);
  }

  _next() {
    let next = this._active;
    this._nextDisabled = true;
    if (next + 1 >= this.carouselItems.length) {
      return;
    }
    if (this.grouping > 1) {
      next = this._active + this.grouping * 2 - 1;
      if (next > this.carouselItems.length - 1) {
        next = this.carouselItems.length - 1;
      }
    } else {
      next += 1;
    }
    this._setEventAndScroll(next, "fwc-carousel-generic-next");
  }

  _prev() {
    let prev = this._active;
    this._prevDisabled = true;
    if (this.grouping > 1) {
      if (prev % this.grouping === 1) {
        prev -= 1;
      } else {
        prev = this._active - this.grouping;
      }
    } else {
      prev -= 1;
    }
    if (prev < 0) {
      prev = 0;
    }
    this._setEventAndScroll(prev, "fwc-carousel-generic-prev");
  }

  _setDisabled() {
    const gCheck = this.grouping > 1 ? this._active + this.grouping : this._active + 1;

    if (this._active - 1 < 0) {
      this._prevDisabled = true;
    } else {
      this._prevDisabled = false;
    }

    if (gCheck >= this.carouselItems.length) {
      this._nextDisabled = true;
    } else {
      this._nextDisabled = false;
    }
  }

  nextSlide() {
    this._next();
  }

  prevSlide() {
    this._prev();
  }

  slideToId(id) {
    if (this.isValidData(id)) {
      this._setEventAndScroll(id, "fwc-carousel-generic-slide");
    }
  }

  _buildGroupedChildren(children) {
    for (let i = 0; i < children.length; i++) {
      children[i].setStyles(100 / this.grouping);
      children[i].setAttribute("id", "Slide_" + i);
    }
    return children;
  }

  _getSlotted() {
    const slot = this.shadowRoot.querySelector("slot");
    const childNodes = slot ? slot.assignedNodes({ flatten: true }) : [];
    const children = childNodes.filter((node) => node.nodeType == Node.ELEMENT_NODE && node.nodeName === "FWC-CAROUSEL-GENERIC-ITEM");
    const slides = this.grouping > 1 ? this._buildGroupedChildren(children) : children;
    return slides;
  }

  /* c8 ignore next 21 */
  isValidData(id) {
    let errors = false;
    try {
      if (id > this.carouselItems.length - 1) {
        errors = true;
        throw new Error(`slideToId: ${id} is greater than this.carouselItems.length. Max: ${this.carouselItems.length - 1} `);
      }
    } catch (error) {
      console.error(error);
    }

    try {
      if (id < 0) {
        errors = true;
        throw new Error(`slideToId: ${id} is not valid. Min: 0 or greater is required `);
      }
    } catch (error) {
      console.error(error);
    }
    return !errors;
  }

  render() {
    return html`
      <section class="fwc-carousel-generic-wrapper" aria-roledescription="carousel" aria-label="${this.labelAria}">
        <div class="fwc-carousal-wrapper">
          <button class="fwc-prev ${this._hideBtns ? "hidden" : ""}" ?disabled=${this._prevDisabled} @click="${this._prev}" aria-label="${this.labelAriaPrevBtn}" aria-hidden="${this._hideBtns}" aria-disabled="${this._prevDisabled}">
            <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" focusable="false">
              <polygon points="2.719 39.66 0 36.941 23.641 13.3 25 12 26.359 13.3 50 36.941 47.281 39.66 25 17.378" transform="matrix(1 0 0 -1 0 51.66)"></polygon>
            </svg>
          </button>

          <div id="Container" class="fwc-list-cont" tabindex="0">
            <slot role="list" aria-label="carousel"></slot>
          </div>

          <button class="fwc-next ${this._hideBtns ? "hidden" : ""}" ?disabled=${this._nextDisabled} @click="${this._next}" aria-label="${this.labelAriaNextBtn}" aria-hidden="${this._hideBtns}" aria-disabled="${this._nextDisabled}">
            <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" focusable="false">
              <polygon points="2.719 39.66 0 36.941 23.641 13.3 25 12 26.359 13.3 50 36.941 47.281 39.66 25 17.378" transform="matrix(1 0 0 -1 0 51.66)"></polygon>
            </svg>
          </button>
        </div>
        <div id="Pagination" class="fwc-pager">${this._getPager(this.carouselItems)}</div>
      </section>
    `;
  }
}

class FwcCarouselGenericItemComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          flex-grow: 0;
          flex-shrink: 0;
          scroll-snap-align: start;
        }
        @media screen and (max-width: 600px) {
          :host {
            flex-grow: 1 !important;
            flex-basis: 100% !important;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      basis: { type: Number },
    };
  }

  constructor() {
    super();
    this.basis = 100;
  }

  firstUpdated() {
    this.setStyles(this.basis);
  }

  setStyles(basis) {
    this.shadowRoot.host.style.width = basis + "%";
    this.shadowRoot.host.style.paddingLeft = 1.5 * baseUnit + "px";
    this.shadowRoot.host.style.paddingRight = 1.5 * baseUnit + "px";
    this.basis = basis;
  }

  render() {
    return html` <slot></slot>`;
  }
}

class FwcCarouselGeneric extends FwcBase {
  constructor() {
    super("fwc-carousel-generic", FwcCarouselGenericComponent);
    const FwcCarouselGenericItem = new FwcBase("fwc-carousel-generic-item", FwcCarouselGenericItemComponent);
    FwcCarouselGenericItem.build();
  }
}

const FOCUSABLE_ELEMENT_SELECTORS = ["a[href]", "button:not([disabled])", 'input:not([disabled]):not([type="hidden"])', "select:not([disabled])", "textarea:not([disabled])", "[contenteditable]", '[tabindex]:not([tabindex="-1"])'].join(", ");

const KEY_CODE_MAP = {
  TAB: 9,
};

let keyboardHandler = null;
let containerElementRef = null;
let ignoredSelectorsRef = null;
let targetTrap = null;

function getActiveElement() {
  let activeElement;

  try {
    activeElement = document.activeElement;
  } catch (e) {
    activeElement = document.body || null;
  }

  while (activeElement && activeElement.shadowRoot && activeElement.shadowRoot.activeElement) {
    activeElement = activeElement.shadowRoot.activeElement;
  }

  return activeElement;
}

function getFocusableElements(containerElement, ignoreSelectors) {
  if (!containerElement) {
    throw new Error("Could not initialize focus-trapping - Container Element Missing");
  }

  // store a ref for refresh
  containerElementRef = containerElement;
  ignoredSelectorsRef = ignoreSelectors;

  let focusableElements = [].slice.call(containerElement.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS));

  // if there is a shadowRoot we need a separate query
  if (containerElement.shadowRoot) {
    const shadowEls = [].slice.call(containerElement.shadowRoot.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS));
    // shadow elements need to be at the beginning
    focusableElements = shadowEls.concat(focusableElements);
  }

  // remove ignored elements
  if (ignoreSelectors) {
    const ignoredShadowElements = [].slice.call(containerElement.shadowRoot.querySelectorAll(ignoreSelectors.join(",")));
    const ignoredElements = [].slice.call(containerElement.querySelectorAll(ignoreSelectors.join(","))).concat(ignoredShadowElements);

    focusableElements = focusableElements.filter((focusableEl) => {
      return !ignoredElements.some((ignoredEl) => ignoredEl === focusableEl);
    });
  }

  // order by tabindex
  return focusableElements.sort((a, b) => Number(a.getAttribute("tabindex")) - Number(b.getAttribute("tabindex")));
}

// ignoreSelectors is an array of strings, a list of css selectors to be ignored by the focus trap
// ['.someElement button']
function focusTrap(containerElement, ignoreSelectors) {
  const focusableElements = getFocusableElements(containerElement, ignoreSelectors);

  targetTrap = focusTrap;

  if (focusableElements.length > 0) {
    const firstFocusableEl = focusableElements[0];
    const lastFocusableEl = focusableElements[focusableElements.length - 1];
    keyboardHandler = function keyboardHandler(e) {
      const activeElem = getActiveElement();
      if (e.keyCode === KEY_CODE_MAP.TAB) {
        // Rotate Focus
        if (e.shiftKey && activeElem === firstFocusableEl) {
          e.preventDefault();
          lastFocusableEl.focus();
        } else if (!e.shiftKey && activeElem === lastFocusableEl) {
          e.preventDefault();
          firstFocusableEl.focus();
        }
      }
    };

    firstFocusableEl.focus();
    containerElement.addEventListener("keydown", keyboardHandler);
  }
}

function focusTrapCustomTabs(containerElement, ignoreSelectors) {
  const focusableElements = getFocusableElements(containerElement, ignoreSelectors);

  targetTrap = focusTrapCustomTabs;

  if (focusableElements.length > 0) {
    const firstFocusableEl = focusableElements[0];
    const lastFocusableEl = focusableElements[focusableElements.length - 1];
    firstFocusableEl.focus();
    keyboardHandler = function keyboardHandler(e) {
      const activeElem = getActiveElement();
      if (e.keyCode === KEY_CODE_MAP.TAB) {
        // Rotate Focus
        if (e.shiftKey && activeElem === firstFocusableEl) {
          e.preventDefault();
          lastFocusableEl.focus();
        } else if (!e.shiftKey && activeElem === lastFocusableEl) {
          e.preventDefault();
          firstFocusableEl.focus();
        } else if (e.shiftKey) {
          // custom tabbing, move to previous tab item on keydown
          e.preventDefault();
          for (let i = focusableElements.length; i > 0; i--) {
            if (activeElem === focusableElements[i]) {
              focusableElements[i - 1].focus();
              return;
            }
          }
        } else {
          // this is the custom tabbing, move to next tab item on keydown
          e.preventDefault();
          for (let i = 0; i < focusableElements.length; i++) {
            if (activeElem === focusableElements[i]) {
              focusableElements[i + 1].focus();
              return;
            }
          }
        }
      }
    };

    // firstFocusableEl.focus();
    containerElement.addEventListener("keydown", keyboardHandler);
  }
}

function resetFocusTrap() {
  removeFocusTrapListener(containerElementRef);
  setTimeout(() => {
    targetTrap(containerElementRef, ignoredSelectorsRef);
  }, 0);
}

function removeFocusTrapListener(containerElement) {
  if (containerElement && keyboardHandler) {
    containerElement.removeEventListener("keydown", keyboardHandler);
  }
}

class FwcHeaderPageComponent extends LitElement {
  /* For slotted content, use ./fwc-assets/styles/components/fwc-header-page.css */
  static get styles() {
    return [
      sharedStyles,
      css`
        @keyframes rotation {
          0% {
            transform: rotate(0deg) scale(1);
          }
          50% {
            transform: scale(2);
          }
          100% {
            transform: rotate(359deg) scale(1);
          }
        }
        :host {
          position: relative;
        }
        section {
          background-color: var(--color-primary);
          padding: calc(6 * var(--fwcu)) 0;
        }
        .blobs {
          overflow: hidden;
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
        }
        div.shadowed:not(.slotted) {
          display: flex;
          max-width: calc(144 * var(--fwcu));
          margin: 0 auto;
          align-items: flex-start;
          justify-content: flex-start;
          flex-wrap: nowrap;
        }
        .shadowed h1,
        .slotted h1 {
          margin: 0;
        }
        svg {
          position: absolute;
          opacity: 0.2;
          animation-name: rotation;
          animation-fill-mode: none;
          animation-duration: 20s;
          animation-iteration-count: infinite;
          transform-origin: center;
          animation-timing-function: cubic-bezier(0.785, 0.135, 0.15, 0.86);
        }
        svg.left {
          left: -115px;
          top: -70px;
          transform: rotate(293deg);
        }
        svg.mid-top {
          left: 37%;
          top: -375px;
          transform: rotate(-50deg);
        }
        svg.mid-bottom {
          left: 47%;
          transform: rotate(302deg);
          top: 36%;
        }
        svg.right {
          right: -348px;
          transform: rotateX(180deg);
        }
        h1 {
          color: white;
          padding: 0 calc(1.5 * var(--fwcu));
          margin: calc(4 * var(--fwcu)) 0;
        }
        @media screen and (max-width: 600px) {
          div.shadowed:not(.slotted) {
            /* Match fwc-row */
            width: calc(100% - (5 * var(--fwcu)));
          }
          section {
            padding: calc(2 * var(--fwcu)) 0;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      title: { type: String },
    };
  }

  constructor() {
    super();
    this.title = null;
    this.config = fwcConfig;
  }

  firstUpdated() {
    this.classList.add("fwc-container");
  }

  _blobs() {
    const leftBlob = html`
      <svg class="left" focusable="false" xmlns="http://www.w3.org/2000/svg" width="450" height="550" viewBox="0 0 450 550">
        <defs>
          <linearGradient id="left-a" x1="29.451%" x2="73.736%" y1="15.098%" y2="84.421%">
            <stop offset="0%" stop-color="#FFF" stop-opacity=".408" />
            <stop offset="58.024%" stop-color="#FFF" stop-opacity=".182" />
            <stop offset="100%" stop-opacity=".09" />
          </linearGradient>
        </defs>
        <path fill="url(#left-a)" d="M206.715387,10.3410556 L235.347677,9.18888856 C343.848361,4.8228068 435.344954,89.2405817 439.711035,197.741266 C439.926588,203.097915 439.922967,208.461138 439.700183,213.817491 L434.117864,348.031848 C429.780097,452.323739 346.15034,535.840657 241.852688,540.037608 L213.220398,541.189775 C104.719713,545.555857 13.2231213,461.138082 8.85703955,352.637398 C8.64148731,347.280749 8.64510794,341.917526 8.86789236,336.561173 L14.4502111,202.346816 C18.7879779,98.0549252 102.417734,14.5380069 206.715387,10.3410556 Z" opacity=".8" transform="translate(.346)" />
      </svg>
    `;
    const rightBlob = html`
      <svg class="right" focusable="false" xmlns="http://www.w3.org/2000/svg" width="450" height="549" viewBox="0 0 450 549">
        <path fill="#FFF" fill-opacity=".2" d="M1243.71539,9.73218384 L1272.34768,8.58001675 C1380.84836,4.21393499 1472.34495,88.6317099 1476.71104,197.132394 C1476.92659,202.489043 1476.92297,207.852266 1476.70018,213.208619 L1471.11786,347.422976 C1466.7801,451.714867 1383.15034,535.231785 1278.85269,539.428737 L1250.2204,540.580904 C1141.71971,544.946985 1050.22312,460.52921 1045.85704,352.028526 C1045.64149,346.671877 1045.64511,341.308654 1045.86789,335.952301 L1051.45021,201.737945 C1055.78798,97.4460534 1139.41773,13.929135 1243.71539,9.73218384 Z" transform="translate(-1036.654)" />
      </svg>
    `;
    const midBottomBlob = html`
      <svg class="mid-bottom" focusable="false" xmlns="http://www.w3.org/2000/svg" width="454" height="547" viewBox="0 0 454 547">
        <defs>
          <linearGradient id="mid-bottom-a" x1="26%" x2="79.518%" y1="36.887%" y2="89.438%">
            <stop offset="0%" stop-color="#FFF" stop-opacity=".21" />
            <stop offset="100%" stop-opacity=".15" />
          </linearGradient>
        </defs>
        <path fill="url(#mid-bottom-a)" d="M1786.71539,15.7321838 L1815.34768,14.5800167 C1923.84836,10.213935 2015.34495,94.6317099 2019.71104,203.132394 C2019.92659,208.489043 2019.92297,213.852266 2019.70018,219.208619 L2014.11786,353.422976 C2009.7801,457.714867 1926.15034,541.231785 1821.85269,545.428737 L1793.2204,546.580904 C1684.71971,550.946985 1593.22312,466.52921 1588.85704,358.028526 C1588.64149,352.671877 1588.64511,347.308654 1588.86789,341.952301 L1594.45021,207.737945 C1598.78798,103.446053 1682.41773,19.929135 1786.71539,15.7321838 Z" transform="translate(-1577.654 -7)" style="mix-blend-mode:color" />
      </svg>
    `;
    const midTopBlob = html`
      <svg class="mid-top" focusable="false" xmlns="http://www.w3.org/2000/svg" width="454" height="548" viewBox="0 0 454 548">
        <defs>
          <linearGradient id="mid-top-a" x1="79.518%" x2="26%" y1="89.438%" y2="36.887%">
            <stop offset="0%" stop-color="#FFF" stop-opacity=".15" />
            <stop offset="100%" stop-opacity=".15" />
          </linearGradient>
        </defs>
        <path fill="url(#mid-top-a)" d="M738.715387,10.7776312 L767.347677,9.62546415 C875.848361,5.25938239 967.344954,89.6771573 971.711035,198.177842 C971.926588,203.53449 971.922967,208.897714 971.700183,214.254067 L966.117864,348.468423 C961.780097,452.760314 878.15034,536.277233 773.852688,540.474184 L745.220398,541.626351 C636.719713,545.992433 545.223121,461.574658 540.85704,353.073973 C540.641487,347.717325 540.645108,342.354101 540.867892,336.997749 L546.450211,202.783392 C550.787978,98.4915008 634.417734,14.9745825 738.715387,10.7776312 Z" transform="translate(-527.654 -3)" style="mix-blend-mode:color" />
      </svg>
    `;

    return {
      right: rightBlob,
      left: leftBlob,
      midTop: midTopBlob,
      midBottom: midBottomBlob,
    };
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />
      <section>
        <div class="blobs">${this._blobs().right} ${this._blobs().midBottom} ${this._blobs().midTop} ${this._blobs().left}</div>
        <div class=${this.title ? "shadowed" : "slotted"}>
          ${this.title ? html`<h1>${this.title}</h1>` : html``}
          <slot></slot>
        </div>
      </section>
    `;
  }
}

class FwcHeaderPage extends FwcBase {
  constructor() {
    super("fwc-header-page", FwcHeaderPageComponent);
  }
}

class MobileViewComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .mobile-view-header,
        .mobile-view-footer {
          display: none;
        }

        @media screen and (max-width: 600px) {
          .mobile-view-wrapper.open {
            position: fixed;
            display: flex;
            flex-direction: column;
            width: 100%;
            top: 0;
            left: 0;
            background: var(--color-white);
            height: 100%;
            z-index: 3;
          }
          .open .mobile-view-header {
            display: block;
            width: 100%;
          }
          .open .mobile-view-body {
            flex-grow: 1;
            flex-shrink: 1;
            flex-basis: auto;
            overflow: auto;
            position: relative;
            margin-bottom: calc(9.75 * var(--fwcu));
            z-index: 3;
          }
          .open .mobile-view-body.full {
            margin-bottom: 0;
          }

          .open .mobile-view-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            height: calc(9.75 * var(--fwcu));
            display: block;
            padding: calc(2.5 * var(--fwcu));
            background: var(--color-white);
            box-shadow: 0px -4px 3px 0 rgba(50, 50, 50, 0.1);
            z-index: 3;
          }
          .mobile-view-footer.hide {
            display: none;
          }
          .mobile-view-wrapper.decrease-index-z .mobile-view-header,
          .mobile-view-wrapper.decrease-index-z .mobile-view-footer {
            z-index: 2;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      hideFooter: { attribute: "hide-footer", type: Boolean },
      mobileHeaderText: { attribute: "mobile-header-text", type: String },
      open: { type: Boolean },
      decreaseIndexZ: { attribute: "decrease-index-z", type: Boolean },
    };
  }

  constructor() {
    super();
    this.config = null; // Must pass config in parent component.
    this.hideFooter = false;
    this.mobileHeaderText = "";
    this.open = false;
    this.decreaseIndexZ = false;
    const header = new FwcHeaderPage();
    header.build();
  }

  render() {
    return html`
      <div class="mobile-view-wrapper ${this.open ? "open" : ""} ${this.decreaseIndexZ ? "decrease-index-z" : ""}">
        <fwc-header-page title=${this.mobileHeaderText} config=${JSON.stringify(this.config)} class="mobile-view-header "></fwc-header-page>

        <div class="mobile-view-body ${this.hideFooter ? "full" : ""}">
          <slot name="body"></slot>
        </div>

        <div class="mobile-view-footer ${this.hideFooter ? "hide" : ""}">
          <slot name="mobile-actions"></slot>
        </div>
      </div>
    `;
  }
}

class MobileView extends FwcBase {
  constructor() {
    super("mobile-view", MobileViewComponent);
  }
}

const sharedMobileViewStyles = css`
  .mobile-view-footer-slot {
    display: none;
  }

  @media screen and (max-width: 600px) {
    .mobile-view-footer-slot {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 4rem;
    }
    .mobile-view-footer-slot button {
      flex-basis: 50%;
    }
    .mobile-view-footer-slot button.fwc-link {
      text-align: center;
      font-size: calc(2 * var(--fwcu));
    }
  }
`;

class FwcDatePickerComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      sharedMobileViewStyles,
      css`
        :host {
          position: relative;
        }
        button[disabled] {
          appearance: none;
          border: none;
          background: transparent;
        }
        .fwc-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }
        .fwc-date-picker-wrapper {
          width: 100%;
        }
        .fwc-calendar {
          position: absolute;
          display: flex;
          flex-direction: row;
          visibility: hidden;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
          box-shadow: var(--box-shadow);
          border: none;
          background: white;
          margin-top: 0;
          height: 0;
          width: 0;
          overflow: hidden;
        }
        .fwc-calendar-mobile-nav {
          display: none;
        }
        .fwc-calendar-month {
          padding: calc(2.5 * var(--fwcu)) calc(1.5 * var(--fwcu)) calc(1.5 * var(--fwcu));
        }
        .fwc-calendar.open {
          z-index: 3;
          visibility: visible;
          opacity: 1;
          height: auto;
          width: auto;
          border: solid 1px rgba(var(--color-rgb-primary), 0.06);
          overflow: visible;
        }
        /* we use this class to get the calendar dimensions and set it position before it opens _setCalendarPosition()*/
        .fwc-calendar.pre-open {
          height: auto;
          width: auto;
        }
        .fwc-calendar-header {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          margin-bottom: calc(2.5 * var(--fwcu));
        }
        .fwc-date-picker-input {
          position: relative;
        }
        .fwc-date-picker-input span {
          position: absolute;
        }
        .fwc-date-picker-input input {
          width: 100%;
          font-family: var(--font-sans-serif, "sans-serif");
        }
        .fwc-icon-calendar {
          border: none;
          appearance: none;
          background: none;
          position: absolute;
          right: calc(1.25 * var(--fwcu));
          top: 0;
          bottom: 0;
        }
        .fwc-icon-calendar:hover {
          cursor: pointer;
        }
        .fwc-icon-calendar svg {
          fill: var(--color-primary);
        }
        .fwc-icon-calendar.invalid svg {
          fill: var(--color-alert-error);
        }

        .fwc-btn-prev,
        .fwc-btn-next {
          border: none;
          appearance: none;
          background: transparent;
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          height: calc(3 * var(--fwcu));
          width: calc(3 * var(--fwcu));
          padding: 0;
        }
        .fwc-btn-prev svg,
        .fwc-btn-next svg {
          fill: var(--color-primary);
          height: auto;
          width: 100%;
        }
        .fwc-btn-next {
          right: calc(1.5 * var(--fwcu));
          top: 0;
          bottom: 0;
          margin: auto;
        }
        .fwc-btn-prev {
          transform: rotate(180deg);
          left: calc(1.5 * var(--fwcu));
          top: 0;
          bottom: 0;
          margin: auto;
        }
        .fwc-btn-next:hover,
        .fwc-btn-prev:hover {
          cursor: pointer;
        }
        .fwc-month-year {
          font-weight: 700;
          text-transform: uppercase;
          color: var(--color-gray-one);
        }
        .fwc-calendar-body th {
          font-size: calc(1.5 * var(--fwcu));
          padding-bottom: calc(1.5 * var(--fwcu));
          color: var(--color-gray-one);
        }
        .fwc-calendar-body button {
          appearance: none;
          border: none;
          background: transparent;
          width: calc(5.5 * var(--fwcu));
          height: calc(5.5 * var(--fwcu));
          border-radius: 50%;
          font-size: calc(1.5 * var(--fwcu));
          color: var(--color-gray-one);
          font-family: var(--font-sans-serif, "sans-serif");
        }
        .fwc-calendar-body button:hover {
          cursor: pointer;
        }
        .fwc-calendar-body button:hover,
        .fwc-calendar-body button:focus {
          border: 2px solid var(--color-primary);
          font-size: calc(2 * var(--fwcu));
          font-weight: 700;
          color: var(--color-primary);
          outline: none;
        }
        .fwc-calendar-body button:disabled {
          color: var(--color-gray-four);
        }
        .fwc-calendar-body button:disabled {
          cursor: auto;
        }
        .fwc-calendar-body button:disabled:hover,
        .fwc-calendar-body button:disabled:focus {
          border: 2px solid transparent;
          color: var(--color-gray-four);
          outline: none;
          font-size: calc(1.5 * var(--fwcu));
          font-weight: 400;
        }
        .fwc-calendar-body button.active {
          background: var(--color-primary);
          color: var(--color-white);
          font-size: calc(2 * var(--fwcu));
        }
        .fwc-calendar-body button.active:hover {
          border: 2px solid transparent;
          color: var(--color-white);
        }
        .fwc-calendar-body button span {
          pointer-events: none;
        }
        .fwc-calendar-body button[data-date=""] {
          visibility: hidden;
        }
        .fwc-hide {
          display: none;
          visibility: hidden;
        }
        .fwc-footer-container {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .fwc-calender-desktop-footer {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          align-items: center;
          margin-top: calc(4 * var(--fwcu));
        }
        .fwc-calender-desktop-footer .fwc-btn-secondary {
          margin-right: calc(2 * var(--fwcu));
        }
        @media screen and (max-width: 900px) {
          .fwc-calendar {
            display: block;
          }
          .fwc-calendar-month:nth-of-type(3) {
            padding-top: 0;
          }
        }
        @media screen and (max-width: 600px) {
          .fwc-overlay {
            background-color: var(--color-white);
          }
          .fwc-input.open {
            opacity: 0;
          }
          .fwc-calendar-header .fwc-btn-prev,
          .fwc-calendar-header .fwc-btn-next {
            display: none;
          }
          .fwc-calendar.open {
            width: 100%;
            margin-top: 0;
            /* important is needed to override the inline positioning */
            top: 0 !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            box-shadow: none;
            border: none;
          }
          .fwc-btn-prev,
          .fwc-btn-next {
            position: relative;
          }
          .fwc-calendar-mobile-nav {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: calc(2.5 * var(--fwcu)) 0 0;
            font-size: calc(2 * var(--fwcu));
            color: var(--color-gray-one);
            font-weight: 700;
          }
          .fwc-calendar-mobile-nav span {
            display: inline-block;
            min-width: calc(4 * var(--fwcu));
          }
          .fwc-calendar-mobile-nav span:nth-child(2) {
            margin: 0 calc(4 * var(--fwcu));
          }
          /* ios flex padding bottom hack */
          .fwc-calendar:after {
            content: "";
            display: block;
            padding-bottom: calc(12.5 * var(--fwcu));
          }
          .fwc-calendar-month {
            flex-basis: 100%;
            padding: calc(2.5 * var(--fwcu)) calc(2.5 * var(--fwcu)) calc(1.5 * var(--fwcu));
          }
          .fwc-calendar-body table {
            width: 100%;
          }
          .fwc-calendar-body td {
            width: 14.29%;
          }
          .fwc-calendar-body button {
            width: 100%;
            padding-top: 90%;
            position: relative;
            border: 2px solid transparent;
          }
          .fwc-calendar-body button > span {
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .fwc-calender-desktop-footer {
            display: none;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      startDate: { attribute: "start-date", type: String },
      showDate: { attribute: "show-date", type: Boolean },
      monthNames: { attribute: "month-names", type: Array },
      dayNames: { attribute: "day-names", type: Array },
      dateRange: { attribute: "date-range", type: Array },
      disabledDates: { attribute: "disabled-dates", type: Array },
      btnCancel: { attribute: "btn-cancel", type: String },
      btnApply: { attribute: "btn-apply", type: String },
      errorInvalidLabel: { attribute: "error-invalid-label", type: String },
      errorDisabledLabel: { attribute: "error-disabled-label", type: String },
      inputLabel: { attribute: "input-label", type: String },
      labelAriaNext: { attribute: "label-aria-next", type: String },
      labelAriaPrevious: { attribute: "label-aria-previous", type: String },
      labelAriaCancel: { attribute: "label-aria-cancel", type: String },
      labelAriaApply: { attribute: "label-aria-apply", type: String },
      labelAriaCalendarIcon: { attribute: "label-aria-calendar-icon", type: String },
      labelAriaDayNames: { attribute: "label-aria-day-names", type: Array },
      invalid: { type: Boolean },
      config: { type: Object },
      selectedDate: {},
      _calendarTableRows: {},
      _isCalendarOpen: {},
      _rangeStart: {},
      _rangeEnd: {},
    };
  }

  constructor() {
    super();
    this.mobileView = new MobileView();
    this.mobileView.build();
    this.startDate = new Date();
    this._startDate = new Date();
    this.monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    this.dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    this.dateRange = [null, null];
    this.disabledDates = [];
    this.inputLabel = "Select a Date";
    this.labelAriaClose = "Cancel";
    this.invalid = false;
    this.btnApply = "Apply";
    this.btnCancel = "Cancel";
    this.labelAriaNext = "Next Month";
    this.labelAriaCancel = "Cancel";
    this.labelAriaApply = "Apply";
    this.labelAriaPrevious = "Previous Month";
    this.labelAriaCalendarIcon = "Toggle Calendar";
    this.errorDisabledLabel = "Disabled Date";
    this.errorInvalidLabel = "Invalid Date";
    this.labelAriaDayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    this._calendarMonths = [];
    this._calendarTableRows = [];
    this.selectedDate = "";
    this._isCalendarOpen = false;
    this._rangeEnd = false;
    this._rangeStart = false;
    this._calendarMaxDate = new Date();
    this._calendarMinDate = new Date("01/01/1900");
    this._numMonthsToDisplay = 2;
    this._maxFutureYears = 20;
    this._tempDateStorage = null;
    this.config = fwcConfig;
    this._calendarMaxDate.setFullYear(this._calendarMaxDate.getFullYear() + this._maxFutureYears);
    this.focusTrapDesk = ["button.fwc-hide", ".fwc-btn-primary", ".fwc-btn-secondary", ".fwc-calendar-mobile-nav .fwc-btn-prev", ".fwc-calendar-mobile-nav .fwc-btn-next", ".fwc-selected-date", ".fwc-icon-calendar"];
    this.focusTrapMobile = ["button.fwc-hide", ".fwc-calendar-header button", ".fwc-selected-date", ".fwc-icon-calendar"];
    this._handleCalendarClickOut = this._handleCalendarClickOut.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("click", this._handleCalendarClickOut);
  }
  disconnectedCallback() {
    document.removeEventListener("click", this._handleCalendarClickOut);
    super.disconnectedCallback();
  }

  firstUpdated() {
    const calendar = this.shadowRoot.querySelector(".fwc-calendar");
    calendar.addEventListener("transitionend", (e) => {
      // set the focus trap inside the calendar after open
      if (this._isCalendarOpen && e.propertyName === "opacity") {
        const ignoredFocusTrapSelectors = window.innerWidth > breakpoints.sm ? this.focusTrapDesk : this.focusTrapMobile;
        focusTrapCustomTabs(this, ignoredFocusTrapSelectors);
      }
      // set the focus back to the open button on calendar close
      if (!this._isCalendarOpen && e.propertyName === "opacity") {
        const iconBtn = this.shadowRoot.querySelector(".fwc-icon-calendar");
        iconBtn.focus();
        removeFocusTrapListener(this);
      }
    });

    // allow esc to close the calendar
    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        this.toggleCalendarOpen();
      }
    });

    this._setMinMaxDates();
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "startDate") {
        const parsedDate = new Date(this.startDate);
        // check if a valid date string was passed as an attribute
        if (parsedDate instanceof Date && !isNaN(parsedDate)) {
          this._startDate = new Date(this.startDate);
          this._setInputDate();
          this._buildCalendar();
        } else {
          try {
            throw new Error('Invalid date string passed to "start-date" attribute');
          } catch (e) {
            /* eslint-disable no-console */
            console.error(e);
            /* eslint-enable no-console */
          }
        }
      }

      // when the calendar view changes
      // check and set the selectedDate corresponding button to active
      if (propName === "_calendarTableRows") {
        this._setActiveBtnState(this.selectedDate);
      }
    });
  }

  _setInputDate() {
    // Set date on init with show-date attribute
    if (!this.showDate) return;
    this._changeSelectedDate(this._startDate, true);
  }

  _handleCalendarClickOut(e) {
    if (!this.contains(e.target) && this._isCalendarOpen) {
      this.toggleCalendarOpen();
    }
  }

  _buildCalendar() {
    // show one month in the calendar view between 600 and 900 viewport
    if (window.innerWidth > breakpoints.sm && window.innerWidth <= breakpoints.md) {
      this._numMonthsToDisplay = 1;
    } else {
      this._numMonthsToDisplay = 2;
    }

    this._calendarMonths = this._getDaysInMonths(this._startDate, this._numMonthsToDisplay);
    this._calendarTableRows = this._calendarMonths.map((month) => {
      return this._breakDaysIntoTableRows(month);
    });

    //disable all date outside of dateRange and individual dates passed
    this._rangeEnd = this._isEndOfRange();
    this._rangeStart = this._isStartOfRange();
    this._disableDates();
  }

  _getDaysInMonths(date, numMonths) {
    const months = [];
    const dt = new Date(date);

    for (let i = 0; i < numMonths; i++) {
      const month = dt.getMonth();
      const days = [];
      dt.setDate(1);

      while (dt.getMonth() === month) {
        days.push(new Date(dt));
        dt.setDate(dt.getDate() + 1);
      }

      months.push(days);

      // set date to 1st day of next month
      dt.setDate(dt.getMonth() + 1);
      dt.setDate(1);
    }

    return months;
  }

  _isEndOfRange() {
    // check if we can move to a calendar in the future
    if (this._calendarMonths.length) {
      const i = this._calendarMonths.length - 1;
      const lastDayInCalendarView = this._calendarMonths[i][this._calendarMonths[i].length - 1];
      return lastDayInCalendarView.getFullYear() === this._calendarMaxDate.getFullYear() && lastDayInCalendarView.getMonth() >= this._calendarMaxDate.getMonth();
    }
    return false;
  }

  _isStartOfRange() {
    // check if we can move to a calendar in the past
    if (this._calendarMonths.length) {
      const firstDayInCalendarView = this._calendarMonths[0][0];
      return firstDayInCalendarView.getFullYear() === this._calendarMinDate.getFullYear() && firstDayInCalendarView.getMonth() <= this._calendarMinDate.getMonth();
    }
    return false;
  }

  _disableDates() {
    setTimeout(() => {
      const dateButtons = this.shadowRoot.querySelectorAll("[data-date]");
      dateButtons.forEach((btn) => {
        const btnDate = new Date(btn.getAttribute("data-date"));
        btnDate.setHours(0, 0, 0);

        // remove disabled for all active buttons, ignore empty placeholders
        // all active date buttons need a tabindex of 4 by default
        if (btn.getAttribute("data-date")) {
          btn.removeAttribute("disabled");
          btn.setAttribute("tabindex", 4);
        }

        // check list from attribute disabled-dates
        this.disabledDates.forEach((disabledDate) => {
          disabledDate = new Date(disabledDate);
          if (btnDate.getFullYear() === disabledDate.getFullYear() && btnDate.getMonth() === disabledDate.getMonth() && btnDate.getDate() === disabledDate.getDate()) {
            btn.setAttribute("disabled", "");
            btn.setAttribute("tabindex", -1);
          }
        });

        // check against date range
        // move time to seconds vs milliseconds
        if (Math.floor(this._calendarMinDate.getTime() / 1000) > Math.floor(btnDate.getTime() / 1000) || Math.floor(this._calendarMaxDate.getTime() / 1000) < Math.floor(btnDate.getTime() / 1000)) {
          btn.setAttribute("disabled", "");
          btn.setAttribute("tabindex", -1);
        }
      });
    }, 0);
  }

  _setMinMaxDates() {
    // if null is passed for start date set it to year 1900
    this._calendarMinDate = !this.dateRange[0] ? new Date("01/01/1900") : new Date(this.dateRange[0]);
    // if null is passed for end date set it to 20 years from now
    if (!this.dateRange[1]) {
      this._calendarMaxDate = new Date();
      this._calendarMaxDate.setFullYear(this._calendarMaxDate.getFullYear() + this._maxFutureYears);
    } else {
      this._calendarMaxDate = new Date(this.dateRange[1]);
    }

    this._calendarMinDate.setHours(0, 0, 0);
    this._calendarMaxDate.setHours(0, 0, 0);
  }

  _buildTableHeaders() {
    return html`
      <tr>
        <th aria-label="${this.labelAriaDayNames[0]}">${this.dayNames[0]}</th>
        <th aria-label="${this.labelAriaDayNames[1]}">${this.dayNames[1]}</th>
        <th aria-label="${this.labelAriaDayNames[2]}">${this.dayNames[2]}</th>
        <th aria-label="${this.labelAriaDayNames[3]}">${this.dayNames[3]}</th>
        <th aria-label="${this.labelAriaDayNames[4]}">${this.dayNames[4]}</th>
        <th aria-label="${this.labelAriaDayNames[5]}">${this.dayNames[5]}</th>
        <th aria-label="${this.labelAriaDayNames[6]}">${this.dayNames[6]}</th>
      </tr>
    `;
  }

  _breakDaysIntoTableRows(days) {
    let daysArr = [...days];
    let rows = [];
    let week = [];
    const startDay = days[0].getDay();
    const lastDay = days[days.length - 1].getDay();

    // we need to add empty slots to the beginning and end of each month to fill out the table rows
    // add empty values to beginning
    for (let i = 0; i < startDay; i++) {
      daysArr.unshift(null);
    }

    // add empty values to the end
    for (let i = 0; i < 6 - lastDay; i++) {
      daysArr.push(null);
    }

    // break daysArr into rows by week, sunday to saturday
    for (let i = 1; i <= daysArr.length; i++) {
      week.push(daysArr[i - 1]);

      if (i % 7 === 0) {
        rows.push(week);
        week = [];
      }
    }

    return rows;
  }

  _getMonthAndYear(date) {
    if (window.innerWidth > breakpoints.sm) {
      return `${this.monthNames[date.getMonth()]} ${date.getFullYear()}`;
    } else {
      return `${this.monthNames[date.getMonth()]}`;
    }
  }

  _formatDate(date) {
    var month = date.getMonth() + 1;
    var day = date.getDate();
    var year = date.getFullYear();
    return month + "/" + day + "/" + year;
  }

  _changeCalendarMonths(dir) {
    const num = dir === "next" ? this._numMonthsToDisplay : -this._numMonthsToDisplay;
    this._startDate = new Date(this._calendarMonths[0][0].getFullYear(), this._calendarMonths[0][0].getMonth() + num, 1);
    this._buildCalendar();
    resetFocusTrap();
  }

  _updateSelectDateInput(date) {
    const input = this.shadowRoot.querySelector("#SelectedDateInput");
    input.value = date ? this._formatDate(date) : "";
  }

  _changeSelectedDate(date, set) {
    this._updateSelectDateInput(date);
    this.selectedDate = date;
    this._emitSelectedEvent(date);
    if (set) this._setActiveBtnState(date);
  }

  _handleSelectedDate(date, e) {
    // Store date and let _handleApply set date
    if (e.code === "Enter" || e.code === "Space") {
      this._tempDateStorage = date;
      this._changeSelectedDate(this._tempDateStorage, true);
    } else if (e.type === "click") {
      this._tempDateStorage = date;
      this._setActiveBtnState(date);
    }
  }

  _handleApply() {
    if (this._tempDateStorage) this._changeSelectedDate(this._tempDateStorage, false);
    this.toggleCalendarOpen();
  }

  //public method to reset calendar and input
  resetDate(emit) {
    this._updateSelectDateInput("");
    this._resetDate(emit);
  }

  _resetDate(emit) {
    this.selectedDate = "";
    this._setActiveBtnState("");
    if (emit) this._emitSelectedEvent("");
  }

  _parseDate(e) {
    if (!e.target.value) {
      this._resetDate();
      this._emitBlurEvent();
      return;
    }
    // some browsers do not like to parse dates as 01-01-2020
    // so we replace all the dashes with slashes, if a user typed in that format
    const parsedDate = new Date(e.target.value.replace(/-/g, "/"));

    // check manual entered date against disabled dates & min/max range
    if (this._checkDisabledDate(parsedDate)) {
      this._emitErrorEvent(this.errorDisabledLabel);
      return false;
    }

    this._emitBlurEvent();

    if (parsedDate instanceof Date && !isNaN(parsedDate)) {
      this._startDate = parsedDate;
      // build the calendar view to reflect the new typed starting date
      this._buildCalendar();
      // highlight the date
      this._changeSelectedDate(parsedDate, true);
    } else {
      this._emitErrorEvent(this.errorInvalidLabel);
    }
  }

  //returns true if date is disabled or out of min/max range
  _checkDisabledDate(date) {
    return this.disabledDates.some((disabledDate) => {
      disabledDate = new Date(disabledDate);
      return (date.getFullYear() === disabledDate.getFullYear() && date.getMonth() === disabledDate.getMonth() && date.getDate() === disabledDate.getDate()) || Math.floor(this._calendarMinDate.getTime() / 1000) > Math.floor(date.getTime() / 1000) || Math.floor(this._calendarMaxDate.getTime() / 1000) < Math.floor(date.getTime() / 1000);
    });
  }

  _emitErrorEvent(error) {
    // emit date parse error, date entered into input field was not valid
    const event = new CustomEvent("fwc-date-picker-error", {
      detail: error,
      bubbles: true,
    });
    this.dispatchEvent(event);
    this._resetDate();
  }

  _emitFocusEvent() {
    const event = new CustomEvent("fwc-date-picker-focus", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  _emitBlurEvent() {
    const event = new CustomEvent("fwc-date-picker-blur", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  _emitSelectedEvent(data) {
    const event = new CustomEvent("fwc-date-picker-selected", {
      detail: data,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  _setActiveBtnState(curDate) {
    // loop all the buttons and match their data-date attr to the selectedDate to add an active state
    // this happens because we need to check/set the active date when switching calendar months, typing in a date, and clicking one
    const btns = this.shadowRoot.querySelectorAll(".fwc-calendar-body button");
    const lastActiveBtn = this.shadowRoot.querySelector("button.active");
    if (lastActiveBtn) {
      lastActiveBtn.classList.remove("active");
    }

    if (btns && curDate) {
      const curDateFormatted = this._formatDate(curDate);
      btns.forEach((btn) => {
        const dt = btn.getAttribute("data-date");
        if (dt === curDateFormatted) {
          btn.classList.add("active");
        }
      });
    }
  }

  toggleCalendarOpen() {
    this._buildCalendar();
    // check the calendar before it opens and set its position
    if (!this._isCalendarOpen && window.innerWidth > breakpoints.sm) {
      setTimeout(() => {
        this._setCalendarPosition();
      }, 0);
    } else {
      // remove transform positioning from calendar on close
      // because the getBoundingClientRect will now be changed with the new transform styling
      this._removeCalendarPositioning();
    }

    this._isCalendarOpen = !this._isCalendarOpen;
    if (this._isCalendarOpen && this.selectedDate) {
      this._setActiveBtnState(this.selectedDate);
    } else {
      this._tempDateStorage = null;
    }
    this._toggleBodyScrollClass(this._isCalendarOpen);
  }

  _toggleBodyScrollClass(isOpen) {
    if (isOpen && window.innerWidth <= breakpoints.sm) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }

  _setCalendarPosition() {
    const calendar = this.shadowRoot.querySelector(".fwc-calendar");
    const input = this.shadowRoot.querySelector(".fwc-date-picker-input");
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const CALENDAR_VERTICAL_MARGIN = baseUnit * 2;
    let inputRect = null;
    let calendarRect = null;
    // this sets the calendar width and height to auto so we can get its dimensions
    calendar.classList.add("pre-open");

    inputRect = input.getBoundingClientRect();
    calendarRect = calendar.getBoundingClientRect();

    // check and set left and right positioning
    if (viewportWidth - inputRect.left > calendarRect.width) {
      // set left positioned
      // check first if there is space right as initial option
      calendar.style.left = "0px";
    } else if (inputRect.right > calendarRect.width) {
      // set right positioned
      calendar.style.right = "0px";
    } else {
      // fallback left positioned
      calendar.style.left = "0px";
    }

    if (viewportHeight - inputRect.bottom > calendarRect.height) {
      // set bottom positioned
      // check first if there is space below as initial option
      calendar.style.top = `calc(100% + ${CALENDAR_VERTICAL_MARGIN}px`;
    } else if (inputRect.top > calendarRect.height) {
      // set top positioned
      calendar.style.bottom = `calc(100% - ${CALENDAR_VERTICAL_MARGIN}px)`;
    } else {
      // fallback bottom positioned
      calendar.style.top = `calc(100% + ${CALENDAR_VERTICAL_MARGIN}px`;
    }

    calendar.classList.remove("pre-open");
  }

  _removeCalendarPositioning() {
    const calendar = this.shadowRoot.querySelector(".fwc-calendar");
    calendar.style.removeProperty("left");
    calendar.style.removeProperty("right");
    calendar.style.removeProperty("bottom");
    calendar.style.removeProperty("top");
  }

  _buildApplyButton() {
    return html` <button class="fwc-btn fwc-btn-primary" aria-label="${this.labelAriaApply}" @click="${this._handleApply}" tabindex="6">${this.btnApply}</button> `;
  }

  _buildCancelButton() {
    return html` <button class="fwc-btn fwc-btn-secondary" aria-label="${this.labelAriaCancel}" @click="${this.toggleCalendarOpen}" tabindex="5">${this.btnCancel}</button> `;
  }

  _arrowSvg() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" focusable="false">
        <polygon points="14.067 50 11 46.933 32.933 25 11 3.067 14.067 0 37.533 23.467 39 25 37.533 26.533" />
      </svg>
    `;
  }

  _buttonPrev() {
    return html` <button class="fwc-btn-prev ${this._rangeStart ? "fwc-hide" : ""}" @click="${() => this._changeCalendarMonths("prev")}" aria-label="${this.labelAriaPrevious}" tabindex="1">${this._arrowSvg()}</button> `;
  }

  _buttonNext() {
    return html` <button class="fwc-btn-next ${this._rangeEnd ? "fwc-hide" : ""}" aria-label="${this.labelAriaNext}" @click="${() => this._changeCalendarMonths("next")}" tabindex="2">${this._arrowSvg()}</button> `;
  }

  _calendarIcon() {
    return html`
      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" focusable="false" width="20px" height="20px" viewBox="0 0 36.449 36.448" xml:space="preserve">
        <g>
          <g>
            <rect x="12.858" y="14.626" width="4.596" height="4.089" />
            <rect x="18.996" y="14.626" width="4.595" height="4.089" />
            <rect x="25.128" y="14.626" width="4.596" height="4.089" />
            <rect x="6.724" y="20.084" width="4.595" height="4.086" />
            <rect x="12.858" y="20.084" width="4.596" height="4.086" />
            <rect x="18.996" y="20.084" width="4.595" height="4.086" />
            <rect x="25.128" y="20.084" width="4.596" height="4.086" />
            <rect x="6.724" y="25.54" width="4.595" height="4.086" />
            <rect x="12.858" y="25.54" width="4.596" height="4.086" />
            <rect x="18.996" y="25.54" width="4.595" height="4.086" />
            <rect x="25.128" y="25.54" width="4.596" height="4.086" />
            <path
              d="M31.974,32.198c0,0.965-0.785,1.75-1.75,1.75h-24c-0.965,0-1.75-0.785-1.75-1.75V12.099h-2.5v20.099
            c0,2.343,1.907,4.25,4.25,4.25h24c2.344,0,4.25-1.907,4.25-4.25V12.099h-2.5V32.198z"
            />
            <path
              d="M30.224,3.948h-1.098V2.75c0-1.517-1.197-2.75-2.67-2.75c-1.474,0-2.67,1.233-2.67,2.75v1.197h-2.74V2.75
            c0-1.517-1.197-2.75-2.67-2.75c-1.473,0-2.67,1.233-2.67,2.75v1.197h-2.74V2.75c0-1.517-1.197-2.75-2.67-2.75
            c-1.473,0-2.67,1.233-2.67,2.75v1.197H6.224c-2.343,0-4.25,1.907-4.25,4.25v2h2.5h27.5h2.5v-2
            C34.474,5.855,32.568,3.948,30.224,3.948z M11.466,7.646c0,0.689-0.525,1.25-1.17,1.25s-1.17-0.561-1.17-1.25V2.75
            c0-0.689,0.525-1.25,1.17-1.25s1.17,0.561,1.17,1.25V7.646z M19.546,7.646c0,0.689-0.525,1.25-1.17,1.25s-1.17-0.561-1.17-1.25
            V2.75c0-0.689,0.525-1.25,1.17-1.25s1.17,0.561,1.17,1.25V7.646z M27.626,7.646c0,0.689-0.525,1.25-1.17,1.25
            c-0.646,0-1.17-0.561-1.17-1.25V2.75c0-0.689,0.524-1.25,1.17-1.25c0.645,0,1.17,0.561,1.17,1.25V7.646z"
            />
          </g>
        </g>
      </svg>
    `;
  }

  _desktopFooterNav(isLastCalender) {
    if (isLastCalender) {
      return html`<div class="fwc-calender-desktop-footer">
        <div>${this._buildCancelButton()}</div>
        <div>${this._buildApplyButton()}</div>
      </div>`;
    }
    return null;
  }
  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/buttons-links.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/components/fwc-input.css" />
      <mobile-view mobile-header-text="${this.inputLabel}" ?open=${this._isCalendarOpen} config=${JSON.stringify(this.config)}>
        <!-- mobile-view is a shared 'modal' overlay. Body slot is for both desktop and mobile. -->
        <div slot="body">
          <div class="fwc-date-picker-wrapper">
            <div class="fwc-input ${this._isCalendarOpen ? "open" : ""}">
              <label class="fwc-label" for="SelectedDateInput">${this.inputLabel}</label>
              <div class="fwc-date-picker-input">
                <input type="text" id="SelectedDateInput" class="fwc-selected-date" aria-haspopup="dialog" aria-describedby="date_error" @blur="${(e) => this._parseDate(e)}" @focus="${(e) => this._emitFocusEvent(e)}" aria-invalid=${this.invalid} />
                <button class="fwc-icon-calendar ${this.invalid ? "invalid" : ""}" @click="${this.toggleCalendarOpen}" aria-label="${this.labelAriaCalendarIcon}">${this._calendarIcon()}</button>
              </div>
            </div>
            <div class="fwc-calendar ${this._isCalendarOpen ? "open" : ""}">
              ${this._calendarTableRows.map((month, i, arr) => {
                return html`
                  ${i === 0
                    ? html`
                        <div class="fwc-calendar-mobile-nav">
                          <span>${this._buttonPrev()}</span>
                          <span>${this._calendarMonths[i][0].getFullYear()}</span>
                          <span>${this._buttonNext()}</span>
                        </div>
                      `
                    : ""}
                  <div class="fwc-calendar-month">
                    <div class="fwc-calendar-header">
                      ${i === 0 ? this._buttonPrev() : ""}
                      <span class="fwc-month-year" id="${this.monthNames[this._calendarMonths[i][0].getMonth()]}${this._calendarMonths[i][0].getFullYear()}"> ${this._calendarMonths ? this._getMonthAndYear(this._calendarMonths[i][0]) : ""} </span>
                      ${i === this._calendarTableRows.length - 1 ? this._buttonNext() : ""}
                    </div>
                    <div class="fwc-calendar-body">
                      <table role="table" width="100%" aria-labelledby="${this.monthNames[this._calendarMonths[i][0].getMonth()]}${this._calendarMonths[i][0].getFullYear()}">
                        ${this._buildTableHeaders()}
                        ${month.map((row) => {
                          return html`
                            <tr role="row">
                              ${row.map(
                                (day) => html`
                                  <td>
                                    <button
                                      tabindex="${day ? 4 : -1}"
                                      ?disabled="${day ? false : true}"
                                      aria-hidden="${day ? false : true}"
                                      aria-pressed="${day === this.selectedDate}"
                                      aria-label="${day ? `${this._formatDate(day)}` : ""}"
                                      data-date="${day ? this._formatDate(day) : ""}"
                                      @click="${(e) => {
                                        this._handleSelectedDate(day, e);
                                      }}"
                                      @keydown="${(e) => {
                                        this._handleSelectedDate(day, e);
                                      }}"
                                    >
                                      <span>${day ? day.getDate() : ""}</span>
                                    </button>
                                  </td>
                                `
                              )}
                            </tr>
                          `;
                        })}
                      </table>
                    </div>
                    ${this._desktopFooterNav(i === arr.length - 1)}
                  </div>
                `;
              })}
            </div>
          </div>
        </div>

        <!-- Mobile specific footer to allow for fixed design/position in mobile-view -->
        <div class="mobile-view-footer-slot" slot="mobile-actions">${this._buildCancelButton()} ${this._buildApplyButton()}</div>
      </mobile-view>
    `;
  }
}

class FwcDatePicker extends FwcBase {
  constructor() {
    super("fwc-date-picker", FwcDatePickerComponent);
  }
}

class FwcPreloaderComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
     @keyframes slideDown {
          0% {
            top: 0%;
            bottom: 100%;
            opacity: 0;
          }

          5% {
            top: 0%;
            bottom: 0%;
          }

          12% {
            opacity: 1;
          }

          50% {
            top: 0%;
            bottom: 0%;
            opacity: 1;
          }

          55% {
            top: 0%;
            bottom: 100%;
          }

          62% {
            opacity: 0;
          }

          100% {
            top: 0%;
            bottom: 100%;
            opacity: 0%;
          }
        }

        @keyframes slideUp {
          0% {
            top: 100%;
            bottom: 0%;
            opacity: 0;
          }

          5% {
            top: 0%;
            bottom: 0%;
          }

          12% {
            opacity: 1;
          }

          50% {
            top: 0%;
            bottom: 0%;
          }

          55% {
            top: 100%;
            bottom: 0%;
            opacity: 1;
          }

          62% {
            opacity: 0;
          }

          100% {
            top: 100%;
            bottom: 0%;
          }
        }
        .fwc-preloader {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
        }
        .fwc-preloader .fwc-preloader-heart {
          display: flex;
          padding: calc(1.5*var(--fwcu));
          height: calc(15*var(--fwcu));
        }
        .fwc-preloader .fwc-preloader-heart span {
          position: relative;
          background: var(--color-gray-five, #eee);
          width: var(--fwcu);
          display: flex;
          margin: calc(0.5*var(--fwcu));
          border-radius: calc(0.5*var(--fwcu));
          height: calc(10*var(--fwcu));
          top: calc(3*var(--fwcu));
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(even)::before {
          animation: slideUp 5s ease-out infinite;
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(odd)::before {
          animation: slideDown 5s ease-out infinite;
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(2)::before {
          animation-delay: 0.286s;
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(3)::before {
          animation-delay: 0.572s;
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(4)::before {
          animation-delay: 0.858s;
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(5)::before {
          animation-delay: 1.144s;
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(6)::before {
          animation-delay: 1.43s;
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(7)::before {
          animation-delay: 1.716s;
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(1), .fwc-preloader .fwc-preloader-heart span:nth-child(7) {
          height: calc(3.75*var(--fwcu));
          top: calc(2.5*var(--fwcu));
        }
        .fwc-preloader .fwc-preloader-heart span:nth-child(2), .fwc-preloader .fwc-preloader-heart span:nth-child(6) {
          height: calc(7.5*var(--fwcu));
          top: calc(1.25*var(--fwcu));
        }
      }
      .fwc-preloader {
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .fwc-preloader .fwc-preloader-heart {
        display: flex;
        padding: calc(1.5*var(--fwcu));
        height: calc(15*var(--fwcu));
      }
      .fwc-preloader .fwc-preloader-heart span {
        position: relative;
        background: var(--color-gray-five, #eee);
        width: var(--fwcu);
        display: flex;
        margin: calc(0.5*var(--fwcu));
        border-radius: calc(0.5*var(--fwcu));
        height: calc(10*var(--fwcu));
        top: calc(3*var(--fwcu));
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(even)::before {
        animation: slideUp 5s ease-out infinite;
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(odd)::before {
        animation: slideDown 5s ease-out infinite;
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(2)::before {
        animation-delay: 0.286s;
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(3)::before {
        animation-delay: 0.572s;
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(4)::before {
        animation-delay: 0.858s;
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(5)::before {
        animation-delay: 1.144s;
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(6)::before {
        animation-delay: 1.43s;
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(7)::before {
        animation-delay: 1.716s;
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(1),
      .fwc-preloader .fwc-preloader-heart span:nth-child(7) {
        height: calc(3.75*var(--fwcu));
        top: calc(2.5*var(--fwcu));
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(2),
      .fwc-preloader .fwc-preloader-heart span:nth-child(6) {
        height:calc(7.5*var(--fwcu));
        top: calc(1.25*var(--fwcu));
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(3),
      .fwc-preloader .fwc-preloader-heart span:nth-child(5) {
        height: calc(10*var(--fwcu));
        top: calc(1.25*var(--fwcu));
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(odd)::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        background: linear-gradient(0deg, var(--color-primary), var(--color-primary-hover));
        border-radius: calc(0.5*var(--fwcu));
        top: 0%;
      }
      .fwc-preloader .fwc-preloader-heart span:nth-child(even)::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        background: linear-gradient(180deg, var(--color-primary), var(--color-primary-hover));
        border-radius: calc(0.5*var(--fwcu));
        bottom: 0%;
      }
      .fwc-preloader-text {
        font-size: 24px;
        color: #003359;
        text-align: center;
        margin-top: 16px;
      }
      .fwc-preloader-text .dot {
        animation: dotFade 1.4s ease-in-out infinite;
      }
      .fwc-preloader-text .dot:nth-child(1) {
        animation-delay: 0s;
      }
      .fwc-preloader-text .dot:nth-child(2) {
        animation-delay: 0.2s;
      }
      .fwc-preloader-text .dot:nth-child(3) {
        animation-delay: 0.4s;
      }
      @keyframes dotFade {
        0%, 60%, 100% { opacity: 0.3; }
        30% { opacity: 1; }
      }
      `,
    ];
  }
  static get properties() {
    return {
      labelAriaValueText: { attribute: "label-aria-value-text", type: String },
    };
  }

  constructor() {
    super();
    this.labelAriaValueText = "Loading";
  }

  render() {
    return html`
          <div class="fwc-preloader">
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width="80" height="80">
  <defs>
    <style>
      @keyframes eds-rotate {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes eds-stroke-1 {
        0% { stroke-dashoffset: 99%; }
        18.5% { stroke-dashoffset: 0; }
        83.6% { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: 0; }
      }
      @keyframes eds-stroke-2 {
        0% { stroke-dashoffset: 100%; }
        4.6% { stroke-dashoffset: 100%; }
        10.1% { stroke-dashoffset: 80%; }
        18.2% { stroke-dashoffset: 80%; }
        34.6% { stroke-dashoffset: 0; }
        67.3% { stroke-dashoffset: 0; }
        83.6% { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: 0; }
      }
      @keyframes eds-stroke-3 {
        0% { stroke-dashoffset: 100%; }
        5.1% { stroke-dashoffset: 90%; }
        15.5% { stroke-dashoffset: 90%; }
        50.9% { stroke-dashoffset: 10%; }
        67.3% { stroke-dashoffset: 10%; }
        83.6% { stroke-dashoffset: 1%; }
        100% { stroke-dashoffset: 1%; }
      }
      .eds-spinner-group {
        transform-origin: 60px 60px;
        animation: eds-rotate 9s linear infinite;
      }
      .eds-spinner-stroke {
        stroke-linecap: round;
        fill: none;
      }
      .eds-stroke-1 {
        stroke: rgb(68, 184, 243);
        animation: eds-stroke-1 9s ease-in-out infinite;
      }
      .eds-stroke-2 {
        stroke: rgb(40, 108, 226);
        animation: eds-stroke-2 9s ease-in-out infinite;
      }
      .eds-stroke-3 {
        stroke: rgb(0, 51, 89);
        animation: eds-stroke-3 9s ease-in-out infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        .eds-spinner-group {
          animation: none;
        }
        .eds-spinner-stroke {
          animation: none !important;
          stroke-dashoffset: 0 !important;
        }
      }
    </style>
  </defs>
  <g class="eds-spinner-group">
    <circle class="eds-spinner-stroke eds-stroke-1" cx="60" cy="60" r="52" stroke-width="8" pathLength="100" stroke-dasharray="100" stroke-dashoffset="99"/>
    <circle class="eds-spinner-stroke eds-stroke-2" cx="60" cy="60" r="52" stroke-width="8" pathLength="100" stroke-dasharray="100" stroke-dashoffset="100"/>
    <circle class="eds-spinner-stroke eds-stroke-3" cx="60" cy="60" r="52" stroke-width="8" pathLength="100" stroke-dasharray="100" stroke-dashoffset="100"/>
  </g>
</svg>
<div class="fwc-preloader-text">Please Wait<span class="dot">.</span><span class="dot">.</span><span class="dot">.</span></div>
      </div>
    `;
  }
}

class FwcPreloader extends FwcBase {
  constructor() {
    super("fwc-preloader", FwcPreloaderComponent);
  }
}

class FwcDateTimePickerComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .date-time-picker-container {
          width: 100%;
        }
        .week-btns {
          display: flex;
          justify-content: space-between;
          border-bottom: 1px solid var(--color-gray-four);
          padding-bottom: calc(0.5 * var(--fwcu));
          margin-bottom: var(--fwcu);
        }
        .week-btns button {
          display: flex;
          align-items: center;
          font-weight: 700;
          font-size: calc(2 * var(--fwcu));
          color: var(--color-primary);
          border: none;
          background: transparent;
          padding: var(--fwcu) 0;
          line-height: calc(2 * var(--fwcu));
          transition: background-color 250ms ease, color 250ms ease;
          cursor: pointer;
        }
        .week-btns button[disabled],
        .week-btns button[disabled]:hover {
          border-color: var(--color-gray-five);
          color: var(--color-gray-three);
          pointer-events: none;
        }
        .week-btns button[disabled] svg,
        .week-btns button[disabled]:hover svg {
          fill: var(--color-gray-three);
        }
        .week-btns svg {
          display: inline-block;
          width: calc(2 * var(--fwcu));
          height: calc(2 * var(--fwcu));
          fill: var(--color-primary);
        }
        /* TODO Needs to be updated to use overlay */
        .week-btns button:hover,
        .week-btns button:hover svg {
          color: var(--color-primary-hover);
          fill: var(--color-primary-hover);
        }
        .week-btns button:focus {
          outline-offset: 4px;
        }
        .prev svg {
          transform: rotate(180deg);
          margin-right: var(--fwcu);
        }
        .next svg {
          margin-left: var(--fwcu);
        }
        .dates {
          display: flex;
          justify-content: space-between;
        }
        .dates button {
          text-align: center;
          border: 1px solid var(--color-primary);
          font-size: calc(1.5 * var(--fwcu));
          padding: calc(0.5 * var(--fwcu));
          line-height: calc(1.5 * var(--fwcu));
          width: 100%;
          appearance: none;
          background: var(--color-white);
          min-height: calc(3.75 * var(--fwcu));
          border-radius: var(--border-radius);
          color: var(--color-primary);
          transition: background 250ms ease-in-out;
          font-weight: 700;
          cursor: pointer;
        }
        .dates button:focus {
          /* TODO: change if necessary */
          outline: none;
          box-shadow: 0 0 0 1.5px var(--color-primary);
        }
        .dates button:hover {
          color: var(--color-white);
          background-image: linear-gradient(180deg, rgba(var(--color-rgb-black), 0), rgba(var(--color-rgb-black), 0.2)), linear-gradient(180deg, var(--color-primary), var(--color-primary));
          border-color: var(--color-primary);
        }
        .date-col {
          width: calc(14 * var(--fwcu));
          margin-right: calc(2 * var(--fwcu));
        }
        .date-col:last-child {
          margin-right: 0;
        }
        h6 {
          text-align: center;
          margin: calc(2.5 * var(--fwcu)) 0;
          color: var(--color-gray-one);
        }
        h6 span {
          display: block;
          font-weight: 700;
          margin-bottom: calc(0.5 * var(--fwcu));
        }
        ul {
          list-style-type: none;
          padding: 0;
          margin: 0;
        }
        li {
          margin-bottom: calc(2 * var(--fwcu));
          width: 100%;
        }
        li:hover button {
          background-color: var(--color-primary);
          color: var(--color-white);
        }
        li.selected button {
          background-color: var(--color-primary);
          color: var(--color-white);
        }
        li.empty-time {
          text-align: center;
          color: var(--color-gray-three);
          height: calc(3.75 * var(--fwcu));
          margin-bottom: calc(2 * var(--fwcu));
          display: flex;
          align-items: center;
          justify-content: center;
        }
        li span {
          display: block;
          height: calc(2 * var(--fwcu));
          line-height: calc(2 * var(--fwcu));
        }
        .no-results {
          text-align: center;
          font-size: calc(2.25 * var(--fwcu));
          font-weight: 700;
          font-family: var(--font-serif);
          color: var(--color-gray-one);
        }
        .hide {
          display: none;
        }
        .week-btns .hide {
          opacity: 0;
          visibility: hidden;
          cursor: none;
        }
        @media only screen and (max-width: 900px) {
          .week-btns {
            border-bottom: none;
          }
          .dates {
            flex-direction: column;
            width: 100%;
          }
          .date-col {
            width: 100%;
          }
          ul {
            display: flex;
            flex-wrap: wrap;
            margin-bottom: calc(2.5 * var(--fwcu));
          }
          li {
            width: calc(50% - var(--fwcu));
            margin-bottom: calc(1.5 * var(--fwcu));
          }
          li:nth-child(odd) {
            margin-right: calc(1.5 * var(--fwcu));
          }
          li:hover button,
          .dates button:hover {
            outline: none;
            box-shadow: var(--box-shadow);
            background-image: none;
            color: var(--color-primary);
            background-color: var(--color-white);
          }
          li.selected:hover button {
            background-color: var(--color-primary);
            color: var(--color-white);
            background-image: linear-gradient(180deg, rgba(var(--color-rgb-black), 0), rgba(var(--color-rgb-black), 0.2)), linear-gradient(180deg, var(--color-primary), var(--color-primary));
          }
          h6 {
            text-align: left;
            margin-bottom: calc(2 * var(--fwcu));
          }
          li.empty-time {
            display: none;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      schedule: { type: Array },
      labelNextWeek: { attribute: "label-next-week", type: String },
      labelPrevWeek: { attribute: "label-prev-week", type: String },
      labelDays: { attribute: "label-days", type: Array },
      labelMonths: { attribute: "label-months", type: Array },
      labelNoResults: { attribute: "label-no-results", type: String },
      hidePreviousButton: { attribute: "hide-previous-button", type: Boolean },
      hideNextButton: { attribute: "hide-next-button", type: Boolean },
      pressedButton: {},
    };
  }

  set schedule(value) {
    let oldVal = this._schedule;
    this._schedule = !value ? [] : value;
    this._init();
    this.requestUpdate("schedule", oldVal);
  }

  get schedule() {
    return this._schedule;
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this._schedule = [];
    this.labelNextWeek = "Next Week";
    this.labelPrevWeek = "Previous Week";
    this.labelNoResults = "No Results";
    this.hidePreviousButton = false;
    this.hideNextButton = false;
    this.labelDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    this.labelMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    this.pressedButton = null;
    this.maxNumberTimeSlots = 0;
    this.initialLoad = true;
    this.fwcPreloaderCmp = new FwcPreloader();
    this.fwcPreloaderCmp.build();
    this._showPreloader = true;
    this._disableNextWeekBtn = false;
    this._disablePrevWeekBtn = false;
  }

  firstUpdated() {
    this._init();
    this.initialLoad = false;
    this._showPreloader = false;
  }

  showPreloader() {
    this._showPreloader = true;
    this._disablePrevWeekBtn = this._disableNextWeekBtn = true;
    this.requestUpdate();
  }

  hidePreloader() {
    this._showPreloader = false;
    this._disablePrevWeekBtn = this._disableNextWeekBtn = false;
    this.requestUpdate();
  }

  updated() {
    this._init();
  }

  _init() {
    this.maxNumberTimeSlots = this._getMaxNumberTimeSlots();
  }

  _getMaxNumberTimeSlots() {
    return this._schedule.reduce((tot, cur) => {
      if (cur.slots.length > tot) {
        return cur.slots.length;
      }
      return tot;
    }, 0);
  }

  _emitWeekChange(dir) {
    const event = new CustomEvent("fwc-date-time-picker-week-change", {
      detail: { week: dir, data: this._schedule },
      bubbles: true,
    });
    this.showPreloader();

    /** TODO: Replace setTimeout with async callback to simulate actual loading */
    setTimeout(() => {
      this.hidePreloader();
    }, 1000);

    this.dispatchEvent(event);
  }

  _emitSelectedDateTime(date) {
    const event = new CustomEvent("fwc-date-time-picker-selected", {
      detail: date,
      bubbles: true,
    });

    this.dispatchEvent(event);
  }

  _selectedTime(time) {
    const start = new Date(time.start);
    const end = new Date(time.end);

    if (this.pressedButton === start.getTime()) {
      this.pressedButton = null;
      this._emitSelectedDateTime({ start: null, end: null });
    } else {
      this.pressedButton = start.getTime();
      this._emitSelectedDateTime({ start: start, end: end });
    }
  }

  _getDayName(date) {
    const dt = new Date(date);
    return this.labelDays[dt.getDay()];
  }

  _getMonthName(date) {
    const dt = new Date(date);
    return this.labelMonths[dt.getMonth()];
  }

  _getTime(date, periods = false) {
    let hours = new Date(date).getHours();
    let minutes = new Date(date).getMinutes();
    const ampm = periods ? (hours >= 12 ? " pm" : " am") : "";
    hours = hours % 12;
    hours = hours ? hours : 12;
    minutes = minutes < 10 ? "0" + minutes : minutes;
    return `${hours}:${minutes}${ampm}`;
  }

  _getSvgArrow() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" focusable="false">
        <polygon points="14.067 50 11 46.933 32.933 25 11 3.067 14.067 0 37.533 23.467 39 25 37.533 26.533" />
      </svg>
    `;
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/buttons-links.css" />
      <div class="date-time-picker-container">
        <div class="week-btns">
          <button
            class="prev ${this.hidePreviousButton ? "hide" : ""}"
            aria-label="${this.labelPrevWeek}"
            aria-hidden="${this.hidePreviousButton}"
            ?disabled=${this._disablePrevWeekBtn}
            @click=${() => {
              this._emitWeekChange("prev");
            }}
          >
            ${this._getSvgArrow()} ${this.labelPrevWeek}
          </button>
          <button
            class="next  ${this.hideNextButton ? "hide" : ""}"
            aria-label="${this.labelNextWeek}"
            aria-hidden="${this.hideNextButton}"
            ?disabled=${this._disableNextWeekBtn}
            @click=${() => {
              this._emitWeekChange("next");
            }}
          >
            ${this.labelNextWeek} ${this._getSvgArrow()}
          </button>
        </div>
        ${this._showPreloader ? html`<fwc-preloader></fwc-preloader>` : ""}
        <div class="dates ${!this._schedule.length || this._showPreloader ? "hide" : ""}">
          ${this._schedule.map((item, i) => {
            return html`
              <div class="date-col">
                <h6 id="fwc_heading_${i}">
                  ${this._getDayName(item.date)}
                  <span>${this._getMonthName(item.date)} ${new Date(item.date).getDate()}</span>
                </h6>
                <ul aria-labelledby="fwc_heading_${i}">
                  ${item.slots.map((time) => {
                    return html`
                      <li class="${this.pressedButton === new Date(time.start).getTime() ? "selected" : ""}">
                        <button
                          @click="${() => {
                            this._selectedTime(time);
                          }}"
                          aria-label="${this._getTime(time.start, false)} - ${this._getTime(time.end, true)}"
                          aria-pressed="${this.pressedButton === new Date(time.start).getTime()}"
                        >
                          ${this._getTime(time.start, false)} - ${this._getTime(time.end, true)}
                        </button>
                      </li>
                    `;
                  })}
                  <!-- add the empty dashes -->
                  ${[...Array(this.maxNumberTimeSlots - item.slots.length)].map((num, i) => {
                    return html`
                      <li class="empty-time" aria-hidden="true">
                        <span>&mdash;</span>
                      </li>
                    `;
                  })}
                </ul>
              </div>
            `;
          })}
        </div>
        <div class="no-results ${!this._schedule.length && !this.initialLoad ? "" : "hide"} ${this._showPreloader ? "hide" : ""}">
          <svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 150 150">
            <g fill="#D8D8D8" transform="translate(5)">
              <path d="M18.3054585 68.8356164L40.2561854 68.8356164C41.8551069 68.8356164 43.1506849 67.4557192 43.1506849 65.7534247 43.1506849 64.0520548 41.8551069 62.6712329 40.2561854 62.6712329L18.3054585 62.6712329C16.7068264 62.6712329 15.4109589 64.0520548 15.4109589 65.7534247 15.4109589 67.4557192 16.7068264 68.8356164 18.3054585 68.8356164zM18.317628 83.2191781L42.2988104 83.2191781C43.9032917 83.2191781 45.2054795 81.8380479 45.2054795 80.1369863 45.2054795 78.4343836 43.9032917 77.0547945 42.2988104 77.0547945L18.317628 77.0547945C16.7122747 77.0547945 15.4109589 78.4343836 15.4109589 80.1369863 15.4109589 81.8380479 16.7122747 83.2191781 18.317628 83.2191781zM54.4520548 83.2191781C55.262363 83.2191781 56.0581849 82.892774 56.6311644 82.3160959 57.2044521 81.7431164 57.5342466 80.9506849 57.5342466 80.1369863 57.5342466 79.3266781 57.2044521 78.5308562 56.6311644 77.9575685 56.0581849 77.384589 55.262363 77.0547945 54.4520548 77.0547945 53.6417466 77.0547945 52.8459247 77.384589 52.2729452 77.9575685 51.6962671 78.5308562 51.369863 79.3266781 51.369863 80.1369863 51.369863 80.9506849 51.6962671 81.7431164 52.2729452 82.3160959 52.8459247 82.892774 53.6380479 83.2191781 54.4520548 83.2191781zM81.3604541 63.8695312C95.5607481 63.8695312 107.112771 52.3040039 107.112771 38.0871093 107.112771 23.8710937 95.5607481 12.3046875 81.3604541 12.3046875 67.1613305 12.3046875 55.6081373 23.8710937 55.6081373 38.0871093 55.6081373 52.3040039 67.1613305 63.8695312 81.3604541 63.8695312zM81.3604541 18.1652343C92.3327835 18.1652343 101.260238 27.1019532 101.260238 38.0871093 101.260238 49.0722657 92.3327835 58.0101562 81.3604541 58.0101562 70.3881248 58.0101562 61.461841 49.0722657 61.461841 38.0871093 61.461841 27.1019532 70.3881248 18.1652343 81.3604541 18.1652343z" />
              <path d="M137.566428,80.3100586 L134.028864,76.7680664 L134.028864,36.909375 C134.028864,28.8322266 127.464077,22.2597657 119.39636,22.2597657 L115.960046,22.2597657 C109.946567,9.13710937 96.700529,0 81.3604541,0 C66.0215498,0 52.7746334,9.13710937 46.7620333,22.2597657 L14.6325036,22.2597657 C6.56361604,22.2597657 0,28.8322266 0,36.909375 L0,110.739843 C0,118.818164 6.56361604,125.389453 14.6325036,125.389453 L52.3722717,125.389453 L52.3722717,132.427734 C44.4347735,132.580957 38.0253716,139.088086 38.0253716,147.070313 C38.0253716,148.688378 39.3354612,150 40.9516383,150 L93.0552787,150 C94.6714558,150 95.9815453,148.688378 95.9815453,147.070313 C95.9815453,139.088086 89.5724361,132.582128 81.6361084,132.427734 L81.6361084,125.389453 L119.39636,125.389453 C127.465248,125.389453 134.028864,118.818164 134.028864,110.739843 L134.028864,96.6418945 C135.341002,96.1508789 136.545746,95.3818359 137.566428,94.3599609 C141.43466,90.4860351 141.43466,84.1839843 137.566428,80.3100586 Z M119.39636,28.1203125 C124.237283,28.1203125 128.17633,32.062793 128.17633,36.909375 L128.17633,70.9086914 L114.340357,57.0568359 C117.560421,51.46875 119.404262,44.9900391 119.404262,38.0871093 C119.404262,34.6400391 118.941326,31.2984375 118.079541,28.1203125 L119.39636,28.1203125 Z M81.3604541,5.859375 C99.110017,5.859375 113.550558,20.3167968 113.550558,38.0871093 C113.550558,55.8574218 99.110017,70.3148438 81.3604541,70.3148438 C63.6108912,70.3148438 49.1703508,55.8574218 49.1703508,38.0871093 C49.1703508,20.3167968 63.6108912,5.859375 81.3604541,5.859375 Z M14.6325036,28.1203125 L44.642831,28.1203125 C43.7807528,31.2984375 43.3178175,34.6400391 43.3178175,38.0871093 C43.3178175,41.8040039 43.85508,45.3952149 44.8508886,48.7942383 L17.8970467,48.7942383 C16.2808696,48.7942383 14.9707801,50.1055664 14.9707801,51.7239258 C14.9707801,53.3408203 16.2808696,54.6536133 17.8970467,54.6536133 L47.1082107,54.6536133 C53.274147,67.3792968 66.3106649,76.1753907 81.3604541,76.1753907 C88.2544457,76.1753907 94.7252991,74.3293945 100.30686,71.1067383 L123.53293,94.3599609 C124.833949,95.662207 126.432861,96.5572266 128.17633,96.984082 L128.17633,99.6070313 L5.85253325,99.6070313 L5.85253325,36.909375 C5.85253325,32.062793 9.79158075,28.1203125 14.6325036,28.1203125 Z M89.6283278,144.140625 L44.3797597,144.140625 C45.5868446,140.729003 48.8423163,138.280078 52.6578753,138.280078 L81.3490417,138.280078 C85.1646007,138.280078 88.4200723,140.729003 89.6283278,144.140625 Z M58.224805,132.420703 L75.7835751,132.420703 L75.7835751,125.389453 L58.224805,125.389453 L58.224805,132.420703 Z M119.39636,119.530078 L14.6325036,119.530078 C9.79158075,119.530078 5.85253325,115.586426 5.85253325,110.739843 L5.85253325,105.466407 L128.17633,105.466407 L128.17633,110.739843 C128.17633,115.586426 124.237283,119.530078 119.39636,119.530078 Z M133.427516,90.2162109 C131.840894,91.8055664 129.257586,91.8043945 127.670964,90.2162109 L105.215379,67.7329101 C107.332241,66.0231445 109.262992,64.0904297 110.970761,61.9708008 L133.427516,84.4541016 C135.014138,86.0422851 135.014138,88.6277343 133.427516,90.2162109 Z" />
              <path d="M81.0709838,30.6355531 C85.1910336,30.6355531 88.5429639,33.9877271 88.5429639,38.106881 C88.5429639,39.7577109 89.8810461,41.0958904 91.5317559,41.0958904 C93.1827646,41.0958904 94.5205479,39.7577109 94.5205479,38.106881 C94.5205479,30.6902519 88.4870735,24.6575342 81.0709838,24.6575342 C79.420274,24.6575342 78.0821918,25.9966104 78.0821918,27.6465437 C78.0821918,29.2973736 79.420274,30.6355531 81.0709838,30.6355531 Z" />
            </g>
          </svg>
          <div>${this.labelNoResults}</div>
        </div>
      </div>
    `;
  }
}

class FwcDateTimePicker extends FwcBase {
  constructor() {
    super("fwc-date-time-picker", FwcDateTimePickerComponent);
  }
}

// Helper class to allow transitions from display:none/block
// Used by: FwcExpandMultiItem

class ExpandCollapse {
  constructor(content) {
    this.content = content;
    this.drawer = content.firstElementChild;
    this.isExpanded = false;

    // when the panel closes
    this.content.addEventListener("transitionend", () => {
      if (!this.isExpanded) {
        // hides content from tab order when closed.
        this.drawer.style.display = "none";
      }
    });
  }

  _setPanelStyles(lock) {
    const element = this.content;
    this.drawer.style.display = "block";
    // have the element transition to the height of its inner content
    element.style.height = lock ? "auto" : `${element.scrollHeight}px`;
    if (!lock) {
      element.addEventListener("transitionend", function handler() {
        element.style.height = "auto";
        element.removeEventListener("transitionend", handler);
      });
    }
  }

  expandPanel(lock) {
    this._setPanelStyles(lock);
    this.isExpanded = true;
  }

  collapsePanel() {
    const sectionHeight = this.content.scrollHeight;

    // temporarily disable all css transitions
    const elementTransition = this.content.style.transition;
    this.content.style.transition = "";
    this.isExpanded = false;

    // on the next frame (as soon as the previous style change has taken effect),
    // explicitly set the element's height to its current pixel height, so we
    // aren't transitioning out of 'auto'
    requestAnimationFrame(() => {
      this.content.style.height = `${sectionHeight}px`;
      this.content.style.transition = elementTransition;

      // on the next frame (as soon as the previous style change has taken effect),
      requestAnimationFrame(() => {
        this.content.style.height = `${0}px`;
      });
    });
  }
}

class FwcExpandComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-expand-wrapper {
          border-top: 1px solid var(--color-gray-four);
        }
        .fwc-expand-last {
          border-bottom: 1px solid var(--color-gray-four);
        }
        .fwc-expand-header {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          padding: calc(4 * var(--fwcu)) calc(4 * var(--fwcu)) calc(4 * var(--fwcu)) calc(1.5 * var(--fwcu));
        }
        .fwc-expand-label {
          color: var(--color-gray-one);
          line-height: calc(3 * var(--fwcu));
        }
        .fwc-expand-button {
          padding: 0;
          border: none;
          background: none;
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0;
          margin: 0 calc(1.75 * var(--fwcu));
          border-radius: 0;
        }
        .fwc-expand-button:hover {
          cursor: pointer;
        }
        .fwc-expand-icon {
          fill: var(--color-primary);
          height: calc(3 * var(--fwcu));
          width: calc(3 * var(--fwcu));
        }
        .fwc-expand-icon.expanded {
          transform: rotateX(180deg);
        }
        .fwc-expand-content {
          overflow: hidden;
          transition: height 0.3s ease;
          padding-right: calc(5 * var(--fwcu));
          padding-left: calc(8 * var(--fwcu));
          height: 0;
        }
        .fwc-expand-drawer {
          display: none;
          padding-bottom: calc(4 * var(--fwcu));
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      expanded: { type: Boolean },
      labelAriaExpandBtn: { attribute: "label-aria-expand-btn", type: String },
      label: { type: String },
      last: { type: Boolean },
      noBorder: { attribute: "no-border", type: Boolean }
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this.expanded = false;
    this.last = false;
  }

  firstUpdated() {
    const content = this.shadowRoot.querySelector(".fwc-expand-content");
    this.expandCollapse = new ExpandCollapse(content);
    if (this.expanded) {
      this._expandPanel(true);
    }
  }

  _collapsePanel() {
    this.expandCollapse.collapsePanel();
  }

  _expandPanel(lock) {
    this.expandCollapse.expandPanel(lock);
  }

  expand() {
    this._expand();
  }
  // use to toggle outside of component
  _expand() {
    if (this.expanded) {
      this._collapsePanel();
    } else {
      this._expandPanel();
    }
    this.expanded = !this.expanded;
    const event = new CustomEvent("fwc-expand-expanded", {
      detail: this.expanded,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  _buildLabel() {
    const label = this.label ? html`<span class="fwc-h4 fwc-expand-label">${this.label}</span>` : html`<slot name="fwc-expand-title"></slot>`;
    return label;
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />
      <div class="${this.noBorder ? "" : `fwc-expand-wrapper ${this.last ? "fwc-expand-last" : ""}`}">
        <div class="fwc-expand-header">
          <button id="expandBtn_${this.id}" class="fwc-expand-button" type="button" role="button" 
            aria-controls="panel_${this.id}" aria-label="${this.labelAriaExpandBtn ? this.labelAriaExpandBtn : this.label}" aria-expanded="${this.expanded}" @click=${this._expand}>
            <svg xmlns="http://www.w3.org/2000/svg" width="51" height="50" viewBox="0 0 50 50" class="fwc-expand-icon ${this.expanded ? "expanded" : ""}" focusable="false">
              <polygon points="2.719 39.66 0 36.941 23.641 13.3 25 12 26.359 13.3 50 36.941 47.281 39.66 25 17.378" transform="matrix(1 0 0 -1 0 51.66)" />
            </svg>
          </button>
          ${this._buildLabel()}
        </div>
        <div class="fwc-expand-content ${this.expanded ? "expanded" : ""}">
          <div role="region" class="fwc-expand-drawer" id="panel_${this.id}" aria-labelledby="expandBtn_${this.id}">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}

class FwcExpand extends FwcBase {
  constructor() {
    super("fwc-expand", FwcExpandComponent);
  }
}

class FwcExpandMultiComponent extends LitElement {
  static get styles() {
    return [sharedStyles, css``];
  }

  constructor() {
    super();
    this.slotItems = null;
    this.slotItemActiveIndex = null;
    // used for setting an index on <fwc-expand-multi-item>
    this.childIndexTracker = 0;
  }

  connectedCallback() {
    // dispatch an event when the component has connected
    super.connectedCallback();
    const componentName = this.tagName.toLowerCase();
    const event = new CustomEvent(`${componentName}-connected`, {
      detail: this,
      bubbles: true,
    });

    this.dispatchEvent(event);
  }

  firstUpdated() {
    // get slotted children
    this.slotItems = this._slottedChildren();
    if (this.slotItemActiveIndex) {
      this._expandSlotByActiveIndex();
    }
  }

  _expandSlotByActiveIndex() {
    // expand a child by index
    this.slotItems.forEach((item, index) => {
      if (this.slotItemActiveIndex === index) {
        item.expandPanel();
      } else {
        item.closePanel();
      }
    });
  }

  expandByIndex(index) {
    this.slotItemActiveIndex = index;

    if (this.slotItems) {
      this._expandSlotByActiveIndex();
    }
  }

  _slottedChildren() {
    const slot = this.shadowRoot.querySelector("slot");
    const childNodes = slot.assignedNodes({ flatten: true });
    return Array.prototype.filter.call(childNodes, (node) => node.nodeType === Node.ELEMENT_NODE);
  }

  render() {
    return html`
      <div class="fwc-expand-multi-wrapper">
        <slot></slot>
      </div>
    `;
  }
}

class FwcExpandMultiItemComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-expand-multi-item {
        }
        .fwc-expand-header {
          position: relative;
          padding: var(--fwcu);
          display: flex;
        }
        .fwc-expand-button {
          flex-basis: calc(3 * var(--fwcu));
          border: none;
          background: none;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 0;
        }
        .fwc-expand-icon {
          height: calc(2 * var(--fwcu));
          width: calc(2 * var(--fwcu));
        }
        .fwc-expand-icon.expanded {
          transform: rotateX(180deg);
        }
        .fwc-expand-content {
          overflow: hidden;
          transition: height 0.3s ease 0s;
          height: 0px;
        }
      `,
    ];
  }

  static get properties() {
    return {
      expanded: { type: Boolean },
      expandButtonAriaLabel: { attribute: "label-aria-expand-text", type: String },
    };
  }

  constructor() {
    super();
    this.expanded = false;
    this.index = 0;
  }

  firstUpdated() {
    const content = this.shadowRoot.querySelector(".fwc-expand-content");
    this.expandCollapse = new ExpandCollapse(content);

    // set the stored index from the parent and then increment it for next instance
    this.index = this.parentNode.childIndexTracker;
    this.parentNode.childIndexTracker += 1;

    // if the property is passed in
    if (this.expanded) {
      this.expandPanel(true);
    }
    this.parentNode.addEventListener("fwc-expand-multi-expanded", (e) => {
      // if the event is dispatched from a sibling component, close its panel
      if (e.target !== this) {
        this.closePanel();
        return;
      }

      // if the event is dispatched from itself, toggle the panel
      if (this.expanded) {
        this.closePanel();
      } else {
        this.expandPanel();
      }

      // stop the event from bubbling outside fwc-expand-multi
      e.cancelBubble = true;
    });
  }

  expandPanel(lock) {
    this.setAttribute("expanded", "true");
    this.expandCollapse.expandPanel(lock);
  }

  closePanel() {
    this.removeAttribute("expanded");
    this.expandCollapse.collapsePanel();
  }

  //TODO send index in details
  //Send on expandPanel
  _dispatchExpandEvent() {
    const event = new CustomEvent("fwc-expand-multi-expanded", {
      detail: !this.expanded,
      bubbles: true,
      composed: false,
    });

    this.dispatchEvent(event);
  }

  render() {
    return html`
      <div class="fwc-expand-multi-item">
        <div class="fwc-expand-header">
          <button class="fwc-expand-button" type="button" id="expand_${this.index}" aria-expanded="${this.expanded}" aria-controls="panel_${this.index}" aria-label="${this.expandButtonAriaLabel}" @click=${this._dispatchExpandEvent}>
            <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" class="fwc-expand-icon ${this.expanded ? "expanded" : ""}" focusable="false">
              <polygon points="2.719 39.66 0 36.941 23.641 13.3 25 12 26.359 13.3 50 36.941 47.281 39.66 25 17.378" transform="matrix(1 0 0 -1 0 51.66)" />
            </svg>
          </button>
          <slot name="expand-header"></slot>
        </div>
        <div class="fwc-expand-content">
          <div class="fwc-expand-drawer" role="region" id="panel_${this.index}" aria-labelledby="expand_${this.index}" style="display: none;">
            <slot name="expand-panel"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

class FwcExpandMulti extends FwcBase {
  constructor() {
    super("fwc-expand-multi", FwcExpandMultiComponent);
    const FwcExpandMultiItem = new FwcBase("fwc-expand-multi-item", FwcExpandMultiItemComponent);
    FwcExpandMultiItem.build();
  }
}

class FwcExpandMobileColComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          display: block;
          width: 100%;
          padding: calc(2 * var(--fwcu)) 0;
        }
        ul {
          list-style-type: none;
          margin: 0;
          padding: 0;
          background: transparent;
          overflow: hidden;
          transition: all 500ms ease;
        }
        .fwc-mobile-title {
          font-size: var(--default-font-size);
          color: var(--color-white);
          justify-content: space-between;
          display: none;
        }
        .fwc-expand-icon {
          height: calc(2.75 * var(--fwcu));
          width: calc(2.75 * var(--fwcu));
          background: var(--color-white);
          border-radius: 50%;
          position: relative;
          border: none;
          display: none;
        }
        .horizontal {
          display: flex;
          flex-direction: row;
        }
        .fwc-expand-icon:before {
          content: "";
          position: absolute;
          background: var(--color-black);
          height: calc(0.25 * var(--fwcu));
          width: calc(1.5 * var(--fwcu));
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          margin: auto;
        }
        .fwc-expand-icon:after {
          content: "";
          position: absolute;
          background: var(--color-black);
          height: calc(0.25 * var(--fwcu));
          width: calc(1.5 * var(--fwcu));
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          margin: auto;
          transform: rotate(90deg);
          opacity: 1;
          transition: opacity 500ms ease;
        }
        :host(.expanded) ul {
          margin-top: calc(2 * var(--fwcu));
        }
        :host(.expanded) .fwc-expand-icon:after {
          opacity: 0;
        }
        @media screen and (max-width: 900px) {
          .fwc-expand-icon {
            display: inline-block;
          }
          .fwc-mobile-title {
            display: flex;
          }
          .horizontal {
            display: flex;
            flex-direction: column;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      mobileTitle: { attribute: "mobile-title", type: String },
      horizontal: { attribute: "horizontal", type: Boolean },
    };
  }

  constructor() {
    super();
    this.mobileTitle = "";
    this.horizontal = false;
    this.isExpanded = false;
  }

  firstUpdated() {
    if (this.horizontal) {
      const slotItems = this._getSlotted();
      slotItems.forEach((slotItem, i) => {
        slotItem.setAttribute("horizontal", "");
      });
    }
  }

  _getSlotted() {
    const slot = this.shadowRoot.querySelector("slot");
    const childNodes = slot ? slot.assignedNodes({ flatten: true }) : [];
    return Array.prototype.filter.call(childNodes, (node) => node.nodeType == Node.ELEMENT_NODE && node.nodeName === "FWC-EXPAND-MOBILE-COL-ITEM");
  }

  _mobileExpandToggle(e) {
    const slotItems = this._getSlotted();
    slotItems.forEach((slotItem, i) => {
      if (slotItem.hasAttribute("expanded")) {
        slotItem.removeAttribute("expanded");
      } else {
        slotItem.setAttribute("expanded", "");
      }
    });

    this.classList.toggle("expanded");
    this.isExpanded = !this.isExpanded;
    // aria-expanded does not update without this?
    this.requestUpdate();
  }

  render() {
    return html`
      <div class="fwc-expand-mobile-col-wrapper">
        <div class="fwc-mobile-title">
          <span>${this.mobileTitle}</span>
          <button class="fwc-expand-icon" @click="${this._mobileExpandToggle}" aria-expanded="${this.isExpanded}" aria-label="${this.mobileTitle}"></button>
        </div>
        <ul>
          <slot class="${this.horizontal ? "horizontal" : ""}"></slot>
        </ul>
      </div>
    `;
  }
}

class FwcExpandMobileColItemComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          display: block;
          width: 100%;
        }
        ::slotted(*) {
          color: var(--color-white) !important;
        }
        span {
          color: var(--color-white);
          text-decoration: none;
          display: flex;
          align-items: center;
          position: absolute;
          top: 0;
          bottom: 0;
          width: 100%;
        }
        li {
          transition: all 0.5s cubic-bezier(0.36, 0, 0.44, 0.2);
          position: relative;
          overflow: hidden;
          padding-top: calc(3.5 * var(--fwcu));
          top: 0;
        }
        li.list-item {
          margin-bottom: calc(var(--fwcu));
        }
        li.horizontal {
          padding-top: calc(6.75 * var(--fwcu));
          margin: 0 calc(var(--fwcu));
        }
        @media screen and (max-width: 900px) {
          li,
          li.horizontal {
            padding-top: 0;
            top: calc(-12.25 * var(--fwcu));
          }
          li.expanded,
          li.expanded.horizontal {
            padding-top: calc(6.75 * var(--fwcu));
            top: 0;
          }
          span {
            border-top: 1px solid rgba(var(--color-rgb-gray-five), 0.2);
            padding-left: calc(2 * var(--fwcu));
          }
          li.list-item {
            visibility: hidden;
          }
          li.list-item.expanded {
            visibility: visible;
          }
          li.list-item {
            margin-bottom: 0;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      expanded: { type: Boolean },
      horizontal: { type: Boolean },
    };
  }

  constructor() {
    super();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);
    if (name === "expanded") {
      const liElem = this.shadowRoot.querySelector("li");
      if (this.expanded) {
        liElem.classList.add("expanded");
      } else {
        liElem.classList.remove("expanded");
      }
    }
  }

  render() {
    return html`
      <li class="list-item ${this.horizontal ? "horizontal" : ""}">
        <span>
          <slot></slot>
        </span>
      </li>
    `;
  }
}

class FwcExpandMobileCol extends FwcBase {
  constructor() {
    super("fwc-expand-mobile-col", FwcExpandMobileColComponent);
    const FwcExpandMobilColItem = new FwcBase("fwc-expand-mobile-col-item", FwcExpandMobileColItemComponent);
    FwcExpandMobilColItem.build();
  }
}

class FwcFeaturetteComponent extends LitElement {
  static get properties() {
    return {
      triggerAnimate: { attribute: "trigger-animate", type: String },
    };
  }

  constructor() {
    super();
    this.featuretteItems = [];
    this.isAnimateEnabled = false;
  }

  /** Checks if trigger-animate was updated to 'start' or already equal to 'start' */
  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);
    if (name === "trigger-animate" && newVal === "start") {
      if (oldVal) {
        this._startAnimation();
      } else {
        this.isAnimateEnabled = true;
      }
    }
  }

  /** Checks if isAnimateEnabled is true on page load and starts animation */
  firstUpdated() {
    this.featuretteItems = this._getSlottedChildren();
    if (this.isAnimateEnabled) {
      this._setAnimation();
    }
  }

  /** Dispatches an event when the component has rendered in the DOM */
  connectedCallback() {
    super.connectedCallback();
    const componentName = this.tagName.toLowerCase();
    const event = new CustomEvent(`${componentName}-connected`, {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  /** Retrieves the slotted children in the view and condenses it into an array of elements */
  _getSlottedChildren() {
    const slot = this.shadowRoot.querySelector("slot");
    const childNodes = slot.assignedNodes({ flatten: true });
    return Array.prototype.filter.call(childNodes, (node) => node.nodeType === Node.ELEMENT_NODE && node.nodeName === "FWC-FEATURETTE-ITEM");
  }

  /** Manually fires the animations on attribute change outside of lifecycle */
  _startAnimation() {
    if (this.featuretteItems) {
      this.featuretteItems.forEach((item, index) => {
        item.setAnimation(true, index * 300);
        item.startAnimation();
      });
    }
  }

  /** Sets the animate bool to true on child featurette items that will animate after they finish loading */
  _setAnimation() {
    if (this.featuretteItems) {
      this.featuretteItems.forEach((item, index) => {
        item.setAnimation(true, index * 300);
      });
    }
  }

  render() {
    return html` <slot></slot> `;
  }
}

class FwcFeaturetteItemComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(90%);
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(0);
          }
        }
        .featurette {
          position: relative;
          display: flex;
          flex-direction: row;
          margin-bottom: calc(5 * var(--fwcu));
          transform: translateY(90%);
          opacity: 0;
        }
        .animate {
          animation-name: fadeInUp;
          animation-delay: 0ms;
          animation-duration: 1s;
          animation-timing-function: ease-out;
          animation-fill-mode: both;
          opacity: 1;
        }
        .featurette-image {
          display: flex;
          justify-content: flex-start;
          opacity: 1;
          width: calc(6.25 * var(--fwcu));
          align-items: center;
          flex: 1 0 auto;
        }
        .featurette-image ::slotted(img),
        .featurette-image ::slotted(svg) {
          width: calc(6.25 * var(--fwcu));
          height: calc(6.25 * var(--fwcu));
        }
        .featurette-content {
          flex: 50 0 0;
          padding-left: calc(1.5 * var(--fwcu));
        }
        @media screen and (max-width: 600px) {
          .featurette {
            transform: none;
            margin-bottom: calc(6 * var(--fwcu));
            opacity: 1;
          }
          .animate {
            animation-duration: 0s;
          }
          .featurette-image {
            opacity: 0.1;
            position: absolute;
            left: 50%;
            transform: translate(-50%, -50%);
            top: 50%;
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: center;
          }
          .featurette-image ::slotted(img),
          .featurette-image ::slotted(svg) {
            width: calc(11.5 * var(--fwcu));
            height: calc(11.5 * var(--fwcu));
          }
        }
        @media screen and (max-width: 900px) {
          .featurette-image {
            align-items: flex-start;
          }
        }
      `,
    ];
  }

  constructor() {
    super();
    this.delay = 0;
    this.animate = false;
  }

  /** Starts fade-up animation if 'animate' bool was already set to true by parent component */
  firstUpdated() {
    if (this.animate) {
      this.startAnimation();
    }
  }

  /** Set animate bool and the delay time on this featurette item */
  setAnimation(val, delay) {
    this.animate = val;
    this.delay = delay;
  }

  /** Starts fade-up animation but also checks the parent container exists before animating */
  startAnimation() {
    const featurette = this.shadowRoot.querySelector(".featurette");
    if (featurette) {
      setTimeout(() => {
        featurette.classList.add("animate");
      }, this.delay);
    }
  }

  render() {
    return html`
      <div class="featurette">
        <div class="featurette-image">
          <slot name="fwc-featurette-image"></slot>
        </div>
        <div class="featurette-content">
          <slot name="fwc-featurette-content"></slot>
        </div>
      </div>
    `;
  }
}

class FwcFeaturette extends FwcBase {
  constructor() {
    super("fwc-featurette", FwcFeaturetteComponent);
    const FwcFeaturetteItem = new FwcBase("fwc-featurette-item", FwcFeaturetteItemComponent);
    FwcFeaturetteItem.build();
  }
}

class FwcFooterStickyComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: auto;
          position: fixed;
          background-color: var(--color-white);
          border: 1px solid var(--color-gray-four);
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
        }
        footer {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          min-height: calc(3 * var(--fwcu));
          padding: calc(3 * var(--fwcu)) calc(1.5 * var(--fwcu));
          max-width: calc(144 * var(--fwcu));
          margin: 0 auto;
        }
        footer.space {
          justify-content: space-between;
        }

        @media screen and (max-width: 600px) {
          slot[name="fwc-footer-sticky-left"] {
            display: none;
          }
          footer {
            padding-right: calc(4 * var(--fwcu));
            padding-left: calc(4 * var(--fwcu));
          }
          footer,
          footer.space {
            justify-content: center;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      left: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.left = false;
  }

  _onSlotChange() {
    this._checkLeftSlot();
  }

  _checkLeftSlot() {
    const slot = this.shadowRoot.querySelector("#leftSlot");
    const nodes = slot.assignedNodes();
    if (nodes.length) {
      this.left = true;
    }
  }

  render() {
    return html`
      <footer class=${`${this.left ? "space" : ""}`}>
        <slot @slotchange=${this._onSlotChange} id="leftSlot" name="fwc-footer-sticky-left"></slot>
        <slot id="rightSlot" name="fwc-footer-sticky-right"></slot>
      </footer>
    `;
  }
}

class FwcFooterSticky extends FwcBase {
  constructor() {
    super("fwc-footer-sticky", FwcFooterStickyComponent);
  }
}

class FwcHeaderCompoundComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          margin-bottom: calc(6 * var(--fwcu));
        }
        h4 {
          font-family: var(--font-serif);
          color: var(--color-gray-one);
          font-size: var(--fwcu);
          margin: 0;
          padding: 0;
          display: flex;
          justify-content: flex-start;
          flex-direction: column;
          display: inline-block;
        }

        span.top {
          color: var(--color-gray-two);
          font-size: calc(2 * var(--fwcu));
          line-height: calc(2.25 * var(--fwcu));
          font-family: var(--font-sans-serif);
          font-weight: 400;
          display: block;
        }
        span.btm {
          color: var(--color-gray-one);
          font-size: calc(2.5 * var(--fwcu));
          line-height: calc(3 * var(--fwcu));
          font-family: var(--font-serif);
          font-weight: 600;
        }
        span.line {
          content: "";
          display: block;
          align-self: stretch;
          flex-shrink: 0;
          width: auto;
          height: 1px;
          margin-bottom: calc(2.25 * var(--fwcu));
          background-color: var(--color-dviz-two);
        }
      `,
    ];
  }

  static get properties() {
    return {
      textTop: { attribute: "text-top", type: String },
      textBtm: { attribute: "text-btm", type: String },
    };
  }

  constructor() {
    super();
    /* Accessiblity concern if h4 is empty */
    this.textTop = "-";
    this.textBtm = "--";
  }

  render() {
    return html` <h4>
      <span class="line"></span>
      <span class="top">${this.textTop}</span>
      <span class="btm">${this.textBtm}</span>
    </h4>`;
  }
}

class FwcHeaderCompound extends FwcBase {
  constructor() {
    super("fwc-header-compound", FwcHeaderCompoundComponent);
  }
}

class FwcHeroComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        @keyframes scaleIn {
          0% {
            transform: scale(1.75);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        :host {
          position: relative;
          overflow: hidden;
          background-color: var(--color-white);
          --gradient: rgba(var(--color-rgb-white), 1) 0%, rgba(var(--color-rgb-white), 0.8) 66%, rgba(var(--color-rgb-white), 0) 100%;
        }

        h1 {
          margin-bottom: calc(1.5 * var(--fwcu));
          color: var(--color-gray-one);
        }

        picture {
          width: 100%;
          height: 100%;
          display: flex;
          background-color: var(--color-white);
        }

        img {
          background-color: var(--color-white);
          object-fit: cover;
          width: 100%;
          height: auto;
          min-height: calc(45 * var(--fwcu));
          opacity: 0;
        }

        .fwc-container {
          padding: calc(12 * var(--fwcu)) 0;
        }

        .img-container {
          position: absolute;
          top: 0;
          bottom: 0;
          right: 0;
          left: 0;
          overflow: hidden;
        }

        .mobile-bottom-fade {
          position: absolute;
          display: none;
          bottom: 0;
          width: 100%;
          height: 50%;
          background-image: linear-gradient(to bottom, rgba(var(--color-rgb-white), 0), rgba(var(--color-rgb-white), 1) 100%);
        }

        .image-fade {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 100%;
          background-image: linear-gradient(to right, var(--gradient));
        }
        .image-fade.right {
          background-image: linear-gradient(to left, var(--gradient));
        }

        .scale-in {
          animation-fill-mode: forwards;
          animation-name: scaleIn;
          animation-duration: 7s;
          animation-timing-function: ease-out;
          width: 100%;
          height: auto;
        }

        .content {
          position: relative;
          text-align: left;
          z-index: 1;
        }

        @media screen and (max-width: 900px) {
          .fwc-container {
            padding: 0;
            padding-bottom: calc(6 * var(--fwcu));
          }

          .content {
            padding: 0 calc(2 * var(--fwcu));
          }

          .img-container {
            position: relative;
          }

          .mobile-bottom-fade {
            display: block;
          }

          .image-fade {
            display: none;
          }
        }

        @media screen and (max-width: 600px) {
          .content {
            padding: 0;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      alignContent: { attribute: "align-content", type: String }, // left | right
      config: { type: Object },
      holdAnim: { attribute: "hold-anim", type: Boolean },
      imagePositionX: { attribute: "image-position-x", type: String },
      imagePositionY: { attribute: "image-position-y", type: String },
      image: { type: String },
      mobileImage: { attribute: "mobile-image", type: String },
      overlay: { type: Boolean },
      title: { type: String },
    };
  }

  constructor() {
    super();
    this.alignContent = "left";
    this.holdAnim = false;
    this.config = fwcConfig;
    this.imagePositionX = "center";
    this.imagePositionY = "center";
    this.image = null;
    this.mobileImage = null;
    this.overlay = false;
  }

  firstUpdated() {
    if (this.mobileImage === null) {
      this.mobileImage = this.image;
    }
  }

  _startImageAnimation(e) {
    if (!e.target.classList.contains("scale-in")) {
      e.target.classList.add("scale-in");
    }
  }

  _onImageLoad(e) {
    if (!this.holdAnim) {
      this._startImageAnimation(e);
    }
  }

  triggerAnimation() {
    if (this.holdAnim) {
      const img = this.shadowRoot.getElementById("Hero");
      this._startImageAnimation({ target: img });
    }
  }

  _renderTitle() {
    if (this.title) return html`<h1>${this.title}</h1>`;
  }

  _contentAligned() {
    const { alignContent } = this;
    const padding = html`<div class="fwc-col-1 md-0"></div>`;
    return html`
      <div class="fwc-row ${alignContent === "right" ? "j-end" : ""}">
        ${alignContent === "left" ? padding : ""}
        <div class="fwc-col-6 md-12">
          <div class="content">
            ${this._renderTitle()}
            <slot name="fwc-hero-content"></slot>
          </div>
        </div>
        ${alignContent === "right" ? padding : ""}
      </div>
    `;
  }

  _overlay() {
    if (this.overlay) {
      return html`<div class="image-fade ${this.alignContent}"></div>`;
    }
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/grid.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />

      <div class="fwc-container">
        <div class="img-container">
          <picture>
            <source srcset=${this.mobileImage} media="(max-width: 600px)" />
            <img @load=${this._onImageLoad} id="Hero" src=${this.image} alt="Hero Image" style="object-position: ${this.imagePositionX} ${this.imagePositionY}" />
          </picture>
          <div class="mobile-bottom-fade"></div>
        </div>

        ${this._overlay()} ${this._contentAligned()}
      </div>
    `;
  }
}

class FwcHero extends FwcBase {
  constructor() {
    super("fwc-hero", FwcHeroComponent);
  }
}

class FwcIconTextComponent extends LitElement {
  static get properties() {
    return {
      triggerAnimate: { attribute: "trigger-animate", type: String },
    };
  }

  constructor() {
    super();
    this.iconTextItems = [];
    this.isAnimateEnabled = false;
  }

  /** Checks if trigger-animate was updated to 'start' or already equal to 'start' */
  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);
    if (name === "trigger-animate" && newVal === "start") {
      if (oldVal) {
        this._startAnimation();
      } else {
        this.isAnimateEnabled = true;
      }
    }
  }

  /** Checks if isAnimateEnabled is true on page load and starts animation */
  firstUpdated() {
    this.iconTextItems = this._getSlottedChildren();
    if (this.isAnimateEnabled) {
      this._setAnimation();
    }
  }

  /** Dispatches an event when the component has rendered in the DOM */
  connectedCallback() {
    super.connectedCallback();
    const componentName = this.tagName.toLowerCase();
    const event = new CustomEvent(`${componentName}-connected`, {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  /** Retrieves the slotted children in the view and condenses it into an array of elements */
  _getSlottedChildren() {
    const slot = this.shadowRoot.querySelector("slot");
    const childNodes = slot.assignedNodes({ flatten: true });
    return Array.prototype.filter.call(childNodes, (node) => node.nodeType === Node.ELEMENT_NODE && node.nodeName === "FWC-ICON-TEXT-ITEM");
  }

  /** Manually fires the animations on attribute change outside of lifecycle */
  _startAnimation() {
    if (this.iconTextItems) {
      this.iconTextItems.forEach((item, index) => {
        item.setAnimation(true, index * 300);
        item.startAnimation();
      });
    }
  }

  /** Sets the animate bool to true on child iconText items that will animate after they finish loading */
  _setAnimation() {
    if (this.iconTextItems) {
      this.iconTextItems.forEach((item, index) => {
        item.setAnimation(true, index * 300);
      });
    }
  }

  render() {
    return html` <slot></slot> `;
  }
}

class FwcIconTextItemComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(90%);
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(0);
          }
        }
        .iconText {
          position: relative;
          display: flex;
          flex-direction: row;
          margin-bottom: calc(5 * var(--fwcu));
          transform: translateY(90%);
          opacity: 0;
          align-items: flex-start;
        }
        .animate {
          animation-name: fadeInUp;
          animation-delay: 0ms;
          animation-duration: 1s;
          animation-timing-function: ease-out;
          animation-fill-mode: both;
          opacity: 1;
        }
        .iconText-image {
          display: flex;
          justify-content: flex-start;
          opacity: 1;
          width: calc(6.25 * var(--fwcu));
          align-items: center;
          flex: 1 0 auto;
        }
        .iconText-image ::slotted(img),
        .iconText-image ::slotted(svg) {
          width: calc(6.25 * var(--fwcu));
          height: calc(6.25 * var(--fwcu));
        }
        .iconText-content {
          flex: 50 0 0;
          padding-left: calc(2.25 * var(--fwcu));
        }
        @media screen and (max-width: 600px) {
          .iconText {
            transform: none;
            margin-bottom: calc(6 * var(--fwcu));
            opacity: 1;
            flex-direction: row-reverse;
            align-items: center;
          }
          .iconText-content {
            padding-right: calc(2 * var(--fwcu));
          }
          .animate {
            animation-duration: 0s;
          }
          .iconText-image {
            width: auto;
          }
          .iconText-image ::slotted(img),
          .iconText-image ::slotted(svg) {
            width: calc(11.5 * var(--fwcu));
            height: calc(11.5 * var(--fwcu));
          }
        }
        @media screen and (max-width: 900px) {
          .iconText-image {
            align-items: flex-start;
          }
        }
      `,
    ];
  }

  constructor() {
    super();
    this.delay = 0;
    this.animate = false;
  }

  /** Starts fade-up animation if 'animate' bool was already set to true by parent component */
  firstUpdated() {
    if (this.animate) {
      this.startAnimation();
    }
  }

  /** Set animate bool and the delay time on this iconText item */
  setAnimation(val, delay) {
    this.animate = val;
    this.delay = delay;
  }

  /** Starts fade-up animation but also checks the parent container exists before animating */
  startAnimation() {
    const iconText = this.shadowRoot.querySelector(".iconText");
    if (iconText) {
      setTimeout(() => {
        iconText.classList.add("animate");
      }, this.delay);
    }
  }

  render() {
    return html`
      <div class="iconText">
        <div class="iconText-image">
          <slot name="fwc-icon-text-image"></slot>
        </div>
        <div class="iconText-content">
          <slot name="fwc-icon-text-content"></slot>
        </div>
      </div>
    `;
  }
}

class FwcIconText extends FwcBase {
  constructor() {
    super("fwc-icon-text", FwcIconTextComponent);
    const FwcIconTextItem = new FwcBase("fwc-icon-text-item", FwcIconTextItemComponent);
    FwcIconTextItem.build();
  }
}

class FwcInputTypeaheadComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          position: relative;
          width: fit-content;
        }
        .results-panel {
          position: absolute;
          transition: height 0.3s ease;
          height: 0;
          z-index: 1;
          width: 100%;
        }
        ul {
          padding: 0;
          margin: var(--fwcu) auto 0 auto;
          display: none;
          width: 100%;
          z-index: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          max-height: calc(22 * var(--fwcu));
          width: 100%;
        }
      `,
    ];
  }

  static get properties() {
    return {
      _expanded: { attribute: false },
      _slottedInputNodes: { attribute: false },
      _slottedResultNodes: { attribute: false },
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this.expandCollapse = null;
    this._expanded = false;
    this._itemIndex = -1;
    this._slottedInputNodes = null;
    this._slottedResultNodes = null;
    this._slottedResultCount = 0;
    this._slottedNoResultCount = 0;
    this._handlePanelClickout = this._handlePanelClickout.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("click", this._handlePanelClickout);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._handlePanelClickout);
    super.disconnectedCallback();
  }

  firstUpdated() {
    this._slottedInputNodes = this.shadowRoot.querySelector("#Input").assignedNodes();
    const dropdown = this.shadowRoot.querySelector("#ExpandeCollapsePanel");
    this.expandCollapse = new ExpandCollapse(dropdown);

    this.shadowRoot.addEventListener("fwc-typeahead-result-selected", (e) => {
      this._applySelectedValue(e);
      this._handleCollapsePanel();
      this._returnFocus();
    });

    this.shadowRoot.addEventListener("fwc-typeahead-result-arrowup", () => {
      this._handleArrowUp();
    });

    this.shadowRoot.addEventListener("fwc-typeahead-result-arrowdown", () => {
      this._handleArrowDown();
    });

    this.shadowRoot.addEventListener("fwc-typeahead-result-escaped", () => {
      this._handleCollapsePanel();
      this._returnFocus();
    });

    this.shadowRoot.addEventListener("fwc-typeahead-textfield-keydown", (e) => {
      this._handleKeyDown(e);
    });
  }

  _applySelectedValue(event) {
    this._slottedInputNodes[0].setInputValue(event.detail.item.value);
  }

  _handleArrowUp() {
    if (!this._slottedResultNodes || this._slottedResultNodes.length == 0) return;
    if (this._itemIndex - 1 >= 0) {
      this._itemIndex -= 1;
    }
    this._slottedResultNodes[this._itemIndex].setResultFocus();
  }

  _handleArrowDown() {
    if (!this._slottedResultNodes || this._slottedResultNodes.length == 0) return;
    if (this._itemIndex + 1 <= this._slottedResultNodes.length - 1) {
      this._itemIndex += 1;
    }
    this._slottedResultNodes[this._itemIndex].setResultFocus();
  }

  _handlePanelClickout(e) {
    if (!this.contains(e.target)) {
      if (this._expanded) {
        this._handleCollapsePanel();
      } else {
        this._removeActiveSearchState();
      }
    }
  }

  _handleValueChange(value) {
    this._slottedResultCount = this.shadowRoot.querySelector("#Results").assignedNodes({ flatten: true }).length;
    this._slottedNoResultCount = this.shadowRoot.querySelector("#NoResults").assignedNodes({ flatten: true }).length;

    // No typed value
    if (!value.length) {
      this._slottedResultCount = 0;
      this._handleCollapsePanel();
      this._returnFocus();
    } // typed value & no results and no results window
    else if (!this._slottedResultCount && value.length && !this._slottedNoResultCount) {
      this._handleCollapsePanel();
    } // typed value and results
    else if (value.length && this._slottedResultCount) {
      this._handleExpandedPanel();
    } // type value and no results
    else if (!this._slottedResultCount && value.length) {
      this._handleExpandedNoResults();
    }
    this._renderSlotCount();
  }

  _handleExpandedPanel() {
    this._expanded = true;
    this.expandCollapse.expandPanel();
    this._slottedInputNodes[0].shadowRoot.getElementById("SearchField").setAttribute("aria-expanded", true);
    this._slottedResultNodes = this.shadowRoot.querySelector("#Results").assignedNodes();
  }

  _handleExpandedNoResults() {
    this._expanded = true;
    this.expandCollapse.expandPanel();
    this._slottedInputNodes[0].shadowRoot.getElementById("SearchField").setAttribute("aria-expanded", true);
    this._slottedResultNodes = this.shadowRoot.querySelector("#NoResults").assignedNodes();
  }

  _handleCollapsePanel() {
    this._expanded = false;
    this.expandCollapse.collapsePanel();
    this._slottedInputNodes[0].shadowRoot.getElementById("SearchField").setAttribute("aria-expanded", false);
    this._itemIndex = -1;
  }

  _removeActiveSearchState() {
    if (this._slottedInputNodes[0].shadowRoot.querySelector(".fwc-input-search")) {
      this._slottedInputNodes[0].shadowRoot.querySelector(".fwc-input-search").classList.remove("active");
    }
  }

  _returnFocus() {
    this._slottedInputNodes[0].setInputFocus();
  }

  _handleKeyDown(e) {
    switch (e.detail.sourceEvent.key) {
      case "ArrowDown":
        e.preventDefault();
        this._handleArrowDown();
        break;
      case "Down":
        e.preventDefault();
        this._handleArrowDown();
        break;
      case "Tab":
        this._handleCollapsePanel();
        this._removeActiveSearchState();
        break;
      default:
        this._handleValueChange(e.detail.item.value);
    }
  }

  _renderSlotCount() {
    return html` <span aria-live="assertive" style="" role="group" aria-label="${this._slottedResultCount} results are available"></span>`;
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/components/fwc-cards.css" />
      <slot id="Input" name="input"></slot>
      <div class="results-panel ${this._expanded ? "expanded" : ""}" id="ExpandeCollapsePanel">
        <ul role="listbox" tabindex="-1" class="fwc-card-generic" id="ResultsContainer">
          <slot id="Results" name="results"></slot>
          <slot id="NoResults" name="no-results"></slot>
        </ul>
        ${this._renderSlotCount()}
      </div>
    `;
  }
}

class FwcTypeaheadTextfieldComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-typeahead-default-icon {
          position: absolute;
          bottom: 0;
          font-size: calc(2 * var(--fwcu));
          padding: calc(1.5 * var(--fwcu));
          color: var(--color-util-two);
        }
        input.icon-indent {
          padding-left: calc(5 * var(--fwcu));
        }
        .zip-flex {
          display: flex;
          width: 100%;
        }
        .zip-widget .fwc-btn {
          margin-left: calc(-2 * var(--fwcu));
          position: relative;
        }
      `,
    ];
  }

  static get properties() {
    return {
      btnLabel: { attribute: "btn-label", type: String },
      config: { type: Object },
      icon: { type: String },
      inputStyle: { attribute: "input-style", type: String }, // zip | search | custom
      label: { type: String },
      placeholder: { type: String },
      showLabel: { attribute: "show-label", type: Boolean },
      value: { type: String },
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this.label = "Search";
    this.placeholder = "";
    this.searchEl = null;
    this.value = "";
    this._handleInputKeydown = debounce((e) => this._sendKeyEvent(e), 200);
  }

  _sendKeyEvent(e) {
    this.value = this.searchEl.value;
    const keydown = new CustomEvent("fwc-typeahead-textfield-keydown", {
      detail: { sourceEvent: e, item: this },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(keydown);
  }

  firstUpdated() {
    // Only 1 id is needed, because only 1 input will ever be drawn
    this.searchEl = this.shadowRoot.getElementById("SearchField");
  }

  setInputValue(val) {
    this.searchEl.value = val;
  }

  setInputFocus() {
    this.searchEl.focus();
  }

  _addActiveSearchState() {
    this.shadowRoot.querySelector(".fwc-input-search").classList.add("active");
  }

  _handleZipButtonClick(e) {
    this.value = this.searchEl.value;
    const enter = new CustomEvent("fwc-typeahead-textfield-zip-click", {
      detail: { sourceEvent: e, item: this },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(enter);
  }

  _initDefaultInput() {
    return html`
      <div class="fwc-input">
        ${this.icon ? html`<span class="fwc-icon fwc-typeahead-default-icon ${this.icon}"></span>` : ""}
        <label for="SearchField" class="fwc-label ${!this.showLabel ? "sr-only" : ""}">${this.label}</label>
        <input value=${this.value} class="${this.icon ? "icon-indent" : ""}" aria-autocomplete="list" aria-expanded="false" type="text" role="combobox" id="SearchField" name="search" @keydown=${this._handleInputKeydown} placeholder="${this.placeholder}" autocomplete="off" />
      </div>
    `;
  }

  _initSearchInput() {
    return html`
      <div class="fwc-input fwc-input-search">
        ${this.icon ? html`<span class="fwc-icon ${this.icon}"></span>` : ""}
        <label for="SearchField" class="fwc-label ${!this.showLabel ? "sr-only" : ""}">${this.label}</label>
        <span id="search" class="fwc-icon fwc-icon-search" aria-hidden="true"></span>
        <input value=${this.value} class="${this.icon ? "icon-indent" : ""} " @focus="${this._addActiveSearchState}" aria-expanded="false" type="text" role="combobox" id="SearchField" name="search" @keydown=${this._handleInputKeydown} placeholder="${this.placeholder}" autocomplete="off" />
      </div>
    `;
  }

  _initZipWidgetInput() {
    return html`
      <div class="fwc-input zip-widget">
        ${this.icon ? html`<span class="fwc-icon fwc-typeahead-default-icon ${this.icon}"></span>` : ""}
        <label for="SearchField" class="fwc-label ${!this.showLabel ? "sr-only" : ""}">${this.label}</label>
        <div class="zip-flex">
          <input value=${this.value} class="${this.icon ? "icon-indent" : ""}" aria-expanded="false" id="SearchField" name="search" aria-autocomplete="list" type="text" role="combobox" @keydown=${this._handleInputKeydown} placeholder="${this.placeholder}" autocomplete="off" />
          <button class="fwc-btn fwc-btn-primary" @click="${this._handleZipButtonClick}">${this.btnLabel}</button>
        </div>
      </div>
    `;
  }

  _renderWrapperInput() {
    let template;
    switch (this.inputStyle) {
      case "zip":
        template = () => this._initZipWidgetInput();
        break;
      case "search":
        template = () => this._initSearchInput();
        break;
      default:
        template = () => this._initDefaultInput();
    }
    return html`${template()}`;
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/components/fwc-input.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/icons.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/buttons-links.css" />
      ${this._renderWrapperInput()}
    `;
  }
}

class FwcTypeaheadResultComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          position: relative;
        }
        li {
          font-weight: 400;
          display: flex;
          margin: 0;
          color: var(--color-gray-two);
          background: white;
          cursor: pointer;
        }
        li > div {
          padding: calc(1.5 * var(--fwcu));
        }
        li.selected,
        li:hover,
        li:focus {
          background: var(--color-primary);
          color: var(--color-white);
          outline: none;
        }

        .fwc-icon {
          color: var(--color-gray-three);
          padding-right: var(--fwcu);
          font-size: calc(2 * var(--fwcu));
          padding-top: calc(0.5 * var(--fwcu));
        }

        li.selected .fwc-icon,
        li:hover .fwc-icon,
        li:focus .fwc-icon {
          color: var(--color-white);
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      customContent: { attribute: "custom-content", type: Boolean },
      customResult: { attribute: "custom-result", type: Object },
      icon: { type: String },
      selected: { type: Boolean, reflect: true },
      value: { type: String },
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this.customContent = false;
    this.customResult = {};
    this.selected = false;
    this.value = "";
    this.result = null;
  }

  firstUpdated() {
    this.result = this.shadowRoot.getElementById("Result");
    if (this.customContent) {
      this.result.addEventListener("blur", () => this._handleBlur());
    }
  }

  _keydownHandler(e) {
    switch (e.code) {
      case "Tab":
        e.stopPropagation();
        this._dispatchEscapeKey(e.code);
        break;
      case "Space":
      case "Enter":
        e.stopPropagation();
        this._dispatchSelected(e.code);
        break;
      case "ArrowUp":
        e.stopPropagation();
        e.preventDefault();
        this._dispatchArrowKeyUp(e.code);
        break;
      case "ArrowDown":
        e.stopPropagation();
        e.preventDefault();
        this._dispatchArrowKeyDown(e.code);
        break;
      case "Esc":
      case "Escape":
        e.stopPropagation();
        this._dispatchEscapeKey(e.code);
        break;
    }
  }

  _handleBlur() {
    // For custom results - needs access to focus for styling
    if (this.classList.contains("active")) {
      this.classList.remove("active");
    }
  }

  _dispatchArrowKeyUp(key) {
    const arrowup = new CustomEvent("fwc-typeahead-result-arrowup", {
      detail: { key, item: this },
      bubbles: true,
      composed: false,
    });
    this.dispatchEvent(arrowup);
  }

  _dispatchArrowKeyDown(key) {
    const arrowdown = new CustomEvent("fwc-typeahead-result-arrowdown", {
      detail: { key, item: this },
      bubbles: true,
      composed: false,
    });
    this.dispatchEvent(arrowdown);
  }

  _dispatchSelected(key) {
    const selected = new CustomEvent("fwc-typeahead-result-selected", {
      detail: { key, item: this },
      bubbles: true,
      composed: false,
    });
    this.dispatchEvent(selected);
  }

  _dispatchEscapeKey(key) {
    const escaped = new CustomEvent("fwc-typeahead-result-escaped", {
      detail: { key, item: this },
      bubbles: true,
      composed: false,
    });
    this.dispatchEvent(escaped);
  }

  _renderContent() {
    const slotted = html`<slot></slot>`;
    const standard = html`
      <div>
        ${this.icon ? html`<span class="fwc-icon ${this.icon}"></span>` : html``}
        <span>${this.value}</span>
      </div>
    `;
    const template = this.customContent ? slotted : standard;
    return template;
  }

  setResultFocus() {
    this.result.focus();
    if (this.customContent) {
      this.classList.add("active");
    }
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/icons.css" />
      <li role="option" data-value="${this.value}" aria-selected="${this.selected}" class="${this.selected ? "selected" : ""}" tabindex="-1" id="Result" @click="${this._dispatchSelected}" @keydown="${this._keydownHandler}">${this._renderContent()}</li>
    `;
  }
}

class FwcInputTypeahead extends FwcBase {
  constructor() {
    super("fwc-input-typeahead", FwcInputTypeaheadComponent);
    const textfield = new FwcBase("fwc-typeahead-textfield", FwcTypeaheadTextfieldComponent);
    const result = new FwcBase("fwc-typeahead-result", FwcTypeaheadResultComponent);
    textfield.build();
    result.build();
  }
}

class FwcModalSlideComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-modal-wrapper {
          position: fixed;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 9999;
          pointer-events: none;
          width: 100%;
          visibility: hidden;
        }
        .fwc-modal-wrapper.open {
          pointer-events: all;
          visibility: visible;
        }
        .fwc-modal-bg {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          background: rgba(var(--color-rgb-util-four), 0.9);
          transition: opacity 250ms ease;
          opacity: 0;
          visibility: hidden;
        }
        .fwc-modal-wrapper.open .fwc-modal-bg {
          opacity: 1;
          visibility: visible;
        }
        .fwc-modal-container {
          background: var(--color-white);
          border: 1px solid var(--color-white);
          width: 60%;
          height: 100%;
          position: absolute;
          opacity: 0;
          transition: all 350ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
          font-size: var(--default-font-size);
          padding-top: calc(9 * var(--fwcu)); /* 7.5height + 1.5top of close btn */
          overflow-x: hidden;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .fwc-modal-container.open {
          transition: all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        .fwc-modal-container.left {
          left: -100%;
          border-top-right-radius: var(--border-radius);
          border-bottom-right-radius: var(--border-radius);
        }
        .fwc-modal-container.right {
          right: -100%;
          border-top-left-radius: var(--border-radius);
          border-bottom-left-radius: var(--border-radius);
        }
        .fwc-modal-container.top {
          top: -100%;
          left: 0;
          width: 100%;
          height: 60%;
          border-bottom-left-radius: var(--border-radius);
          border-bottom-right-radius: var(--border-radius);
        }
        .fwc-modal-container.bottom {
          bottom: -100%;
          left: 0;
          width: 100%;
          height: 60%;
          border-top-left-radius: var(--border-radius);
          border-top-right-radius: var(--border-radius);
        }
        .fwc-modal-container.bottom.full,
        .fwc-modal-container.top.full {
          height: 100%;
          border-radius: none;
        }
        .open .fwc-modal-container {
          opacity: 1;
        }
        .open .fwc-modal-container.left {
          left: 0%;
        }
        .open .fwc-modal-container.right {
          right: 0%;
        }
        .open .fwc-modal-container.top {
          top: 0%;
        }
        .open .fwc-modal-container.bottom {
          bottom: 0%;
        }

        .fwc-modal-close {
          background: var(--color-gray-five);
          border: none;
          cursor: pointer;
          position: absolute;
          top: calc(1.5 * var(--fwcu));
          padding: calc(0.75 * var(--fwcu));
          width: calc(7.5 * var(--fwcu));
          height: calc(7.5 * var(--fwcu));
          border-radius: var(--border-radius);
        }

        .fwc-modal-close svg {
          fill: var(--color-primary);
        }

        .left .fwc-modal-close {
          right: calc(1.5 * var(--fwcu));
        }
        .right .fwc-modal-close {
          left: calc(1.5 * var(--fwcu));
          transform: rotate(180deg);
        }
        .top .fwc-modal-close {
          right: calc(1.5 * var(--fwcu));
          transform: rotate(90deg);
        }
        .bottom .fwc-modal-close {
          right: calc(1.5 * var(--fwcu));
          transform: rotate(-90deg);
        }

        @media screen and (max-width: 900px) {
          .fwc-modal-container.left,
          .fwc-modal-container.right {
            width: 95%;
          }
          .fwc-modal-container.top,
          .fwc-modal-container.bottom {
            height: 95%;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      open: { type: Boolean },
      direction: { type: String },
      full: { type: Boolean },
      openByAttribute: { attribute: "open-by-attribute", type: Boolean },
      labelAriaCloseBtn: { attribute: "label-aria-close-button", type: String },
      labelAriaModalHeader: { attribute: "label-aria-modal-header", type: String },
    };
  }

  constructor() {
    super();
    this.open = false;
    this.full = false;
    this.callerId = null; // TODO: Remove
    this.direction = "left";
    this.refocusElem = null;
    this.validDirections = ["left", "right", "top", "bottom"];
    this.openByAttribute = false;
    this.labelAriaCloseBtn = "Close Modal";
    this.preventOpenBySlotChange = false;
  }

  firstUpdated() {
    const modal = this.shadowRoot.querySelector(".fwc-modal-wrapper");

    // if an invalid direction is passed
    if (!this.validDirections.includes(this.direction)) {
      this.direction = "left";
    }

    // set event listener to init focus trap after transition ends
    modal.addEventListener("transitionend", (e) => {
      if (this.open && e.propertyName !== "opacity") {
        focusTrap(this);
      }
    });

    modal.addEventListener("keydown", (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        this._onClosePress();
      }
    });
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "openByAttribute") {
        this.preventOpenBySlotChange = this.openByAttribute;
      }
      if (propName === "open") {
        this._toggleBodyScrollClass(oldValue !== undefined ? !oldValue : false);
      }
    });
  }

  _onSlotChange() {
    const slot = this.shadowRoot.querySelector("slot");
    const slotNodes = slot.assignedNodes();

    // do not close the modal if slotted content updates, only if it is empty/removed
    if (this.open && slotNodes.length > 0) {
      return;
    }

    if (!this.preventOpenBySlotChange) {
      this._toggleModal();
    }
  }

  _toggleModal() {
    this.open = !this.open;
    this._refocusElem();
  }

  _toggleBodyScrollClass(isOpen) {
    if (isOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }

  _onClosePress() {
    const event = new CustomEvent("fwc-modal-close", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(event);
    this._closeModal();
  }

  _closeModal() {
    if (this.preventOpenBySlotChange) {
      this.open = false;
      this.removeAttribute("open");
      this._refocusElem();
      return;
    }
    if (this.open) {
      const slot = this.shadowRoot.querySelector("slot");
      const nodes = slot.assignedNodes();
      // remove everything inside the slot
      nodes.forEach((n) => {
        n.parentNode.removeChild(n);
      });
    }
  }

  closeSlideModal() {
    this._closeModal();
  }

  _refocusElem() {
    if (!this.open && this.refocusElem) {
      // Return focus to calling element
      setTimeout(() => {
        this.refocusElem.focus();
      }, 0);
    }
  }

  setReFocus(elem) {
    this.refocusElem = elem;
  }

  resetModalFocusTrap() {
    resetFocusTrap();
  }

  render() {
    return html`
      <div class="fwc-modal-wrapper ${this.open ? "open" : ""}" role="dialog" aria-modal="${this.open}" aria-label="${this.labelAriaModalHeader}">
        <div class="fwc-modal-bg"></div>
        <div class="fwc-modal-container ${this.direction} ${this.full ? "full" : ""}">
          <button type="button" class="fwc-modal-close" aria-label="${this.labelAriaCloseBtn}" @click="${this._onClosePress}">
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 50 50" focusable="false">
              <polygon points="19.216 44.562 1.438 26.784 0 25.281 1.438 23.778 19.216 6 22.222 9.007 8.039 23.19 50 23.19 50 27.373 8.039 27.373 22.222 41.556"/>
            </svg>
          </button>
          <slot @slotchange="${this._onSlotChange}"></slot>
        </div>
      </div>
    `;
  }
}

class FwcModalSlide extends FwcBase {
  constructor() {
    super("fwc-modal-slide", FwcModalSlideComponent);
  }
}

class FwcLeftRailBaseComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .nav-bar {
          display: none;
          background-color: var(--color-white);
        }
        @media screen and (max-width: 600px) {
          .nav-bar {
            display: block;
            height: calc(8 * var(--fwcu));
          }
          .rail {
            display: none;
          }
          .nav-bar {
            border-bottom: 1px solid var(--color-gray-four);
            padding: calc(2 * var(--fwcu)) calc(1.5 * var(--fwcu));
            text-align: right;
            position: absolute;
            left: 0;
            right: 0;
          }
          .nav-bar .fwc-link .fwc-icon {
            vertical-align: text-bottom;
            padding-left: var(--fwcu);
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      labelAriaModalHeader: { attribute: "label-aria-modal-header", type: String },
      isMobile: {},
    };
  }

  constructor() {
    super();
    this.modalSlide = new FwcModalSlide();
    this.modalSlide.build();
    this.config = fwcConfig;
    this.isMobile = null;
    this.labelAriaModalHeader = "Left Rail Modal";
    this.updateOnResize = this.updateOnResize.bind(this);
    this._handleWindowResize = debounce(this.updateOnResize);
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "isMobile") {
        if (!this.isMobile && this.modal.open) {
          this.modal.closeSlideModal();
        }
        const event = new CustomEvent("fwc-left-rail-resized", {
          detail: this.isMobile,
          bubbles: true,
          composed: false,
        });
        this.dispatchEvent(event);
      }
    });
  }

  updateOnResize() {
    this.isMobile = window.innerWidth <= breakpoints.sm ? true : false;
  }

  firstUpdated() {
    this.modal = this.shadowRoot.querySelector("fwc-modal-slide");
    this.updateOnResize();
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this._handleWindowResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._handleWindowResize);
    super.disconnectedCallback();
  }

  _openModal() {
    this.modal.setAttribute("open", true);
  }

  openLeftRail() {
    this._openModal();
  }

  render() {
    return html`
      <div class="nav-bar">
        <slot name="fwc-left-rail-mobile-nav"></slot>
      </div>

      <div class="rail">
        <slot name="fwc-left-rail"></slot>
      </div>

      <fwc-modal-slide direction="left" label-aria-modal-header="${this.labelAriaModalHeader}" open-by-attribute>
        <slot name="fwc-left-rail-mobile"></slot>
      </fwc-modal-slide>
    `;
  }
}

class FwcLeftRailBase extends FwcBase {
  constructor() {
    super("fwc-left-rail-base", FwcLeftRailBaseComponent);
  }
}

class FwcMilestonesComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        @keyframes dot {
          0% {
            width: 0;
            height: 0;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            width: calc(2.5 * var(--fwcu));
            height: calc(2.5 * var(--fwcu));
          }
        }
        @keyframes info {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        :host {
          position: relative;
          color: var(--color-gray-one);
        }
        ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          justify-content: flex-end;
          margin-bottom: calc(12 * var(--fwcu));
          flex-wrap: wrap;
        }
        ul li {
          display: flex;
          align-items: center;
          padding-right: calc(2 * var(--fwcu));
        }
        ul li::before {
          content: "";
          flex: 1 0 auto;
          margin-right: var(--fwcu);
          width: var(--default-font-size);
          height: var(--default-font-size);
          background-color: green;
          border-radius: 50%;
        }
        .bar-container,
        .info-container {
          width: 100%;
          position: relative;
          background-color: var(--color-gray-five);
          height: calc(2.5 * var(--fwcu));
          border: 1px solid rgba(var(--color-rgb-white), 0);
          border-radius: var(--border-radius);
        }
        .info-container {
          bottom: 0;
          opacity: 0;
          background: none;
        }
        .info-container.start-anim {
          opacity: 1;
          transition-delay: 500ms;
        }
        .info {
          position: absolute;
          display: flex;
          justify-content: center;
          flex-direction: column;
          top: calc(-5 * var(--fwcu));
          text-align: center;
          margin: 0;
          font-size: var(--default-font-size);
          width: calc(19 * var(--fwcu));
          color: var(--color-gray-one);
          animation-name: info;
          animation-fill-mode: both;
          animation-duration: 500ms;
          animation-delay: 750ms;
          animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
        }
        .info span.label {
          padding: 0;
          margin: 0;
          line-height: 1;
          font-weight: 400;
        }
        .info:nth-of-type(odd) {
          top: calc(6.5 * var(--fwcu));
        }
        .info::after {
          content: "";
          left: calc(50% - 1px);
          position: absolute;
          width: 2px;
          height: calc(3 * var(--fwcu));
          background-color: var(--color-gray-five);
        }
        .info:nth-of-type(even)::after {
          top: 100%;
        }
        .info:nth-of-type(odd)::after {
          bottom: 100%;
        }
        .info:nth-child(1):not(.incomplete)::after,
        ul li:nth-child(1)::before {
          background-color: var(--color-dviz-two);
        }
        .info:nth-child(2):not(.incomplete)::after,
        ul li:nth-child(2)::before {
          background-color: var(--color-dviz-seven);
        }
        .info:nth-child(3):not(.incomplete)::after,
        ul li:nth-child(3)::before {
          background-color: var(--color-dviz-four);
        }
        .info:nth-child(4):not(.incomplete)::after,
        ul li:nth-child(4)::before {
          background-color: var(--color-dviz-eight);
        }
        .bar {
          position: absolute;
          background-color: var(--color-gray-five);
          border: 1px solid rgba(var(--color-rgb-white), 0);
          border-radius: var(--border-radius);
          top: 0;
          bottom: 0;
          left: 0;
          width: 0;
          opacity: 0;
          transition: all 500ms cubic-bezier(0.19, 1, 0.22, 1);
        }
        .bar.start-anim {
          opacity: 1;
        }
        .bar::after {
          content: "";
          display: block;
          position: absolute;
          right: calc(-1.25 * var(--fwcu)); /* half of width */
          top: -2px;
          border-style: solid;
          border-color: black;
          background-color: var(--color-white);
          border-color: var(--color-gray-five);
          border-radius: 50%;
          z-index: 1;
          width: calc(2.5 * var(--fwcu));
          height: calc(2.5 * var(--fwcu));
          animation-name: dot;
          animation-fill-mode: both;
          animation-duration: 500ms;
          animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
          transform-origin: 50% 50%;
        }
        .bar:nth-of-type(1):not(.incomplete) {
          z-index: 4;
          background-color: var(--color-dviz-two);
        }
        .bar:nth-of-type(1):not(.incomplete)::after {
          animation-delay: 100ms;
          border-color: var(--color-dviz-two);
        }
        .bar:nth-of-type(2):not(.incomplete) {
          transition-delay: 100ms;
          background-color: var(--color-dviz-seven);
          z-index: 3;
        }
        .bar:nth-of-type(2):not(.incomplete)::after {
          border-color: var(--color-dviz-seven);
          animation-delay: 300ms;
        }
        .bar:nth-of-type(3):not(.incomplete) {
          transition-delay: 200ms;
          background-color: var(--color-dviz-four);
          z-index: 2;
        }
        .bar:nth-of-type(3):not(.incomplete)::after {
          border-color: var(--color-dviz-four);
          animation-delay: 400ms;
        }
        .bar:nth-of-type(4):not(.incomplete) {
          transition-delay: 300ms;
          background-color: var(--color-dviz-eight);
          z-index: 1;
        }
        .bar:nth-of-type(4):not(.incomplete)::after {
          border-color: var(--color-dviz-eight);
          animation-delay: 500ms;
        }
        @media screen and (max-width: 600px) {
          ul {
            justify-content: center;
          }
          section.vertical {
            transform: rotate(-90deg);
            transform-origin: left;
            margin-top: calc(66.5 * var(--fwcu)); /* 500px + 32px of margin hard to match 'height' and legend */
            margin-left: 50%;
          }
          section.vertical .bar-container,
          section.vertical .info-container {
            width: calc(62.5 * var(--fwcu));
            /* component is rotated 90 degrees with transform, width here is acting as height */
          }
          section.vertical .info {
            transform: rotate(90deg);
          }
          section.vertical .info:nth-of-type(even) {
            top: calc(-12 * var(--fwcu));
          }
          section.vertical .info:nth-of-type(odd) {
            top: calc(12 * var(--fwcu));
          }
          section.vertical .info::after {
            top: calc(50% - 1px);
            left: 100%;
            height: 2px;
            width: calc(3 * var(--fwcu));
          }
          section.vertical .info:nth-of-type(even)::after {
            left: 100%;
          }
          section.vertical .info:nth-of-type(odd)::after {
            left: 0;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      milestones: { type: Array },
      vertical: { type: Boolean },
      title: { type: String },
      labelIncomplete: { attribute: "label-incomplete", type: String },
      labelComplete: { attribute: "label-complete", type: String },
      holdAnim: { attribute: "hold-anim", type: Boolean },
    };
  }

  constructor() {
    super();
    this.milestones = [];
    this.vertical = false;
    this.title = "milestones";
    this.labelIncomplete = "incomplete";
    this.labelComplete = "complete";
    this.holdAnim = false;
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);
    if (name === "milestones" && oldVal !== newVal) {
      this._renderMilestones();
    }
  }

  updated() {
    if (!this.holdAnim) {
      this._startAnimation();
    }
  }

  triggerAnimation() {
    if (this.holdAnim) {
      this._startAnimation();
    }
  }

  _renderMilestones() {
    const count = this.milestones.length;
    const percent = 100 / count;
    const Info = this.milestones.map((info, i) => {
      const iStyle = {
        left: `calc(${(i + 1) * percent}% - ${9.5 * baseUnit}px)`,
      };
      return html`
        <h5 class="info ${info.complete ? "" : "incomplete"}" style=${styleMap(iStyle)}>
          <span class="amount">${"$" + info.amount}</span>
          <span class="label">${info.label}</span>
          <span class="sr-only">${info.complete ? this.labelComplete : this.labelIncomplete}</span>
        </h5>
      `;
    });
    const Bars = this.milestones.map((bar) => {
      return html` <div class="bar ${bar.complete ? "" : "incomplete"} ${ this.holdAnim ? "" : "start-anim"} "></div> `;
    });
    const Legend = this.milestones.map((l) => {
      return html`<li>${l.label}</li>`;
    });

    return {
      bars: Bars,
      info: Info,
      legend: Legend,
    };
  }

  _startAnimation() {
    const childNodes = this.shadowRoot.querySelector(".bar-container").childNodes;
    const bars = Array.prototype.filter.call(childNodes, (node) => node.nodeType == Node.ELEMENT_NODE && node.nodeName === "DIV");
    const percent = 100 / bars.length;
    this.shadowRoot.querySelector(".info-container").classList.add("start-anim");

    for (let b in bars) {
      bars[b].style.width = "0%";
      bars[b].style.left = "0%";
      bars[b].classList.add("start-anim");
      // need a 'state' change to apply CSS transitions
      setTimeout(() => {
        bars[b].style.width = percent + "%";
        bars[b].style.left = b * percent + "%";
      }, 0);
    }

    if (bars.length > 4) {
      /* eslint-disable no-console */
      console.error("Maximum of 4 Milestones has been exceeded. Component will not render properly.");
      /* eslint-enable no-console */
    }
  }

  render() {
    return html`
      <ul>
        ${this._renderMilestones().legend}
      </ul>
      <section class=${this.vertical ? "vertical" : ""}>
        <h4 class="sr-only">${this.title}</h4>
        <div class="info-container ${ this.holdAnim ? "" : "start-anim" }">${this._renderMilestones().info}</div>
        <div class="bar-container">${this._renderMilestones().bars}</div>
      </section>
    `;
  }
}

class FwcMilestones extends FwcBase {
  constructor() {
    super("fwc-milestones", FwcMilestonesComponent);
  }
}

class FwcModalBaseComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-modal-wrapper {
          position: fixed;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          pointer-events: none;
          display: flex;
          visibility: hidden;
          transition: all 300ms ease;
        }
        .fwc-modal-wrapper.open {
          pointer-events: all;
          visibility: visible;
        }
        .fwc-modal-bg {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          background: rgba(var(--color-rgb-util-four), 0.2);
          transition: opacity 300ms ease;
          opacity: 0;
          visibility: hidden;
        }
        .fwc-modal-wrapper.open .fwc-modal-bg {
          opacity: 1;
          visibility: visible;
        }
        .fwc-modal-container {
          background: var(--color-white);
          position: relative;
          opacity: 0;
          border-radius: var(--border-radius);
          box-shadow: var(--box-shadow);
          animation: fwcClose 0.3s ease-in-out forwards;
          overflow: hidden;
        }
        .fwc-modal-wrapper.open .fwc-modal-container {
          animation: fwcOpen 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
          opacity: 1;
        }
        @-webkit-keyframes fwcOpen {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        @-webkit-keyframes fwcClose {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(0);
            opacity: 0;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      _open: { type: Boolean },
      labelAria: { attribute: "label-aria", type: String },
    };
  }

  constructor() {
    super();
    this._open = false;
    this._dispatchOpenName = "fwc-modal-open";
    this._dispatchCloseName = "fwc-modal-close";
    this.labelAria = "";
  }

  firstUpdated() {
    // set event listener to dispatch event back to parent
    const modal = this.shadowRoot.querySelector(".fwc-modal-wrapper");
    modal.addEventListener("transitionend", (e) => {
      if (this._open && e.propertyName === "opacity") {
        this._dispatch(this._dispatchOpenName);
      }

      if (!this._open && e.propertyName === "opacity") {
        this._dispatch(this._dispatchCloseName);
      }
    });

    // handle background click close
    this.addEventListener("keydown", (e) => this._escapeClose(e));
  }

  _escapeClose(e) {
    if (e.key === "Escape" || e.key === "Esc") {
      this._closeModal();
    }
  }

  _dispatch(name) {
    const event = new CustomEvent(name, {
      detail: this,
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(event);
  }

  _onBackgroundPress() {
    this._closeModal();
  }

  openModal() {
    this._openModal();
  }

  _openModal() {
    setTimeout(() => {
      this._open = true;
      document.body.classList.add("no-scroll");
    }, 0);
  }

  closeModal() {
    this._closeModal();
  }

  _closeModal() {
    this._open = false;
    document.body.classList.remove("no-scroll");
  }

  render() {
    return html`
      <div class="fwc-modal-wrapper ${this._open ? "open" : ""}" aria-modal="${this._open}" role="dialog" aria-label="${this.labelAria}">
        <div class="fwc-modal-bg" @click="${this._onBackgroundPress}"></div>
        <div class="fwc-modal-container">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

class FwcModalBase extends FwcBase {
  constructor() {
    super("fwc-modal-base", FwcModalBaseComponent);
  }
}

class FwcModalComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-modal-wrapper {
          width: 92vw;
          max-width: calc(100 * var(--fwcu));
        }
        .fwc-modal-header {
          background: linear-gradient(180deg, var(--color-white) 0%, var(--color-gray-five) 100%);
          padding: calc(4 * var(--fwcu));
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: calc(4 * var(--fwcu));
          border-radius: var(--border-radius) var(--border-radius) 0 0;
        }
        h3 {
          color: var(--color-gray-one);
          margin: 0;
          margin-right: calc(3 * var(--fwcu));
        }
        .fwc-modal-close {
          background: transparent;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          width: calc(3 * var(--fwcu));
          height: calc(3 * var(--fwcu));
        }
        .fwc-modal-close svg {
          fill: var(--color-gray-two);
        }
        .fwc-modal-body {
          padding: calc(3 * var(--fwcu)) calc(4 * var(--fwcu)) 0 calc(4 * var(--fwcu));
        }
        .fwc-modal-actions {
          display: flex;
          justify-content: flex-end;
          padding: calc(4 * var(--fwcu));
        }
        .fwc-hide {
          display: none;
        }
        @media screen and (max-width: 600px) {
          ::slotted([slot="actions"]) {
            display: flex;
            justify-content: space-between;
            width: 100%;
          }
          .fwc-modal-header {
            align-items: flex-start;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      open: { type: Boolean },
      textHeader: { attribute: "text-header", type: String },
      textBody: { attribute: "text-body", type: String },
      openBySlot: { attribute: "open-by-slot", type: Boolean },
      labelAriaCloseBtn: { attribute: "label-aria-close-btn", type: String },
      // below are props added for watch/update, they are not an option to pass as attributes
      _bodySlotChildren: { type: Number },
      _actionsSlotChildren: { type: Number },
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this._fwcModalBase = new FwcModalBase();
    this._fwcModalBase.build();
    this._modalBaseElement = null;
    this.open = false;
    this.textHeader = "";
    this.textBody = "";
    this.refocusElem = null;
    this.openBySlot = false;
    this.labelAriaCloseBtn = "Close Modal";
    this._bodySlotChildren = 0;
    this._actionsSlotChildren = 0;
  }

  firstUpdated() {
    this._modalBaseElement = this.shadowRoot.querySelector("fwc-modal-base");

    // grab the open/close event from fwc-modal-base and remove the slot items
    this.addEventListener("fwc-modal-open", () => {
      setTimeout(() => {
        focusTrap(this);
      }, 0);
    });
    this.addEventListener("fwc-modal-close", () => {
      if (this.openBySlot) {
        setTimeout(() => {
          const slot = this.shadowRoot.querySelector("slot");
          const nodes = slot.assignedNodes();
          if (nodes.length) {
            nodes.forEach((n) => {
              n.parentNode.removeChild(n);
            });
          }
        }, 0);
      } else {
        this.removeAttribute("open");
      }
      setTimeout(() => {
        this._returnFocus();
      }, 0);
    });
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === "open") {
      setTimeout(() => {
        if (this.open) {
          this._open();
        } else {
          this._close();
        }
      }, 0);
    }
    super.attributeChangedCallback(name, oldVal, newVal);
  }

  _onSlotChange() {
    const bodySlot = this.shadowRoot.querySelector('slot[name="body"]');
    const bodySlotNodes = bodySlot.assignedNodes();
    const actionsSlot = this.shadowRoot.querySelector('slot[name="actions"]');
    const actionsSlotNodes = actionsSlot.assignedNodes();

    this._bodySlotChildren = bodySlotNodes.length;
    this._actionsSlotChildren = actionsSlotNodes.length;

    // do not do anything on slotchange when opening by attribute
    if (!this.openBySlot) return;
    if (bodySlotNodes.length) {
      this._open();
    } else {
      this._close();
    }
  }

  _returnFocus() {
    if (this._refocusElem) {
      // Return focus to calling element
      this._refocusElem.focus();
    }
  }

  // a public func allowing projects to set the refocus element
  setReFocus(elem) {
    this._refocusElem = elem;
  }

  closeModal() {
    this._modalBaseElement.closeModal();
  }

  _close() {
    this.closeModal();
  }

  openModal() {
    this._modalBaseElement.openModal();
  }

  _open() {
    this.openModal();
  }

  resetModalFocusTrap() {
    resetFocusTrap();
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />
      <fwc-modal-base label-aria="${this.textHeader}">
        <div class="fwc-modal-wrapper">
          <div class="fwc-modal-header">
            <h3 id="modalHeader">${this.textHeader}</h3>
            <button type="button" class="fwc-modal-close" aria-label="${this.labelAriaCloseBtn}" @click="${this._close}">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 50 50">
                <polygon points="3.523 50 0 46.477 21.477 25 0 3.523 3.523 0 25 21.477 46.477 0 50 3.523 28.523 25 50 46.477 46.477 50 25 28.523" />
              </svg>
            </button>
          </div>

          <div class="fwc-modal-body">
            <div class="${this._bodySlotChildren > 0 ? "fwc-hide" : ""}">${this.textBody}</div>
            <slot name="body" @slotchange="${this._onSlotChange}"></slot>
          </div>

          <div class="fwc-modal-actions ${this._actionsSlotChildren > 0 ? "" : "fwc-hide"}">
            <slot name="actions" @slotchange="${this._onSlotChange}"></slot>
          </div>
        </div>
      </fwc-modal-base>
    `;
  }
}

class FwcModal extends FwcBase {
  constructor() {
    super("fwc-modal", FwcModalComponent);
  }
}

/* 
  THIS COMPONENT IS DEPRECATED
  IT CAN BE USED, BUT IT IS NOT SUPPORTED
*/

class FwcModalCelebratoryComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-modal-wrapper {
          width: 92vw;
          max-width: calc(100 * var(--fwcu));
        }
        .fwc-modal-header {
          padding: calc(4 * var(--fwcu));
          display: flex;
          justify-content: center;
          align-items: center;
          padding: calc(4 * var(--fwcu)) calc(4 * var(--fwcu)) calc(4 * var(--fwcu)) calc(6 * var(--fwcu));
          border-radius: var(--border-radius) var(--border-radius) 0 0;
        }
        h2 {
          color: var(--color-gray-one);
          margin: 0;
        }
        .fwc-modal-close {
          background: transparent;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          padding: 0;
          width: calc(3 * var(--fwcu));
          height: calc(3 * var(--fwcu));
        }
        .fwc-modal-body {
          padding: 0 calc(6 * var(--fwcu)) 0 calc(6 * var(--fwcu));
        }
        .fwc-modal-actions {
          display: flex;
          justify-content: flex-end;
          padding: calc(6 * var(--fwcu));
        }
        .fwc-hide {
          display: none;
        }
        ::slotted([slot="actions"]) {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          width: 100%;
          position: relative;
          z-index: 1;
        }
        @media screen and (max-width: 600px) {
          .fwc-modal-header {
            align-items: flex-start;
          }
          .fwc-modal-body {
            padding: 0 calc(2 * var(--fwcu)) 0 calc(2 * var(--fwcu));
          }
        }
        canvas {
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          pointer-events: none;
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      open: { type: Boolean },
      delay: { type: Number },
      textHeader: { attribute: "text-header", type: String },
      openBySlot: { attribute: "open-by-slot", type: Boolean },
      // below are props added for watch/update, they are not an option to pass as attributes
      _actionsSlotChildren: { type: Number },
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this._fwcModalBase = new FwcModalBase();
    this._fwcModalBase.build();
    this._modalBaseElement = null;
    this.open = false;
    this.textHeader = "";
    this.refocusElem = null;
    this.openBySlot = false;
    this._actionsSlotChildren = 0;
    this.delay = 0;

    this.canvas = null;
    this.ctx = null;
    this.cx = null;
    this.cy = null;
    this.confettiAnimation = null;
    this.confetti = [];
    this.confettiCount = 300;
    this.gravity = 0.5;
    this.terminalVelocity = 4;
    this.drag = 0.1;
    this.colors = ["#FFE43B", "#B3FAFF", "#FF7CEC", "#D8D8D8"];
    this.shapes = [
      { x: 4, y: 4, type: "circle" },
      { x: 8, y: 8, type: "circle" },
      { x: 10, y: 10, type: "circle" },
      { x: 4, y: 4, type: "triangle" },
      { x: 10, y: 10, type: "triangle" },
      { x: 16, y: 16, type: "triangle" },
      { x: 8, y: 2, type: "rod" },
      { x: 10, y: 4, type: "rod" },
      { x: 16, y: 4, type: "rod" },
    ];
  }

  firstUpdated() {
    this._modalBaseElement = this.shadowRoot.querySelector("fwc-modal-base");

    this.addEventListener("fwc-modal-open", () => {
      setTimeout(() => {
        focusTrap(this);
        // init the confetti after the modal open, the canvas needs the modals full height and width
        this._initConfetti();
      }, 0);
    });

    // grab the close event from fwc-modal-base and remove the slot items
    this.addEventListener("fwc-modal-close", () => {
      if (this.openBySlot) {
        setTimeout(() => {
          const slot = this.shadowRoot.querySelector("slot");
          const nodes = slot.assignedNodes();
          if (nodes.length) {
            nodes.forEach((n) => {
              n.parentNode.removeChild(n);
            });
          }
        }, 0);
      } else {
        this.removeAttribute("open");
      }
      setTimeout(() => {
        this._returnFocus();
      }, 0);
    });
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === "open") {
      setTimeout(() => {
        if (this.open) {
          this._open();
        } else {
          this._close();
        }
      }, 0);
    }
    super.attributeChangedCallback(name, oldVal, newVal);
  }

  _initCanvas() {
    const wrapper = this.shadowRoot.querySelector(".fwc-modal-wrapper");
    this.canvas = this.shadowRoot.getElementById("canvas");
    this.ctx = this.canvas.getContext("2d");
    this.canvas.width = wrapper.clientWidth;
    this.canvas.height = wrapper.clientHeight;
    this.cx = this.ctx.canvas.width / 2;
    this.cy = this.ctx.canvas.height / 2;
  }

  _randomRange(min, max) {
    return Math.random() * (max - min) + min;
  }

  _initConfetti() {
    this._initCanvas();
    this.confetti = [];
    for (let i = 0; i < this.confettiCount; i++) {
      this.confetti.push({
        color: this.colors[Math.floor(this._randomRange(0, this.colors.length))],
        dimensions: this.shapes[Math.floor(this._randomRange(0, this.shapes.length))],
        position: {
          x: this._randomRange(0, this.canvas.width),
          y: this.canvas.height - 1,
        },
        rotation: this._randomRange(0, 2 * Math.PI),
        scale: {
          x: 1,
          y: 1,
        },
        velocity: {
          x: this._randomRange(-25, 25),
          y: this._randomRange(0, -50),
        },
      });
    }

    // use optional delay attribute
    setTimeout(() => {
      this._renderConfetti();
    }, this.delay);
  }

  _renderConfetti() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.confetti.forEach((confetto, index) => {
      let width = confetto.dimensions.x * confetto.scale.x;
      let height = confetto.dimensions.y * confetto.scale.y;

      // Move canvas to position and rotate
      this.ctx.translate(confetto.position.x, confetto.position.y);
      this.ctx.rotate(confetto.rotation);

      // Apply forces to velocity
      confetto.velocity.x -= confetto.velocity.x * this.drag;
      confetto.velocity.y = Math.min(confetto.velocity.y + this.gravity, this.terminalVelocity);
      confetto.velocity.x += Math.random() > 0.5 ? Math.random() : -Math.random();

      // Set position
      confetto.position.x += confetto.velocity.x;
      confetto.position.y += confetto.velocity.y;

      // Delete confetti when out of frame
      if (confetto.position.y >= this.canvas.height) this.confetti.splice(index, 1);

      // Loop confetto x position
      if (confetto.position.x > this.canvas.width) confetto.position.x = 0;
      if (confetto.position.x < 0) confetto.position.x = this.canvas.width;

      // Spin confetto by scaling y
      confetto.scale.y = Math.cos(confetto.position.y * 0.1);
      this.ctx.fillStyle = confetto.color;

      // Draw confetto
      if (confetto.dimensions.type === "circle") {
        this.ctx.beginPath();
        this.ctx.arc(width / 2, height / 2, width / 2, 0, 2 * Math.PI, false);
        this.ctx.fill();
      }
      if (confetto.dimensions.type === "rod") {
        this.ctx.fillRect(-width / 2, -height / 2, width, height);
      }
      if (confetto.dimensions.type === "triangle") {
        this.ctx.beginPath();
        this.ctx.moveTo(width, height);
        this.ctx.lineTo(width * 0.75, height * 0.25);
        this.ctx.lineTo(width * 1.5, height * 0.5);
        this.ctx.fill();
      }

      // Reset transform matrix
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    });

    this.confettiAnimation = window.requestAnimationFrame(this._renderConfetti.bind(this));
  }

  _onSlotChange() {
    const bodySlot = this.shadowRoot.querySelector('slot[name="body"]');
    const bodySlotNodes = bodySlot.assignedNodes();
    const actionsSlot = this.shadowRoot.querySelector('slot[name="actions"]');
    const actionsSlotNodes = actionsSlot.assignedNodes();

    this._actionsSlotChildren = actionsSlotNodes.length;

    // do not do anything on slotchange when opening by attribute
    if (!this.openBySlot) return;

    if (bodySlotNodes.length) {
      this._open();
    } else {
      this._close();
    }
  }

  _returnFocus() {
    if (this._refocusElem) {
      // Return focus to calling element
      this._refocusElem.focus();
    }
  }

  // a public func allowing projects to set the refocus element
  setReFocus(elem) {
    this._refocusElem = elem;
  }

  closeModal() {
    this._modalBaseElement.closeModal();
  }

  _close() {
    this.closeModal();
  }

  openModal() {
    this._modalBaseElement.openModal();
    window.cancelAnimationFrame(this.confettiAnimation);
  }

  _open() {
    this.openModal();
  }

  resetModalFocusTrap() {
    resetFocusTrap();
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />
      <fwc-modal-base label-aria="${this.textHeader}">
        <div class="fwc-modal-wrapper">
          <div class="fwc-modal-header">
            <h2 id="modalHeader">${this.textHeader}</h2>
          </div>

          <div class="fwc-modal-body">
            <slot name="body" @slotchange="${this._onSlotChange}"></slot>
          </div>

          <div class="fwc-modal-actions ${this._actionsSlotChildren > 0 ? "" : "fwc-hide"}">
            <slot name="actions" @slotchange="${this._onSlotChange}"></slot>
          </div>
          <canvas id="canvas" aria-hidden="true"></canvas>
        </div>
      </fwc-modal-base>
    `;
  }
}

class FwcModalCelebratory extends FwcBase {
  constructor() {
    super("fwc-modal-celebratory", FwcModalCelebratoryComponent);
  }
}

/* 
  THIS COMPONENT IS DEPRECATED
  IT CAN BE USED, BUT IT IS NOT SUPPORTED
*/
class FwcModalIllustrationComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .illustration-modal {
          display: block;
          position: relative;
          background-color: transparent;
          background-repeat: no-repeat;
          background-size: cover;
          background-position: right;
          overflow: hidden;
          width: 92vw;
          max-width: calc(100 * var(--fwcu));
          padding: calc(6 * var(--fwcu));
        }
        .illustration-modal:before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: calc(100 * var(--fwcu));
          background: linear-gradient(to left, rgba(var(--color-rgb-black), 0) 0%, rgba(var(--color-rgb-black), 0.5) 47.63%, rgba(var(--color-rgb-black), 0.6) 100%);
        }
        .illustration-wrapper {
          position: relative;
          width: 100%;
        }
        .illustration-content {
          position: relative;
          top: 0;
          left: 0;
          width: calc(44 * var(--fwcu));
        }
        h2 {
          color: var(--color-white);
          margin: 0;
        }
        .illustration-body {
          color: var(--color-white);
          margin-top: calc(3 * var(--fwcu));
        }
        .illustration-actions {
          margin-top: calc(3 * var(--fwcu));
        }

        @media screen and (max-width: 600px) {
          .illustration-modal {
            padding: calc(6 * var(--fwcu)) calc(4 * var(--fwcu));
          }
          .illustration-content {
            left: 0;
            right: 0;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 100%;
            position: relative;
          }
          ::slotted([slot="actions"]) {
            display: flex;
            justify-content: space-between;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      open: { type: Boolean },
      textHeader: { attribute: "text-header", type: String },
      textBody: { attribute: "text-body", type: String },
      image: { type: String },
      openBySlot: { attribute: "open-by-slot", type: Boolean },
      // below are props added for watch/update, they are not an option to pass as attributes
      _bodySlotChildren: { type: Number },
      _actionsSlotChildren: { type: Number },
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this._fwcModalBase = new FwcModalBase();
    this._fwcModalBase.build();
    this._modalBaseElement = null;
    this._refocusElem = null;
    this.textHeader = "";
    this.textBody = "";
    this.image = "";
    this.open = false;
    this.openBySlot = false;
    this._bodySlotChildren = 0;
    this._actionsSlotChildren = 0;
  }

  firstUpdated() {
    this._modalBaseElement = this.shadowRoot.querySelector("fwc-modal-base");

    // grab the open/close event from fwc-modal-base and remove the slot items
    window.addEventListener("fwc-modal-open", () => {
      focusTrap(this);
    });
    window.addEventListener("fwc-modal-close", () => {
      const slot = this.shadowRoot.querySelector("slot");
      const nodes = slot.assignedNodes();

      if (this.openBySlot) {
        nodes.forEach((n) => {
          n.parentNode.removeChild(n);
        });
      } else {
        this.removeAttribute("open");
      }

      this._returnFocus();
    });
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === "open") {
      setTimeout(() => {
        if (this.open) {
          this._modalBaseElement.openModal();
        } else {
          this._modalBaseElement.closeModal();
        }
      }, 0);
    }
    super.attributeChangedCallback(name, oldVal, newVal);
  }

  _onSlotChange() {
    const bodySlot = this.shadowRoot.querySelector('slot[name="body"]');
    const bodySlotNodes = bodySlot.assignedNodes();
    const actionsSlot = this.shadowRoot.querySelector('slot[name="actions"]');
    const actionsSlotNodes = actionsSlot.assignedNodes();

    this._bodySlotChildren = bodySlotNodes.length;
    this._actionsSlotChildren = actionsSlotNodes.length;

    // do not do anything on slotchange when opening by attribute
    if (!this.openBySlot) return;

    if (bodySlotNodes.length) {
      this._modalBaseElement.openModal();
    } else {
      this._modalBaseElement.closeModal();
    }
  }

  _returnFocus() {
    if (this._refocusElem) {
      // Return focus to calling element
      this._refocusElem.focus();
    }
  }

  // a public func allowing projects to set the refocus element
  setReFocus(elem) {
    this._refocusElem = elem;
  }

  _dispatch(e, name) {
    const event = new CustomEvent(name, {
      detail: { target: e.target, component: this },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(event);
  }

  closeModal() {
    this._modalBaseElement.closeModal();
  }

  _close() {
    this.closeModal();
  }

  openModal() {
    this._modalBaseElement.openModal();
  }

  _open() {
    this.openModal();
  }

  resetModalFocusTrap() {
    resetFocusTrap();
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />
      <fwc-modal-base label-aria="${this.textHeader}">
        <div class="illustration-modal" style="background-image: url(${this.image})">
          <div class="illustration-wrapper">
            <div class="illustration-content">
              <h2 id="illustration">${this.textHeader}</h2>
              <div class="illustration-body">
                <div class="${this._bodySlotChildren > 0 ? "fwc-hide" : ""}">${this.textBody}</div>
                <slot name="body" @slotchange="${this._onSlotChange}"></slot>
              </div>
              <div class="illustration-actions ${this._actionsSlotChildren > 0 ? "" : "fwc-hide"}">
                <slot name="actions" @slotchange="${this._onSlotChange}"></slot>
              </div>
            </div>
          </div>
        </div>
      </fwc-modal-base>
    `;
  }
}

class FwcModalIllustration extends FwcBase {
  constructor() {
    super("fwc-modal-illustration", FwcModalIllustrationComponent);
  }
}

class FwcPaginationComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-pagination {
          display: flex;
          flex-wrap: nowrap;
        }
        .fwc-page-align-center {
          justify-content: flex-start;
        }
        .fwc-page-align-center {
          justify-content: center;
        }
        .fwc-page-align-right {
          justify-content: flex-end;
        }
        .fwc-page-item:nth-child(2),
        .fwc-page-item:nth-last-child(2) {
          background-color: transparent;
          border-radius: initial;
          border: 3px solid var(--color-gray-five);
        }
        @media screen and (max-width: 600px) {
          span.fwc-ellipsis {
            display: none;
          }
        }
        .fwc-page-item {
          flex: 0 0 auto;
          width: calc(4.75 * var(--fwcu));
          height: calc(4.75 * var(--fwcu));
          padding: var(--fwcu);
          margin-right: var(--fwcu);
          border-radius: 50%;
          text-decoration: none;
          font-size: var(--default-font-size);
        }
        .fwc-page-num {
          background: var(--color-gray-five);
          text-decoration: none;
          font-weight: 700;
          border: none;
        }
        .fwc-page-num.active {
          background: var(--color-primary);
        }
        @media only screen and (hover: hover) {
          .fwc-page-num:hover {
            background: var(--color-primary);
          }
          .fwc-page-num:hover > span {
            color: var(--color-white);
          }
        }

        .fwc-page-num > span {
          color: var(--color-primary);
        }
        .fwc-page-num.active > span {
          color: var(--color-white);
        }

        .fwc-page-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          text-decoration: none;
          background: none;
          border: none;
        }
        .fwc-page-icon svg {
          fill: var(--color-primary);
        }
        .fwc-prev {
          margin-right: var(--fwcu);
          margin-left: 0;
          transform: rotate(90deg);
        }
        .fwc-next {
          transform: rotate(-90deg);
        }

        .fwc-ellipsis {
          display: flex;
          justify-content: center;
        }
        .fwc-ellipsis span {
          align-self: flex-end;
        }
        .hide {
          display: none;
          visibility: hidden;
        }
      `,
    ];
  }

  static get properties() {
    return {
      alignment: { type: String }, // left | center | right
      model: { type: Object },
      labelAriaNav: { attribute: "label-aria-nav", type: String },
      labelAriaNext: { attribute: "label-aria-next", type: String },
      labelAriaPrev: { attribute: "label-aria-prev", type: String },
      _pages: { type: Array },
      _showPrevEllipsis: {},
      _showNextEllipsis: {},
      _showNextBtn: {},
      _linkStart: { type: Number },
      _linkEnd: { type: Number },
      _startPage: { type: Number },
      _endPage: { type: Number },
    };
  }

  constructor() {
    super();
    this.alignment = "left";
    this.model = null;
    this.ariaLabels = {
      navLabel: "Pagination",
      nextPageLabel: "Next Page",
      prevPageLabel: "Previous Page",
    };
    this.labelAriaNav = "Pagination";
    this.labelAriaNext = "Next Page";
    this.labelAriaPrev = "Previous Page";
    this._viewMax = 5;
    this._viewMaxMobile = 3;
    this._startPage = 0;
    this._endPage = 5;
    this._pages = [];
    this._showNextBtn = true;
    this._showNextEllipsis = false;
    this._showPrevEllipsis = false;
    this._linkStart = 0;
    this._linkEnd = 0;
    this._totalPages = 0;
    this._mobileFlag = false;
    this._resizeObserver = null;
  }

  firstUpdated() {
    // 508 keyboard, set active page
    this.shadowRoot.addEventListener("keydown", function (e) {
      const isArrowBtn = e.target.classList.contains("fwc-next") || e.target.classList.contains("fwc-prev");
      if (e.key === "Enter" && !isArrowBtn) {
        // wait for dom to update
        setTimeout(() => {
          const host = this.getRootNode().host;
          host._setFocusOnCurrentElement();
        }, 100);
      }
    });

    this._screenResize();
    this._initPager();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);
    if (name === "model" && oldVal) {
      this._initPager();
    }
  }

  disconnectedCallback() {
    this._resizeObserver.disconnect();
    super.disconnectedCallback();
  }

  _screenResize() {
    this._resizeObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect.width <= 600) {
        this._mobileFlag = true;
      } else {
        this._mobileFlag = false;
      }
      this._initPager();
    });
    this._resizeObserver.observe(document.querySelector("html"));
  }

  _initPager() {
    this._totalPages = this.model.totalPages;
    this._linkStart = this._startPage;
    this._linkEnd = this._totalPages > this._endPage ? this._endPage : this._totalPages;
    this._pages = [];

    for (let i = 0; i < this._totalPages; i++) {
      this._pages.push({ id: i + 1 });
    }

    this._slicer(this.model.activePage, false);
    this._updateNavButtons();
  }

  _thisPage(page) {
    this._slicer(page.id, true, true);
  }

  _prevPage() {
    if (this.model.activePage) {
      this._slicer(this.model.activePage - 1, true, false);
      // 508, reset focus to first item when prev arrow is hidden
      if (this.model.activePage === 1) {
        const btns = this.shadowRoot.querySelectorAll(".fwc-page-num");
        const first = btns[1];
        first.focus();
      }
    }
  }

  _nextPage() {
    if (this.model.activePage) {
      this._slicer(this.model.activePage + 1, true, false);
      // 508, reset focus to last item when next arrow is hidden
      if (this.model.activePage === this._pages.length) {
        const btns = this.shadowRoot.querySelectorAll(".fwc-page-num");
        const last = btns[btns.length - 2];
        last.focus();
      }
    }
  }

  _getNextEllipsisStatus() {
    return this._linkEnd < this.model.totalPages;
  }

  _getPrevEllipsisStatus(page) {
    if (!this._mobileFlag) {
      return page > 3 && this.model.totalPages > 5;
    }
    return page > 2 && this.model.totalPages > 5;
  }

  _getNextBtnStatus(page) {
    return page === this._pages.length ? false : true;
  }

  _updateNavButtons() {
    this._showNextBtn = this._getNextBtnStatus(this.model.activePage);
    this._showNextEllipsis = this._getNextEllipsisStatus();
    this._showPrevEllipsis = this._getPrevEllipsisStatus(this.model.activePage);
  }

  _slicer(next, emit, focus) {
    let half = Math.ceil(this._viewMax / 2);
    let pen = this._pages.length - 1;

    if (this._mobileFlag) {
      if (next >= 3) {
        this._linkStart = next - 2;
        this._linkEnd = next + 1;

        if (next >= this._pages.length) {
          this._linkStart = next - 3;
          this._linkEnd = next + 1;
        }
      } else {
        this._linkStart = 0;
        this._linkEnd = 3;
      }
    } else {
      // Keeps active page centered when half point is reached
      if (next >= half && next < pen) {
        this._linkStart = next - half;
        this._linkEnd = next + (half - 1);
      }
      // last
      if (next >= pen) {
        // console.log('this._pages.length', this._pages.length)
        // console.log('this._viewMax', this._viewMax)
        this._linkStart = this._pages.length - this._viewMax;
        this._linkEnd = this._pages.length;
      }
      //first
      if (next < this._viewMax && next < half) {
        this._linkStart = 0;
        this._linkEnd = this._viewMax;
      }
    }

    this.model.activePage = next;
    // trigger update on model
    this.model = { ...this.model };

    if (emit) {
      this._emitEvent(this.model);
    }
    if (focus) {
      setTimeout(() => {
        this._setFocusOnCurrentElement();
      }, 0);
    }
    this._updateNavButtons();
  }

  _setFocusOnCurrentElement() {
    const page = this.shadowRoot.querySelector(".fwc-page-num.active");
    page.focus();
  }

  _emitEvent(model) {
    const event = new CustomEvent("fwc-pagination-update", {
      detail: model,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  render() {
    return html`
      <nav class="fwc-pagination fwc-page-align-${this.alignment}" id="er-pagination-testing" role="navigation" aria-label="${this.labelAriaNav}">
        <button class="fwc-page-icon fwc-page-item fwc-prev" @click="${this._prevPage}" style="${this.model.activePage <= 1 ? "visibility: hidden" : "visibility: visible"}" aria-hidden="${this.model.activePage <= 1 ? true : false}" aria-label="${this.labelAriaPrev}">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 50 50" focusable="false">
            <polygon points="2.719 39.66 0 36.941 23.641 13.3 25 12 26.359 13.3 50 36.941 47.281 39.66 25 17.378" transform="matrix(1 0 0 -1 0 51.66)" />
          </svg>
        </button>

        <button aria-label="go to page ${this._pages.length ? this._pages[0].id : ""}" class="fwc-page-num fwc-page-item ${this._showPrevEllipsis ? "" : "hide"}" @click="${() => this._thisPage(this._pages[0])}">
          <span>${this._pages.length ? this._pages[0].id : ""}</span>
        </button>

        <span class="fwc-ellipsis fwc-page-item ${this._showPrevEllipsis ? "" : "hide"}">...</span>

        ${this._pages
          .filter((page) => this._linkStart < page.id && this._linkEnd >= page.id)
          .map(
            (page) => html`
              <button aria-label="go to page ${page.id}" class="fwc-page-num fwc-page-item ${page.id === this.model.activePage ? "active" : ""}" @click="${() => this._thisPage(page)}" aria-current=${page.id === this.model.activePage ? "page" : false}>
                <span>${page.id}</span>
              </button>
            `
          )}

        <span class="fwc-ellipsis fwc-page-item ${this._showNextEllipsis ? "" : "hide"}">...</span>

        <button aria-label="go to page ${this._pages.length ? this._pages[this._pages.length - 1].id : ""}" class="fwc-page-num fwc-page-item ${this._showNextEllipsis ? "" : "hide"}" @click="${() => this._thisPage(this._pages[this._pages.length - 1])}">
          <span>${this._pages.length ? this._pages[this._pages.length - 1].id : ""}</span>
        </button>

        <button class="fwc-page-icon fwc-page-item fwc-next" @click="${this._nextPage}" style="${this._showNextBtn && this._pages.length > 0 ? "visibility: visible" : "visibility: hidden"}" aria-hidden="${this._showNextBtn && this._pages.length > 0 ? false : true}" aria-label="${this.labelAriaNext}">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 50 50">
            <polygon points="2.719 39.66 0 36.941 23.641 13.3 25 12 26.359 13.3 50 36.941 47.281 39.66 25 17.378" transform="matrix(1 0 0 -1 0 51.66)" />
          </svg>
        </button>
      </nav>
    `;
  }
}

class FwcPagination extends FwcBase {
  constructor() {
    super("fwc-pagination", FwcPaginationComponent);
  }
}

class FwcPanelComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      sharedMobileViewStyles,
      css`
        .fwc-panel-container {
          position: relative;
          height: 100%;
        }
        .fwc-panel-wrapper {
          overflow: hidden;
          opacity: 0;
          height: 0;
          visibility: hidden;
          transition: opacity 200ms ease;
          width: 0;
        }
        .fwc-panel-wrapper.open {
          opacity: 1;
          visibility: visible;
          height: auto;
          position: absolute;
          top: calc(1.25 * var(--fwcu));
          border: 1px solid var(--color-gray-four);
          border-radius: var(--border-radius);
          background-color: var(--color-white);
          width: auto;
          z-index: 1;
        }
        .fwc-panel-footer {
          position: relative;
          padding: calc(2.5 * var(--fwcu));
          background-color: var(--color-white);
          border-top: 1px solid var(--color-gray-six);
          box-shadow: var(--box-shadow);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .fwc-panel-footer.hide {
          display: none;
        }
        .fwc-panel-apply-cancel-wrapper {
          display: flex;
          justify-content: space-between;
          width: auto;
        }
        .fwc-panel-restart svg {
          width: calc(1.5 * var(--fwcu));
          height: calc(1.5 * var(--fwcu));
          margin-right: calc(0.5 * var(--fwcu));
          fill: var(--color-gray-three);
          transform: rotateZ(90deg);
        }
        .fwc-panel-content {
          padding: calc(2.5 * var(--fwcu));
          height: auto;
          max-height: calc(35.5 * var(--fwcu));
          overflow-y: auto;
          overflow-x: hidden;
        }
        .hide-sm {
          display: block;
        }
        .hide-lg {
          display: none;
        }
        .fwc-panel-reset-wrapper {
          display: flex;
          align-items: center;
        }

        @media screen and (max-width: 600px) {
          .fwc-panel-content {
            padding: calc(2 * var(--fwcu)) calc(4 * var(--fwcu)); /* Match mobile grid padding at 32px */
            padding-bottom: calc(12 * var(--fwcu));
            height: 100%;
            max-height: none;
          }
          .fwc-panel-footer {
            display: none;
          }
          .hide-sm {
            display: none;
          }
          .hide-lg {
            display: block;
          }
          .fwc-panel-wrapper.open {
            position: relative;
            top: 0;
            border: none;
            width: auto;
            height: 100%;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      open: { type: Boolean },
      text: { type: String },
      labelMobileHeader: { attribute: "label-mobile-header", type: String },
      labelResetBtn: { attribute: "label-reset-button", type: String },
      labelCancelBtn: { attribute: "label-cancel-button", type: String },
      labelApplyBtn: { attribute: "label-apply-button", type: String },
      hideApplyBtn: { attribute: "hide-apply-button", type: Boolean },
      hideCancelBtn: { attribute: "hide-cancel-button", type: Boolean },
      hideResetBtn: { attribute: "hide-reset-button", type: Boolean },
      reduceIndex: { attribute: "reduce-index", type: Boolean },
      hideFooter: {},
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this.labelMobileHeader = "";
    this.mobileView = new MobileView();
    this.mobileView.build();
    this.open = false;
    this.labelApplyBtn = "Okay";
    this.labelCancelBtn = "Cancel";
    this.labelResetBtn = "Reset";
    this.reduceIndex = false;
    this.returnEl = null;
    this.hideFooter = false;
  }

  firstUpdated() {
    const panel = this.shadowRoot.querySelector("#Panel");

    panel.addEventListener("transitionend", (e) => {
      // set the focus trap inside the panel after open
      if (this.open && e.propertyName === "opacity") {
        const ignoredFocusTrapSelectors = window.innerWidth <= breakpoints.sm ? [".fwc-link-icon.hide-sm", ".fwc-panel-footer button"] : [".fwc-link-icon.hide-lg", ".mobile-footer button"];
        setTimeout(() => {
          focusTrapCustomTabs(this, ignoredFocusTrapSelectors);
        }, 0);
      }
      // set the focus back to the open button on panel close
      if (!this.open && e.propertyName === "opacity") {
        removeFocusTrapListener(this);
        this._returnFocus();
      }
    });

    // listen for Escape keydown
    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        this._closePanel();
      }
    });

    this.hideFooter = this._hideFooter();
  }

  _hideFooter() {
    return this.hideResetBtn && this.hideApplyBtn && this.hideCancelBtn;
  }

  setReturnEl(el) {
    this.returnEl = el;
  }

  togglePanel() {
    this.open = !this.open;
    this._dispatchToggled();
    this._toggleBodyScrollClass();
    setTimeout(() => {
      this._setPanelPosition();
    }, 0);
  }

  _closePanel() {
    this.open = false;
    this._toggleBodyScrollClass();
    this._returnFocus();
    this._clearPanelPosition();
  }

  _toggleBodyScrollClass() {
    if (this.open && window.innerWidth <= breakpoints.sm) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }

  _returnFocus() {
    if (!this.open && this.returnEl) {
      setTimeout(() => {
        this.returnEl.focus();
      }, 0);
    }
  }

  // emit an event on hitting the apply button
  _dispatchApply() {
    const applyEvent = new CustomEvent("fwc-panel-apply", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(applyEvent);
    this._closePanel();
  }

  // emit an event on hitting the cancel button
  _dispatchCancel() {
    const cancelEvent = new CustomEvent("fwc-panel-cancel", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(cancelEvent);
    this._closePanel();
  }

  // emit an event on hitting the reset button
  _dispatchReset() {
    const resetEvent = new CustomEvent("fwc-panel-reset", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(resetEvent);
  }

  _dispatchToggled() {
    const resetEvent = new CustomEvent("fwc-panel-toggled", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(resetEvent);
  }

  _setPanelPosition() {
    const panel = this.shadowRoot.querySelector("#Panel");
    const viewportWidth = document.documentElement.clientWidth;

    if (this.open) {
      if (panel.getBoundingClientRect().right < viewportWidth) {
        // if rightmost right-side is can fit within viewport
        panel.style.left = 0;
      } else if (panel.getBoundingClientRect().right > viewportWidth) {
        // if rightmost right-side is beyond viewport
        panel.style.right = 0;
      }
    } else {
      this._clearPanelPosition();
    }
  }

  _clearPanelPosition() {
    const panel = this.shadowRoot.querySelector("#Panel");
    panel.style.removeProperty("left");
    panel.style.removeProperty("right");
  }

  /** Stores svg htmlement for Restart icon */
  _getRestartSVG() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" focusable="false">
        <path d="M25,0 L25,4.16666667 C36.531575,4.16666667 45.8333333,13.468425 45.8333333,25 C45.8333333,36.531575 36.531575,45.8333333 25,45.8333333 C13.468425,45.8333333 4.16666667,36.531575 4.16666667,25 C4.16666667,18.1966146 7.47070417,12.1907563 12.5,8.3984375 L12.5,18.75 L16.6666667,18.75 L16.6666667,2.08333333 L0,2.08333333 L0,6.25 L8.52864583,6.25 C3.3203125,10.8317062 0,17.5374354 0,25 C0,38.7858083 11.2141938,50 25,50 C38.7858083,50 50,38.7858083 50,25 C50,11.2141938 38.7858083,0 25,0 Z" />
      </svg>
    `;
  }

  _cancelButton(id, mobile) {
    if (!this.hideCancelBtn) {
      return html`<button id=${id ? id : "PanelCancelBtn"} tabindex="1" class=${mobile ? "fwc-link" : "fwc-btn fwc-btn-secondary"} @click="${this._dispatchCancel}" aria-label="${this.labelCancelBtn}">${this.labelCancelBtn}</button>`;
    }
  }

  _applyButton(id) {
    if (!this.hideApplyBtn) {
      return html`<button id=${id ? id : "PanelApplyBtn"} tabindex="1" class="fwc-btn fwc-btn-primary" @click="${this._dispatchApply}" aria-label="${this.labelApplyBtn}">${this.labelApplyBtn}</button>`;
    }
  }

  _resetButton(hide) {
    if (!this.hideResetBtn) {
      return html`
        <a href="javascript:void(0);" tabindex="1" class="fwc-link-icon left ${hide}" @click="${this._dispatchReset}" role="button" aria-label="${this.labelResetBtn}">
          <span class="fwc-panel-restart">${this._getRestartSVG()}</span>
          <span>${this.labelResetBtn}</span>
        </a>
      `;
    }
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/buttons-links.css" />
      <slot name="fwc-panel-action" id="FwcActionSlot"></slot>
      <mobile-view mobile-header-text="${this.labelMobileHeader}" ?open=${this.open} ?decrease-index-z=${this.reduceIndex} ?hide-footer=${this.hideFooter} config=${JSON.stringify(this.config)}>
        <!-- mobile-view is a shared 'modal' overlay. Body slot is for both desktop and mobile. -->
        <div class="fwc-panel-container" slot="body">
          <div id="Panel" aria-expanded="${this.open}" class="fwc-panel-wrapper ${this.open ? "open" : ""}">
            <div class="fwc-panel-content">
              <slot name="fwc-panel-content" role="region"></slot>
              ${this._resetButton("hide-lg")}
            </div>

            <div class="fwc-panel-footer ${this.hideFooter ? "hide" : ""}">
              ${this._resetButton("hide-sm")}
              <div class="fwc-panel-apply-cancel-wrapper">${this._cancelButton()} ${this._applyButton()}</div>
            </div>
          </div>
        </div>

        <!-- Mobile specific footer to allow for fixed design/position in mobile-view -->
        <div class="mobile-view-footer-slot" slot="mobile-actions">${this._cancelButton("#MobilePanelCancelBtn", true)} ${this._applyButton("#MobilePanelApplyBtn")}</div>
      </mobile-view>
    `;
  }
}

class FwcPanel extends FwcBase {
  constructor() {
    super("fwc-panel", FwcPanelComponent);
  }
}

class FwcPanelNavComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          position: relative;
          width: max-content;
        }
        .container {
          position: absolute;
          margin-top: var(--fwcu);
          width: max-content; /* allows auto width outside of parent */
        }
        .container.open {
          min-width: 200px;
        }
        .container.right {
          right: 0;
        }
        .container.left {
          left: 0;
        }
        .container.mobile {
          left: calc(4 * var(--fwcu));
          right: calc(4 * var(--fwcu));
          width: auto;
        }

        .wrapper {
          position: relative;
          opacity: 0;
          height: 0;
          width: 0;
          z-index: 1;
          visibility: hidden;
          border: 1px solid var(--color-gray-four);
          box-shadow: var(--box-shadow);
          border-radius: var(--border-radius);
          background-color: var(--color-white);
          transition: opacity 200ms ease;
        }
        .container.open .wrapper {
          opacity: 1;
          visibility: visible;
          height: auto;
          width: auto;
        }
        .container.open .wrapper:focus {
          outline: var(--fwc-focus);
        }
        .content {
          padding: calc(2.5 * var(--fwcu));
          height: auto;
          max-height: calc(35.5 * var(--fwcu));
          overflow-y: auto;
          overflow-x: hidden;
        }
      `,
    ];
  }

  static get properties() {
    return {
      _open: {},
      _position: { type: String },
    };
  }

  constructor() {
    super();
    this.controlElem = null;
    this.controlListener = false;
    this._focusReturn = true;
    this._open = false;
    this._panel = null;
    this._position = "center";
    this._positionOrg = "center";
    this._container = null;
    this.returnElem = null;
    this._togglePanel = this._togglePanel.bind(this);
    this._handlePanelClickOut = this._handlePanelClickOut.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("click", this._handlePanelClickOut);
  }
  disconnectedCallback() {
    document.removeEventListener("click", this._handlePanelClickOut);
    super.disconnectedCallback();
  }

  firstUpdated() {
    this._panel = this.shadowRoot.getElementById("Panel");
    this._container = this.shadowRoot.getElementById("Container");
    this._positionOrg = this._position;

    this._panel.addEventListener("transitionend", (e) => {
      if (!this._open && e.propertyName === "opacity") {
        this._returnFocus();
        this._clearPanelPosition();
      }
    });

    // listen for Escape keydown
    this.addEventListener("keydown", (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        // other components listen for esacpe - stop this from bubbling up
        e.stopPropagation();
        this._closePanel();
      }
    });
  }

  setReturnElement(elem) {
    this.returnElem = elem;
  }

  togglePanel() {
    this._togglePanel();
  }

  async setControlElement(elem) {
    const control = await this._setControlElement();
    if (control) {
      this._initActionControl(elem);
    } else {
      console.error("Control element could not be set");
    }
  }

  _setControlElement() {
    return new Promise((resolve, reject) => {
      let counter = 0;
      const setControl = () => {
        counter++;
        // wait for slotchange event to finish, then update
        if (this.controlListener) {
          resolve(true);
        } else if (counter >= 10) {
          reject(false);
        } else {
          setTimeout(() => setControl(), 0);
        }
      };
      setControl();
    });
  }

  _setActiveControl() {
    // use classes in buttons-links.css
    this.controlElem.classList.toggle("fwc-active");
    this.controlElem.setAttribute("aria-expanded", this._open);
  }

  _togglePanel() {
    this._open = !this._open;
    this._setActiveControl();
    this._dispatchToggled();
    if (this._open) {
      setTimeout(() => {
        this._panel.focus();
        this._setPanelPosition();
      }, 0);
    }
  }

  _closePanel() {
    this._open = false;
    this._returnFocus();
  }

  _returnFocus() {
    if (!this._open && this._focusReturn) {
      // return focus to controlElem by default;
      const elem = this.returnElem ? this.returnElem : this.controlElem;
      setTimeout(() => {
        elem.focus();
      }, 0);
    }
    this._focusReturn = true;
  }

  // automatically assign first child of action slot to be control element
  _onActionSlotChange() {
    const bodySlot = this.shadowRoot.getElementById("NavItemSlot");
    const bodySlotNodes = bodySlot.assignedNodes();
    // https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
    if (bodySlotNodes[0].nodeType === 1) {
      for (let node of bodySlotNodes[0].childNodes) {
        if (node.nodeType === 1) {
          this._initActionControl(node);
          break;
        }
      }
    }
  }

  _handleFocusOut() {
    this._togglePanel();
  }

  _handlePanelClickOut(e) {
    if (!this.contains(e.target) && this._open) {
      this._focusReturn = false;
      this._togglePanel();
    }
  }

  _initActionControl(node) {
    if (this.controlElem && this.controlListener) {
      this.controlElem.removeEventListener("click", this._togglePanel);
    }
    this.controlElem = node;
    this.controlElem.setAttribute("aria-expanded", this._open);
    this.controlElem.addEventListener("click", this._togglePanel);
    this.controlListener = true;
  }

  _dispatchToggled() {
    const resetEvent = new CustomEvent("fwc-panel-toggled", {
      detail: this,
      bubbles: true,
    });
    this.dispatchEvent(resetEvent);
  }

  _getControlCenter() {
    const { _container } = this;
    return -(_container.getBoundingClientRect().width / 2 - this.getBoundingClientRect().width / 2) + "px";
  }

  _setPanelPosition() {
    const { _container } = this;
    const { sm } = breakpoints;

    if (window.innerWidth <= sm) {
      this._position = "mobile";
      this.style.position = "static";
      return;
    } else {
      this.style.position = "relative";
      this._clearPanelPosition();
    }

    // center panel based on width of self and component
    if (this._open && this._position === "center") {
      _container.style.left = this._getControlCenter();
    }

    if (this._open) {
      if (_container.getBoundingClientRect().left < 0) {
        this._clearPanelPosition();
        this._position = "left";
      } else if (_container.getBoundingClientRect().right > window.innerWidth) {
        this._clearPanelPosition();
        this._position = "right";
      }
    }
  }

  _clearPanelPosition() {
    const { _container } = this;
    _container.style.removeProperty("left");
    _container.style.removeProperty("right");
    this._position = this._positionOrg;
  }

  _hiddenCloseTabIndex() {
    if (this._open) {
      return html`<span class="tab-close sr-only" aria-hidden="true" @focus=${this._handleFocusOut} tabindex="0"></span>`;
    }
  }

  render() {
    return html`
      <slot id="NavItemSlot" name="nav-item" @slotchange=${this._onActionSlotChange}></slot>
      ${this._hiddenCloseTabIndex()}
      <div id="Container" class="container ${this._open ? "open" : ""} ${this._position}">
        <div id="Panel" class="wrapper" tabindex="0">
          <div class="content">
            <slot name="nav-content" role="group"></slot>
          </div>
        </div>
      </div>
      ${this._hiddenCloseTabIndex()}
    `;
  }
}

class FwcPanelNav extends FwcBase {
  constructor() {
    super("fwc-panel-nav", FwcPanelNavComponent);
  }
}

class FwcProgressLinearComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        ol {
          display: flex;
          flex-direction: column;
          margin: 0;
          padding: 0;
        }
        li {
          list-style: none;
          display: flex;
          align-items: stretch;
        }
        li:last-child .connector {
          display: none;
        }
        li:last-child dl {
          border: none;
          margin-bottom: 0px;
        }
        li strong,
        li dt,
        li p,
        li .fwc-prog-linear-body-min {
          color: var(--color-gray-two);
        }
        li.fwc-prog-linear-next strong,
        li.fwc-prog-linear-next p,
        li.fwc-prog-linear-next .fwc-prog-linear-body-min {
          color: var(--color-black);
        }
        .check {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1 0 100%;
          height: calc(2.5 * var(--fwcu));
          width: calc(2.5 * var(--fwcu));
          margin-top: calc(0.25 * var(--fwcu));
          border-radius: 100%;
          border: 2px solid var(--color-gray-four);
        }
        .check svg {
          width: calc(1.5 * var(--fwcu));
          height: calc(1.5 * var(--fwcu));
          display: none;
        }
        .check.check-complete {
          border: 1px solid var(--color-primary);
          background: var(--color-primary);
        }
        .check.check-complete svg {
          display: block;
        }
        .check.check-in-progress {
          border: calc(0.25 * var(--fwcu)) solid var(--color-primary);
        }
        .connector {
          width: calc(0.25 * var(--fwcu));
          height: calc(100% - calc(4.5 * var(--fwcu)));
          margin: var(--fwcu) 0 var(--fwcu) calc(1.25 * var(--fwcu));
          padding: var(--fwcu) 0;
          background: var(--color-gray-four);
        }
        .connector.connector-complete {
          background: var(--color-primary);
        }
        .connector-non-linear {
          visibility: hidden;
          opacity: 0;
        }
        dl {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          width: 100%;
          margin: 0 0 calc(1.5 * var(--fwcu)) calc(3 * var(--fwcu));
          font-size: var(--default-font-size);
          border-bottom: 1px solid var(--color-gray-four, #cccccc);
        }
        dt {
          width: 100%;
          color: var(--color-gray-one);
        }
        dt p {
          margin: 0px 0px calc(1.5 * var(--fwcu)) 0px;
        }
        dd {
          margin-left: 0;
        }
        dd strong {
          white-space: nowrap;
          text-transform: uppercase;
          color: #fff;
          font-size: calc(1.25 * var(--fwcu));
          border-radius: calc(0.25 * var(--fwcu));
          line-height: 1;
          padding: calc(0.25 * var(--fwcu)) var(--fwcu);
          letter-spacing: 1px;
        }
        .fwc-prog-linear-header-flex {
          width: 100%;
          padding-right: calc(4 * var(--fwcu));
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          margin-bottom: var(--fwcu);
        }
        .fwc-h4.title-complete {
          font-weight: 500;
          color: var(--color-gray-two);
        }
        .fwc-h4.title-incomplete {
          font-weight: 600;
          color: var(--color-gray-one);
        }
        .fwc-prog-linear-date {
          margin-top: 0px;
          margin-bottom: calc(1.5 * var(--fwcu));
        }
        .fwc-prog-linear-body {
          margin-top: 0px;
          padding-right: calc(4 * var(--fwcu));
          margin-bottom: calc(4 * var(--fwcu));
        }
        .fwc-prog-linear dt {
          max-width: 70%;
        }
        .fwc-prog-linear dl {
          margin-bottom: calc(4 * var(--fwcu));
        }
        .fwc-prog-linear:last-child dl {
          margin-bottom: 0px;
        }
        .fwc-prog-linear dd:nth-child(3) {
          width: 100%;
        }
        .fwc-prog-linear-min dt {
          max-width: 70%;
        }
        .fwc-prog-linear-min dl p {
          width: 100%;
        }
        .fwc-prog-linear-min .fwc-prog-linear-body-min {
          margin: calc(var(--fwcu)) 0px calc(1.5 * var(--fwcu)) 0px;
        }
        .fwc-prog-linear-min dl {
          margin-bottom: calc(1.5 * var(--fwcu));
          padding-right: calc(4 * var(--fwcu));
        }
        .fwc-prog-linear-date-min,
        .fwc-prog-linear-min dt p {
          margin: 0px;
        }
        .non-linear .check.check-in-progress {
          border-color: var(--color-gray-four);
        }
        .non-linear-next .fwc-prog-linear-date,
        .non-linear-next .fwc-prog-linear-body,
        .non-linear-next .fwc-prog-linear-date-min,
        .non-linear-next .fwc-prog-linear-body-min {
          font-weight: bold;
        }
        @media screen and (max-width: 600px) {
          .fwc-prog-linear-header-flex {
            flex-direction: column;
            margin-bottom: calc(1.5 * var(--fwcu));
          }
          .fwc-prog-linear-header-flex div:nth-child(2) {
            margin-bottom: calc(1.75 * var(--fwcu));
          }
          .fwc-h4 {
            margin-bottom: calc(3 * var(--fwcu));
          }
          p.fwc-prog-linear-date {
            margin-bottom: 0px;
          }
          .fwc-prog-linear dl,
          .fwc-prog-linear .fwc-prog-linear-body {
            margin-bottom: calc(3 * var(--fwcu));
          }
          .fwc-prog-linear dt {
            max-width: 100%;
          }
          .fwc-prog-linear-min dt {
            flex: 2;
            padding-right: calc(2 * var(--fwcu));
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      config: { type: Object },
      data: { type: Array },
      steps: { type: Array },
      nonLinear: { attribute: "non-linear", type: Boolean },
      minimal: { attribute: "minimal", type: Boolean },
    };
  }

  constructor() {
    super();
    this.config = fwcConfig;
    this.steps = [];
    this.data = null;
    this.nonLinear = false;
    this.minimal = false;
  }

  firstUpdated() {
    if (this.data && !this.minimal) {
      this._buildSteps();
    } else if (this.data && this.minimal) {
      this._buildStepsMinimal();
    }
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "data" && !this.minimal) {
        this._buildSteps();
      } else if (propName === "data" && this.minimal) {
        this._buildStepsMinimal();
      }
    });
  }

  _buildSteps() {
    const steps = html`${this.data.map((step) => {
      return html`
        <li class="fwc-prog-linear ${step.state === "next" || (this.nonLinear === true && step.state !== "complete") ? "fwc-prog-linear-next" : ""} " aria-label="${step.state !== "complete" ? "incomplete" : "complete"}">
          <div>
            <div class="check ${step.state !== "complete" ? "" : "check-complete"} ${step.state === "next" && this.nonLinear === false ? "check-in-progress" : ""}">${this._drawCheckMark()}</div>
            <div class="connector ${step.state !== "complete" ? "" : "connector-complete"} ${this.nonLinear === true ? "connector-non-linear" : ""}"></div>
          </div>
          <dl>
            <div class="fwc-prog-linear-header-flex">
              <dt><div class="fwc-h4 ${ step.state === "complete" ? "title-complete" : "title-incomplete" }">${step.title}</div></dt>
              <dd>${Object.prototype.hasOwnProperty.call(step, "badge") ? html`<span class="fwc-badge ${step.badge.color}"> ${step.badge.label} </span>` : html``}</dd>
            </div>
            <dd><p class="fwc-prog-linear-date">${step.date}</p></dd>
            <dd><p class="fwc-prog-linear-body">${step.body}</p></dd>
          </dl>
        </li>
      `;
    })}`;
    return (this.steps = steps);
  }

  _buildStepsMinimal() {
    const steps = html`${this.data.map((step) => {
      return html`
        <li class="fwc-prog-linear-min ${step.state === "next" || (this.nonLinear === true && step.state !== "complete") ? "fwc-prog-linear-next" : ""}" aria-label="${step.state !== "complete" ? "incomplete" : "complete"}">
          <div>
            <div class="check ${step.state !== "complete" ? "" : "check-complete"} ${step.state === "next" && this.nonLinear === false ? "check-in-progress" : ""}">${this._drawCheckMark()}</div>
            <div class="connector ${step.state !== "complete" ? "" : "connector-complete"} ${this.nonLinear === true ? "connector-non-linear" : ""}"></div>
          </div>
          <dl>
            <dt>
              <strong>${step.title}</strong>
            </dt>
            ${Object.prototype.hasOwnProperty.call(step, "badge") ? html`<dd><span class="fwc-badge ${step.badge.color}"> ${step.badge.label} </span></dd>` : html``} ${Object.prototype.hasOwnProperty.call(step, "date") ? html`<p class="fwc-prog-linear-date-min">${step.date}</p>` : html``}
            <p class="fwc-prog-linear-body-min">${step.body}</p>
          </dl>
        </li>
      `;
    })}`;
    return (this.steps = steps);
  }

  _drawCheckMark() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" focusable="false">
        <polygon fill="#ffffff" points="40.771 7 19.443 26.724 9.229 17.197 0 26.109 18.365 43.545 19.443 44.5 20.519 43.545 50 15.912" />
      </svg>
    `;
  }

  render() {
    return html`
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/components/fwc-badges.css" />
      <link rel="stylesheet" href="${this.config.assetsPath}fwc-assets/styles/typography.css" />
      <ol>
        ${this.minimal ? this._buildStepsMinimal() : this._buildSteps()}
      </ol>
    `;
  }
}

class FwcProgressLinear extends FwcBase {
  constructor() {
    super("fwc-progress-linear", FwcProgressLinearComponent);
  }
}

class FwcProgressStandardComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          display: inline;
          color: var(--color-gray-two);
        }
        ol {
          margin: 0;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          font-size: var(--default-font-size);
          font-weight: bold;
        }
        li {
          flex: 0 0 33%;
          display: flex;
          align-items: center;
          position: relative;
        }
        li:last-child {
          flex: 0 0 auto;
        }
        div {
          height: calc(2.5 * var(--fwcu));
          width: calc(2.5 * var(--fwcu));
          background: white;
          border: 2px solid var(--color-gray-four);
          border-radius: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          flex: 0 0 auto;
        }
        div.complete {
          background: var(--color-primary);
          border-color: var(--color-primary);
        }
        li[aria-label="complete"] + 
        li[aria-label="incomplete"] > div {
          border: 2px solid var(--color-primary);
        }
        li[aria-label="incomplete"]:first-of-type > div{
          border: 2px solid var(--color-primary);
        }
        svg {
          width: calc(1.5 * var(--fwcu));
          height: calc(1.5 * var(--fwcu));
          display: none;
        }
        div.complete svg {
          display: inline;
        }
        span {
          height: 2px;
          display: block;
          margin-left: calc(0.5 * var(--fwcu));
          margin-right: calc(0.5 * var(--fwcu));
          width: calc(100% - calc(3.5 * var(--fwcu)));
          background: var(--color-gray-four);
        }
        span.complete {
          background: var(--color-primary);
        }
        p {
          position: absolute;
          text-align: center;
          width: calc(15.5 * var(--fwcu));
          left: calc(-6.75 * var(--fwcu)); /*  15.5/2-1 */
          top: 100%;
          margin: 0;
          padding-top: var(--fwcu);
        }
        li[aria-label="complete"] + 
        li[aria-label="incomplete"] > p,
        li[aria-label="incomplete"]:first-of-type >p {
          color: var(--color-gray-one);
        }
        /* vertical */
        ol.vert {
          flex-direction: column;
          align-items: flex-start;
          height: 100%;
        }
        ol.vert span {
          width: calc(0.25 * var(--fwcu));
          height: calc(100% - calc(3.75 * var(--fwcu)));
          margin: calc(0.5 * var(--fwcu)) 0;
          margin-left: calc(1.25 * var(--fwcu));
          /* box-sizing: content-box; */
        }
        ol.vert li {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-start;
          width: 100%;
        }
        ol.vert p {
          top: 0;
          bottom: calc(50% - calc(1.5 * var(--fwcu))); /* centers border on vertical line, not parent. Takes into account half of check circle height) */
          padding: 0;
          width: calc(100% - calc(3 * var(--fwcu)));
          left: calc(4 * var(--fwcu));
          text-align: left;
          border-bottom: 1px solid var(--color-gray-four, #cccccc);
        }
        ol.vert li:last-child p {
          border-bottom: none;
        }
      `,
    ];
  }

  static get properties() {
    return {
      type: { type: String },
      data: { type: Array },
      steps: { type: Array },
    };
  }

  constructor() {
    super();
    this.type = "horizontal";
    this.steps = [];
    this.data = null;
  }

  firstUpdated() {
    if (this.data) {
      this._buildSteps();
    }
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "data") {
        this._buildSteps();
      }
    });
  }

  _buildSteps() {
    const steps = html`${this.data.map((step) => {
      return html`
        <li aria-label="${step.complete ? "complete" : "incomplete"}">
          <div class=${step.complete ? "complete" : ""}>${this._drawCheckMark()}</div>
          <span class=${step.complete ? "complete" : ""}></span>
          <p>${step.label}</p>
        </li>
      `;
    })}`;
    this.steps = steps;
  }

  _drawCheckMark() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" focusable="false">
        <polygon fill="#ffffff" points="40.771 7 19.443 26.724 9.229 17.197 0 26.109 18.365 43.545 19.443 44.5 20.519 43.545 50 15.912" />
      </svg>
    `;
  }

  render() {
    return html`
      <ol class="${this.type === "vertical" ? "vert" : ""}">
        ${this.steps}
      </ol>
    `;
  }
}

class FwcProgressStandard extends FwcBase {
  constructor() {
    super("fwc-progress-standard", FwcProgressStandardComponent);
  }
}

class FwcScrubberComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .fwc-scrubber-wrapper {
          padding: 0 calc(1 * var(--fwcu)) calc(8 * var(--fwcu)) calc(1 * var(--fwcu));
          display: block;
          width: 100%;
        }
        fieldset {
          padding: 0;
          margin: 0;
          border: 0;
          min-width: 0;
        }
        .fwc-scrubber-slider-wrapper {
          height: calc(4 * var(--fwcu));
          position: relative;
          transition: all 250ms ease;
        }
        .fwc-scrubber-chip {
          height: calc(3 * var(--fwcu));
          width: calc(3 * var(--fwcu));
          border-radius: 50%;
          position: absolute;
          top: 3px;
          padding: 0;
          margin: 0;
          box-shadow: var(--box-shadow);
          border: 1px solid transparent;
          appearance: none;
          margin-left: calc(-2 * var(--fwcu));
          overflow: visible;
          cursor: grab;
          cursor: -webkit-grab;
          background-color: var(--color-gray-four);
        }
        .fwc-scrubber-chip.last-active {
          z-index: 10;
        }
        .fwc-scrubber-chip.active {
          cursor: grabbing;
          cursor: -webkit-grabbing;
          z-index: 10;
        }
        .fwc-scrubber-chip:focus {
          outline: var(--fwc-focus);
        }
        .fwc-scrubber-chip.pass-through {
          pointer-events: none;
        }
        .fwc-scrubber-bar {
          position: absolute;
          border-radius: calc(0.5 * var(--fwcu));
          border-bottom: calc(1.25 * var(--fwcu)) solid var(--color-gray-five);
          top: calc(1.25 * var(--fwcu));
          width: 100%;
        }
        .fwc-scrubber-range {
          position: absolute;
          top: calc(1.25 * var(--fwcu));
          background-color: var(--color-primary);
          border-radius: calc(0.5 * var(--fwcu));
          height: calc(1.25 * var(--fwcu));
        }
        .fwc-scrubber-range-labels {
          display: flex;
          justify-content: space-between;
          font-size: calc(1.75 * var(--fwcu));
          color: var(--color-gray-one);
          margin-top: calc(3 * var(--fwcu));
        }
        .fwc-scrubber-wrapper.hide,
        .fwc-scrubber-wrapper .hide {
          display: none;
        }
        .fwc-arrow-box {
          position: absolute;
          top: calc(-9.25 * var(--fwcu));
          left: calc(-2.5 * var(--fwcu));
          background: var(--color-white);
          border: 1px solid var(--color-primary);
          padding: calc(0.75 * var(--fwcu));
          min-width: calc(8.75 * var(--fwcu));
          height: calc(6.75 * var(--fwcu));
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: calc(2.25 * var(--fwcu));
          color: var(--color-gray-one);
          border-radius: var(--border-radius);
        }
        .fwc-arrow-box:after,
        .fwc-arrow-box:before {
          top: 100%;
          left: 50%;
          border: solid transparent;
          content: " ";
          height: 0;
          width: 0;
          position: absolute;
          pointer-events: none;
        }
        .fwc-arrow-box:after {
          border-color: transparent;
          border-top-color: var(--color-white);
          border-width: calc(1.25 * var(--fwcu));
          margin-left: calc(-1.25 * var(--fwcu));
        }
        .fwc-arrow-box:before {
          border-color: transparent;
          border-top-color: var(--color-primary);
          border-width: calc(1.375 * var(--fwcu));
          margin-left: calc(-1.375 * var(--fwcu));
        }
        svg {
          fill: var(--color-white);
          position: absolute;
          width: 100%;
          top: -5px;
          left: 0px;
        }
      `,
    ];
  }

  static get properties() {
    return {
      isPercent: { attribute: "is-percent", type: Boolean },
      hideHandleLabel: { attribute: "hide-handle-label", type: Boolean },
      range: { type: Array },
      rangeLabel: { attribute: "range-label", type: String },
      leftChipValue: { attribute: "left-chip", type: Number },
      rightChipValue: { attribute: "right-chip", type: Number },
      screenReaderCaption: { attribute: "screen-reader-caption", type: String },
      dataAnalyticsMin: { attribute: "data-analytics-min", type: String },
      dataAnalyticsMax: { attribute: "data-analytics-max", type: String },
      hideRangeLabel: { attribute: "hide-range-label", type: Boolean },
      labelAriaLabelledby: { attribute: "label-aria-labelledby", type: String },
      parentOpened: { attribute: "parent-opened", type: Boolean },
    };
  }

  constructor() {
    super();

    this.SCALE_MIN = 0;
    this.SCALE_MAX = 100;
    this.isPercent = false;

    // the scale values are in relation to %, 0 to 100% of the bar
    // we adjust these based on the custom range in initValues()
    this.minScale = this.SCALE_MIN;
    this.maxScale = this.SCALE_MAX;

    // the adjusted values are used to display the adjusted value for a custom range other than 0 - 100
    // we are not changing the scale, just the numbers that are displayed/output
    this.displayMinValue = this.SCALE_MIN;
    this.displayMaxValue = this.SCALE_MAX;

    // active property true on mousedown and false on mouseup
    this.isLeftChipActive = false;
    this.isRightChipActive = false;
    // total width of slider bar in px
    this.totalWidth = 0;
    // distance between slider bar and left edge of document
    this.offSetLeft = 0;
    //the last chip/handle that was used, used to position chips on top off each other
    this.lastActive = null;

    this.rangeMinimum = 0;
    this.rangeMaximum = 0;
    this.stepSize = 1;
    this.hasSingleChip = false;
    this.hasValidInputs = true;
    this.isScaleHidden = false;
    this.elementRef = "";
    this.barContainerRef = "";
    this.isDragging = false;
    this.rangeLabel = "";
    this.screenReaderCaption = "";
    this.hideHandleLabel = false;
    this.dataAnalyticsMin = "";
    this.hideRangeLabel = false;
    this.dataAnalyticsMax = "";
  }

  firstUpdated() {
    this.initDragging();
    this.setWidthProperties = this.setWidthProperties.bind(this);

    this.elementRef = this;
    this.barContainerRef = this.shadowRoot.querySelector(".fwc-scrubber-barContainer");
    this.initValues();
    this.validateInputs();
    if (this.hasValidInputs) {
      this.isScaleHidden = false;
      this.initOptions();
      this.setWidthProperties();
    } else {
      this.isScaleHidden = true;
    }
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "parentOpened") {
        this.setWidthProperties();
      }
    });
  }

  initDragging() {
    const leftChip = this.shadowRoot.querySelector(".fwc-left-chip") || null;
    const rightChip = this.shadowRoot.querySelector(".fwc-right-chip") || null;
    let isDraggingLeftChip = false;
    let isDraggingRightChip = false;

    this.addEventListener("mousedown", (e) => {
      isDraggingLeftChip = e.composedPath().some((path) => path === leftChip);
      isDraggingRightChip = e.composedPath().some((path) => path === rightChip);
      if (isDraggingLeftChip || isDraggingRightChip) {
        this.isDragging = true;
      }
    });
    this.addEventListener("touchstart", (e) => {
      // https://stackoverflow.com/questions/7056026/variation-of-e-touches-e-targettouches-and-e-changedtouches
      if (e.changedTouches && e.changedTouches.length == 1) {
        isDraggingLeftChip = e.composedPath().some((path) => path === leftChip);
        isDraggingRightChip = e.composedPath().some((path) => path === rightChip);
        if (isDraggingLeftChip || isDraggingRightChip) {
          this.isDragging = true;
        }
      }
    });
    this.addEventListener("mousemove", (e) => {
      if (this.isDragging && (isDraggingLeftChip || isDraggingRightChip)) {
        this.handleChipMove(e, isDraggingLeftChip ? "min" : "max");
      }
    });
    this.addEventListener(
      "touchmove",
      (e) => {
        if (e.changedTouches && e.changedTouches.length == 1) {
          if (this.isDragging && (isDraggingLeftChip || isDraggingRightChip)) {
            e.preventDefault();
            this.handleChipMove(e, isDraggingLeftChip ? "min" : "max");
          }
        }
      },
      { passive: false }
    );
    this.addEventListener("mouseup", () => {
      this.isDragging = false;
      if (isDraggingLeftChip || isDraggingRightChip) {
        this.dispatchChipPositions();
      }
      isDraggingLeftChip = false;
      isDraggingRightChip = false;
    });
    this.addEventListener("touchend", (e) => {
      if (e.changedTouches && e.changedTouches.length == 1) {
        this.isDragging = false;
        if (isDraggingLeftChip || isDraggingRightChip) {
          this.dispatchChipPositions();
        }
        isDraggingLeftChip = false;
        isDraggingRightChip = false;
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.setWidthProperties);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.setWidthProperties);
    super.disconnectedCallback();
  }

  initValues() {
    this.leftChipValue = this.leftChipValue !== undefined ? parseInt(this.leftChipValue) : undefined;
    this.rightChipValue = this.rightChipValue !== undefined ? parseInt(this.rightChipValue) : undefined;

    // allows for custom range other than 0 - 100
    this.rangeMinimum = this.range ? this.range[0] : this.SCALE_MIN;
    this.rangeMaximum = this.range ? this.range[1] : this.SCALE_MAX;

    //hide the min chip if no value is passed
    this.hasSingleChip = this.leftChipValue === undefined;

    // the min and max values set by the project, will default to 0 and 100
    this.leftChipValue = this.leftChipValue !== undefined || null ? this.leftChipValue : this.rangeMinimum;
    this.rightChipValue = this.rightChipValue !== undefined ? this.rightChipValue : this.rangeMaximum;

    // the base values are used to keep the slider scale from 0 - 100
    this.minScale = ((this.leftChipValue - this.rangeMinimum) / (this.rangeMaximum - this.rangeMinimum)) * 100;
    this.maxScale = ((this.rightChipValue - this.rangeMinimum) / (this.rangeMaximum - this.rangeMinimum)) * 100;

    // Move Chip in stepSize increments;
    this.stepSize = 100 / (this.rangeMaximum - this.rangeMinimum);

    this.setDisplayValues();
  }

  initOptions() {
    // set fieldset legend caption for screen readers
    this.screenReaderCaption = this.screenReaderCaption ? this.screenReaderCaption : "use arrow keys to set minimum and maximum slider values";
    // set data-analytics attributes
    this.dataAnalyticsMin = this.dataAnalyticsMin || "";
    this.dataAnalyticsMax = this.dataAnalyticsMax || "";
    // set aria-ariaLabelledby attribute
    this.labelAriaLabelledby = this.labelAriaLabelledby || "";
  }

  // DOM event handlers
  handleChipMove(e, chip) {
    const val = chip === "min" ? "minScale" : "maxScale";
    const valActive = chip === "min" ? "isLeftChipActive" : "isRightChipActive";
    const valActiveAlt = chip === "min" ? "isRightChipActive" : "isLeftChipActive";

    this.lastActive = chip;
    this.offSetLeft = this.calculateOffset(this.elementRef);

    if (this.isDragging) {
      this[valActive] = true;
      this[valActiveAlt] = false;
      let offsetInPixels;

      if (e.type === "touchmove") {
        offsetInPixels = e.changedTouches[0].pageX - this.offSetLeft;
      } else {
        offsetInPixels = e.clientX - this.offSetLeft;
      }

      let offsetPercentage = (offsetInPixels / this.totalWidth) * 100;

      if (this.canMove(offsetPercentage)) {
        this[val] = offsetPercentage;
      } else {
        this.handleChipOverlap(offsetPercentage, chip);
        this.handleChipRangeEnds(offsetPercentage, chip);
      }

      this.setDisplayValues();
      this.requestUpdate();
    }
  }

  handleChipOverlap(offsetPercentage, chip) {
    //lay the chips on top off each other when you drag past the other chip
    if (chip === "min") {
      this.minScale = offsetPercentage > this.minScale ? this.maxScale : this.minScale;
    } else {
      this.maxScale = offsetPercentage < this.maxScale ? this.minScale : this.maxScale;
    }
  }

  handleChipRangeEnds(offsetPercentage, chip) {
    // position chip to very end of range if you drag outside the range/component/page
    const val = chip === "min" ? "minScale" : "maxScale";
    const valAlt = chip === "min" ? "maxScale" : "minScale";
    if (offsetPercentage >= 100 && this[val] > this[valAlt]) {
      this[val] = this.SCALE_MAX;
    }
    if (offsetPercentage <= 0 && this[val] < this[valAlt]) {
      this[val] = this.SCALE_MIN;
    }
  }

  handleSliderKey(e, chip) {
    //keyboard interaction to move the slider
    const val = chip === "min" ? "minScale" : "maxScale";
    const valAlt = chip === "min" ? "maxScale" : "minScale";
    const valActive = chip === "min" ? "isLeftChipActive" : "isRightChipActive";
    const valActiveAlt = chip === "min" ? "isRightChipActive" : "isLeftChipActive";
    let newPos = 0;

    this[valActive] = true;
    this[valActiveAlt] = false;
    this.lastActive = chip;
    switch (e.key) {
      case "Left": // Edge specific value
      case "ArrowLeft":
      case "Down": // Edge specific value
      case "ArrowDown":
        newPos = Math.round(((this[val] - this.stepSize) * 100) / 100);
        if (this.canMove(newPos) && newPos >= this.SCALE_MIN) {
          this[val] -= this.stepSize;
          this.setDisplayValues();
        }
        e.preventDefault();
        break;
      case "Right": // Edge specific value
      case "ArrowRight":
      case "Up": // Edge specific value
      case "ArrowUp":
        newPos = Math.round(((this[val] + this.stepSize) * 100) / 100);
        if (this.canMove(newPos) && newPos <= this.SCALE_MAX) {
          this[val] += this.stepSize;
          this.setDisplayValues();
        }
        e.preventDefault();
        break;
      case "Home":
        if (this.hasSingleChip || chip === "min") {
          this[val] = this.SCALE_MIN;
        }
        if (chip === "max") {
          this[val] = this.canMove(this.SCALE_MIN) ? this.SCALE_MIN : this[valAlt];
        }
        this.setDisplayValues();
        e.preventDefault();
        break;
      case "End":
        if (this.hasSingleChip || chip === "max") {
          this[val] = this.SCALE_MAX;
        }
        if (chip === "min") {
          this[val] = this.canMove(this.SCALE_MAX) ? this.SCALE_MAX : this[valAlt];
        }
        this.setDisplayValues();
        e.preventDefault();
        break;
      case "PageUp":
        newPos = Math.round(((this[val] + this.stepSize * 10) * 100) / 100);
        if (chip === "min") {
          this[val] = this.canMove(newPos) ? this[val] + this.stepSize * 10 : this[valAlt];
        } else {
          this[val] = this.canMove(newPos) ? this[val] + this.stepSize * 10 : this.SCALE_MAX;
        }
        this.setDisplayValues();
        e.preventDefault();
        break;
      case "PageDown":
        newPos = Math.round(((this[val] - this.stepSize * 10) * 100) / 100);
        if (chip === "max") {
          this[val] = this.canMove(newPos) ? this[val] - this.stepSize * 10 : this[valAlt];
        } else {
          this[val] = this.canMove(newPos) ? this[val] - this.stepSize * 10 : this.SCALE_MIN;
        }
        this.setDisplayValues();
        e.preventDefault();
        break;
    }

    this.dispatchChipPositions();
  }

  canMove(newPos) {
    let canMove = true;

    //only one chip handler active
    if (this.hasSingleChip) {
      canMove = newPos >= this.SCALE_MIN && newPos <= this.SCALE_MAX;
      return canMove;
    }

    //if 2 chip handlers are active
    if (this.isLeftChipActive) {
      canMove = newPos >= this.SCALE_MIN && newPos <= this.maxScale;
    } else {
      canMove = newPos <= this.SCALE_MAX && newPos >= this.minScale;
    }

    return canMove;
  }

  setWidthProperties() {
    this.totalWidth = this.barContainerRef ? this.barContainerRef.offsetWidth : 1;
  }

  setDisplayValues() {
    const range = this.rangeMaximum - this.rangeMinimum;
    if (this.isPercent) {
      this.displayMinValue = Math.round(this.minScale);
      this.displayMaxValue = Math.round(this.maxScale);
    } else {
      this.displayMinValue = Math.round((this.minScale * range) / 100 + this.rangeMinimum);
      this.displayMaxValue = Math.round((this.maxScale * range) / 100 + this.rangeMinimum);
    }

    this.requestUpdate();
  }

  dispatchChipPositions() {
    this.dispatchEvent(
      new CustomEvent("fwc-scrubber-values", {
        detail: { min: this.displayMinValue, max: this.displayMaxValue },
        bubbles: true,
        displayMinValue: this.displayMinValue,
      })
    );
  }

  updateChip(activeChip) {
    if (activeChip === "leftChip") {
      this.isLeftChipActive = false;
    } else {
      this.isRightChipActive = false;
    }
    this.requestUpdate();
  }

  setFocus(e) {
    setTimeout(() => {
      e && e.target && e.target.focus();
    }, 0);
  }

  calculateOffset(elementRef) {
    const rect = elementRef.getBoundingClientRect();
    return rect.left;
  }

  handleRangeClick(e) {
    //clicks inside of range bar
    this.offSetLeft = this.calculateOffset(this.elementRef);
    const offsetInPixels = e.clientX - this.offSetLeft;
    const offsetPercentage = (offsetInPixels / this.totalWidth) * 100;

    if (Math.abs(offsetPercentage - this.minScale) < Math.abs(offsetPercentage - this.maxScale) && !this.hasSingleChip) {
      this.minScale = offsetPercentage;
    } else {
      this.maxScale = offsetPercentage;
    }

    this.setDisplayValues();
    this.dispatchChipPositions();
  }

  handleBarClick(e) {
    //clicks outside of range bar
    this.offSetLeft = this.calculateOffset(this.elementRef);
    const offsetInPixels = e.clientX - this.offSetLeft;
    const offsetPercentage = (offsetInPixels / this.totalWidth) * 100;

    if (offsetPercentage < this.minScale && !this.hasSingleChip) {
      this.minScale = offsetPercentage;
    } else {
      this.maxScale = offsetPercentage;
    }

    this.setDisplayValues();
    this.dispatchChipPositions();
  }

  /* eslint-disable no-console */
  validateInputs() {
    const isLeftChipNaN = isNaN(parseInt(this.leftChipValue));
    const isRightChipNaN = isNaN(parseInt(this.rightChipValue));

    // checks if range is in correct tuple (min, max) format;
    try {
      if (this.rangeMinimum > this.rangeMaximum) {
        this.hasValidInputs = false;
        throw new Error(
          `Range minimum (${this.rangeMinimum}) must be less than range maximum (${this.rangeMaximum}) \n
          Did you mean [${this.rangeMaximum}, ${this.rangeMinimum}]?
          `
        );
      }
    } catch (error) {
      console.error(error);
      return;
    }
    // checks if left chip value is a number
    try {
      if (isLeftChipNaN) {
        this.hasValidInputs = false;
        throw new Error("Invalid type given for left chip value.");
      }
    } catch (error) {
      console.error(error);
      return;
    }
    // checks if right chip value is a number
    try {
      if (isRightChipNaN) {
        this.hasValidInputs = false;
        throw new Error("Invalid type given for right chip value.");
      }
    } catch (error) {
      console.error(error);
      return;
    }
    // checks if left chip start val is within the specified range
    try {
      if (this.leftChipValue < this.rangeMinimum || this.leftChipValue > this.rangeMaximum) {
        this.hasValidInputs = false;
        throw new Error(`Left chip start value (${this.leftChipValue}) must be within range (${this.rangeMinimum} ~ ${this.rangeMaximum}).`);
      }
    } catch (error) {
      console.error(error);
    }
    // checks if left chip start val is less than right chip start val
    try {
      if (this.leftChipValue > this.rightChipValue) {
        this.hasValidInputs = false;
        throw new Error(`Left chip value (${this.leftChipValue}) needs to be less than right chip start value (${this.rightChipValue}).`);
      }
    } catch (error) {
      console.error(error);
      return;
    }
    // checks if right chip start val is greater than left chip start val
    try {
      if (this.rightChipValue < this.leftChipValue) {
        this.hasValidInputs = false;
        throw new Error(`Right chip value (${this.rightChipValue}) needs to be greater than left chip start value (${this.leftChipValue}).`);
      }
    } catch (error) {
      console.error(error);
    }
    // checks if right chip start val is within  the specified range
    try {
      if (this.rightChipValue > this.rangeMaximum || this.rightChipValue < this.rangeMinimum) {
        this.hasValidInputs = false;
        throw new Error(`Right chip start value (${this.rightChipValue}) must be within range (${this.rangeMinimum} ~ ${this.rangeMaximum}).`);
      }
    } catch (error) {
      console.error(error);
    }
  }
  /* eslint-enable no-console */

  _drawChipSvg() {
    return html`
      <svg focusable="false" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" focusable="false">
        <path d="M16,0 C24.836556,0 32,7.163444 32,16 C32,24.836556 24.836556,32 16,32 C7.163444,32 0,24.836556 0,16 C0,7.163444 7.163444,0 16,0 Z M16,7 C14.9456382,7 14.0818349,7.81587779 14.0054857,8.85073766 L14,9 L14,24 C14,25.1045695 14.8954305,26 16,26 C17.0543618,26 17.9181651,25.1841222 17.9945143,24.1492623 L18,24 L18,9 C18,7.8954305 17.1045695,7 16,7 Z M8,11 C6.9456382,11 6.08183488,11.8158778 6.00548574,12.8507377 L6,13 L6,20 C6,21.1045695 6.8954305,22 8,22 C9.0543618,22 9.91816512,21.1841222 9.99451426,20.1492623 L10,20 L10,13 C10,11.8954305 9.1045695,11 8,11 Z M24,11 C22.9456382,11 22.0818349,11.8158778 22.0054857,12.8507377 L22,13 L22,20 C22,21.1045695 22.8954305,22 24,22 C25.0543618,22 25.9181651,21.1841222 25.9945143,20.1492623 L26,20 L26,13 C26,11.8954305 25.1045695,11 24,11 Z" />
      </svg>
    `;
  }

  render() {
    return html`
      <div class="fwc-scrubber-wrapper ${this.isScaleHidden && "hide"}">
        <fieldset>
          <legend class="sr-only">${this.screenReaderCaption}</legend>

          <div class="fwc-scrubber-slider-wrapper">
            <div class="fwc-scrubber-barContainer" #barContainerRef>
              <div class="fwc-scrubber-bar" @click="${(event) => this.handleBarClick(event)}"></div>
              <div class="fwc-scrubber-range" @click="${(event) => this.handleRangeClick(event)}" style="left: ${this.minScale}%; right:${100 - this.maxScale}% "></div>
            </div>

            <div role="slider" tabindex="0" style="left: ${this.minScale}%" class="fwc-left-chip fwc-scrubber-chip min ${this.hasSingleChip ? "hide" : ""} ${this.isLeftChipActive ? "active" : ""} ${this.lastActive === "min" ? "last-active" : ""}" @keydown="${(event) => this.handleSliderKey(event, "min")}" @blur="${() => this.updateChip("leftChip")}" @focusout="${() => this.updateChip("leftChip")}" @mousedown="${(event) => this.setFocus(event)}" aria-valuemax="${this.rangeMaximum}" aria-valuemin="${this.rangeMinimum}" aria-valueNow="${this.displayMinValue}" aria-disabled="${this.hasSingleChip}" aria-valueText="${this.isPercent ? this.displayMinValue + " Percent" : this.displayMinValue}" aria-labelledBy="${this.labelAriaLabelledby}" data-analytics="${this.dataAnalyticsMin}" aria-orientation="horizontal">
              ${this._drawChipSvg()}
              <span class="fwc-arrow-box ${this.hideHandleLabel ? "hide" : ""}" role="presentation">
                <span class="left-chip-val">${this.displayMinValue}</span>
                ${this.isPercent ? html`%` : ""}
              </span>
            </div>

            <div role="slider" tabindex="0" style="left: ${this.maxScale}%" class="fwc-right-chip fwc-scrubber-chip max ${this.isRightChipActive ? "active" : ""} ${this.lastActive === "max" ? "last-active" : ""}" @keydown="${(event) => this.handleSliderKey(event, "max")}" @blur="${() => this.updateChip("rightChip")}" @focusout="${() => this.updateChip("rightChip")}" @mousedown="${(event) => this.setFocus(event)}" aria-valuemax="${this.rangeMaximum}" aria-valuemin="${this.rangeMinimum}" aria-valueNow="${this.displayMaxValue}" aria-disabled="false" aria-valueText="${this.isPercent ? this.displayMaxValue + " Percent" : this.displayMaxValue}" aria-labelledBy="${this.labelAriaLabelledby}" data-analytics="${this.dataAnalyticsMax}" aria-orientation="horizontal">
              ${this._drawChipSvg()}
              <span class="fwc-arrow-box ${this.hideHandleLabel ? "hide" : ""}" role="presentation">
                <span class="right-chip-val">${this.displayMaxValue}</span>
                ${this.isPercent ? html`%` : ""}
              </span>
            </div>
          </div>

          <div class="fwc-scrubber-range-labels ${this.hideRangeLabel ? "hide" : ""}">
            <span class="min">${this.rangeMinimum} ${this.rangeLabel}</span>
            <span class="max">${this.rangeMaximum} ${this.rangeLabel}</span>
          </div>
        </fieldset>
      </div>
    `;
  }
}

class FwcScrubber extends FwcBase {
  constructor() {
    super("fwc-scrubber", FwcScrubberComponent);
  }
}

class FwcTabsComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        .slot-container {
          display: none;
        }
        .slot-container.selected {
          display: block;
        }
        :host > div {
          display: flex;
          align-items: stretch;
          justify-content: flex-start;
        }
        :host > div.inline {
          display: inline-flex;
        }
        section {
          padding: calc(2 * var(--fwcu));
          padding-top: calc(3 * var(--fwcu));
        }
        section:focus {
          outline: var(--fwc-focus);
        }
        button {
          flex: 1 1 auto;
          background: white;
          color: var(--color-primary);
          border: 1px solid var(--color-gray-six);
          border-right: 0px;
          padding: calc(1.5 * var(--fwcu)) calc(2 * var(--fwcu));
          line-height: 1;
          margin: 0;
          border-radius: 0;
          cursor: pointer;
          position: relative;
          font-family: var(--font-sans-serif);
          font-size: calc(2 * var(--fwcu));
          font-weight: 600;
          min-height: calc(5 * var(--fwcu));
        }
        button:first-child {
          border-top-left-radius: var(--border-radius);
          border-bottom-left-radius: var(--border-radius);
        }
        button:last-child {
          border-right: 1px solid var(--color-gray-six);
          border-top-right-radius: var(--border-radius);
          border-bottom-right-radius: var(--border-radius);
        }
        button.selected {
          background: var(--color-primary);
          color: white;
        }
        button:hover:not(.selected) {
          background-color: var(--color-primary-hover);
          color: white;
        }

        .sm button {
          font-size: calc(1.5 * var(--fwcu));
          padding-top: calc(1 * var(--fwcu));
          padding-bottom: calc(1 * var(--fwcu));
          min-height: calc(3.75 * var(--fwcu));
        }
      `,
    ];
  }

  static get properties() {
    return {
      _current: {},
      labelAriaTabList: { attribute: "label-aria-tab-list", type: String },
      size: { type: String }, // sm | lg
      tabs: { type: Array },
      tabsOnly: { attribute: "tabs-only", type: Boolean },
    };
  }

  constructor() {
    super();
    this.tabs = [];
    this.size = "lg";
    this._current = 0;
  }

  firstUpdated() {
    this._initActiveTab();
    this.size = this.tabsOnly ? "lg" : this.size;
  }

  _initActiveTab() {
    for (let i = 0; i < this.tabs.length; i++) {
      if (this.tabs[i].active) {
        this._current = i;
        break;
      }
    }
  }

  _onTabSelect(tab) {
    if (this.tabs[tab].title !== this._current) {
      for (let tab of this.tabs) {
        tab.active = false;
      }
      this.tabs[tab].active = true;
      this._current = tab;
      this._emitEvent(this.tabs[tab]);
    }
  }

  _handleKeyDown(e) {
    switch (e.key) {
      case "Right":
      case "ArrowRight":
        e.preventDefault();
        e.stopPropagation();
        this._switchTab("next");
        break;
      case "Left":
      case "ArrowLeft":
        e.preventDefault();
        e.stopPropagation();
        this._switchTab("prev");
        break;
      case "Home":
        this._onTabSelect(0);
        break;
      case "End":
        this._onTabSelect(this.tabs.length - 1);
        break;
    }
  }

  _switchTab(dir) {
    const end = this.tabs.length - 1;
    if (dir === "next") {
      let next = this._current + 1;
      this._current = next > end ? 0 : next;
    } else {
      let prev = this._current - 1;
      this._current = prev < 0 ? end : prev;
    }
    const tab = this.shadowRoot.getElementById("fwc-TabBtn-" + this._current);
    this._onTabSelect(this._current);
    tab.focus();
  }

  _emitEvent(tab) {
    const event = new CustomEvent("fwc-tabs-clicked", {
      detail: tab,
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  _buildSlots() {
    const slots = html`${this.tabs.map((slot, i) => {
      const slotID = slot.slotID ? slot.slotID : slot.title;
      return html`
        <div class="slot-container ${i === this._current ? "selected" : ""}" id=${this._current + "_" + i}>
          <slot aria-labelledby="fwc-TabBtn-${i}" role="tabpanel" id="fwc-TabBody-${i}" name="fwc-tab-${slotID}"></slot>
        </div>
      `;
    })}`;

    return slots;
  }

  _buildTabs() {
    const tabs = html`${this.tabs.map((tab, i) => {
      const selected = i === this._current;
      return html` <button tabindex=${this._current === i ? 0 : -1} id="fwc-TabBtn-${i}" type="button" role="tab" aria-setsize="${this.tabs.length}" aria-selected="${selected}" aria-controls="fwc-TabBody-${i}" aria-label="${tab.title}" class="${selected ? "selected" : ""}" @click="${() => this._onTabSelect(i)}" @keydown=${this._handleKeyDown}>${tab.title}</button> `;
    })}`;
    return tabs;
  }

  _slotContainer() {
    return html`<section tabindex="0">${this._buildSlots()}</section>`;
  }

  render() {
    return html`
      <div role="tablist" aria-label="${this.labelAriaTabList}" class="${this.size === "sm" ? "inline" : ""} ${this.size}">${this._buildTabs()}</div>
      ${!this.tabsOnly ? this._slotContainer() : null}
    `;
  }
}

class FwcTabs extends FwcBase {
  constructor() {
    super("fwc-tabs", FwcTabsComponent);
  }
}

class FwcTimelineComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        @keyframes dot {
          0% {
            width: 0;
            height: 0;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            width: calc(2.5 * var(--fwcu));
            height: calc(2.5 * var(--fwcu));
          }
        }
        @keyframes info {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        :host {
          position: relative;
        }
        .dot-container,
        .info-container {
          width: 100%;
          position: relative;
          height: calc(2.5 * var(--fwcu));
          opacity: 0;
        }
        .info-container {
          bottom: 0;
          background: none;
        }
        .info-container.start-anim {
          opacity: 1;
          transition-delay: 500ms;
        }
        .bar-container {
          background-color: var(--color-gray-five);
          position: absolute;
          width: 100%;
          height: calc(2.5 * var(--fwcu));
        }
        .info {
          position: absolute;
          display: flex;
          justify-content: center;
          flex-direction: column;
          top: calc(-5 * var(--fwcu));
          text-align: center;
          margin: 0;
          font-size: var(--default-font-size);
          width: calc(19 * var(--fwcu));
          color: var(--color-gray-one);
          animation-name: info;
          animation-fill-mode: both;
          animation-duration: 500ms;
          animation-delay: 750ms;
          animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
        }
        .info span.date {
          padding: 0;
          margin: 0;
          line-height: 1;
          font-weight: 400;
        }
        .info:nth-of-type(odd) {
          top: calc(7.5 * var(--fwcu));
        }
        .info::after {
          content: "";
          left: calc(50% - 1px);
          position: absolute;
          width: calc(0.25 * var(--fwcu));
          height: calc(3 * var(--fwcu));
          background-color: var(--color-alert-info);
        }
        .info.urgent::after {
          background-color: var(--color-alert-error);
        }
        .info:nth-of-type(even)::after {
          top: 100%;
        }
        .info:nth-of-type(odd)::after {
          bottom: 100%;
        }
        .bar {
          height: calc(2.5 * var(--fwcu));
          background: var(--color-primary);
          position: absolute;
          width: 0%;
          transition: all 500ms cubic-bezier(0.19, 1, 0.22, 1);
        }
        .bar.urgent {
          background-color: var(--color-alert-error);
        }
        .dot-container.start-anim {
          opacity: 1;
        }
        .dot {
          position: absolute;
          background-color: none;
          border-style: solid;
          border-color: black;
          background-color: var(--color-white);
          border-color: var(--color-primary);
          border-radius: 50%;
          width: calc(2.5 * var(--fwcu));
          height: calc(2.5 * var(--fwcu));
          animation-name: dot;
          animation-fill-mode: both;
          animation-duration: 500ms;
          animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
          transform-origin: center;
        }
        .dot.urgent {
          border-color: var(--color-util-one);
        }
        .dot:nth-of-type(2) {
          transition-delay: 100ms;
        }
        .dot:nth-of-type(3) {
          transition-delay: 200ms;
        }
        .dot:nth-of-type(4) {
          transition-delay: 300ms;
        }
        @media screen and (max-width: 600px) {
          ul {
            justify-content: center;
          }
          section.vertical {
            transform: rotate(90deg);
            transform-origin: center;
            margin-top: calc(18.75 * var(--fwcu)); /* 150px  */
            margin-bottom: calc(43.75 * var(--fwcu)); /* 350px  */
          }
          section.vertical .dot-container,
          section.vertical .info-container,
          section.vertical .bar-container {
            width: calc(62.5 * var(--fwcu));
            /* component is rotated 90 degrees with transform, width here is acting as height */
          }
          section.vertical .info {
            transform: rotate(-90deg);
          }
          section.vertical .info:nth-of-type(even) {
            top: calc(-12 * var(--fwcu));
          }
          section.vertical .info:nth-of-type(odd) {
            top: calc(12 * var(--fwcu));
          }
          section.vertical .info::after {
            top: calc(50% - 1px);
            height: calc(0.25 * var(--fwcu));
            width: calc(3 * var(--fwcu));
          }
          section.vertical .info:nth-of-type(even)::after {
            left: auto;
            right: 100%;
          }
          section.vertical .info:nth-of-type(odd)::after {
            left: auto;
            right: 0;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      milestones: { type: Array },
      vertical: { type: Boolean },
      title: { type: String },
      urgent: { type: Boolean },
      labelIncomplete: { attribute: "label-incomplete", type: String },
      labelComplete: { attribute: "label-complete", type: String },
      holdAnim: { attribute: "hold-anim", type: Boolean },
    };
  }

  constructor() {
    super();
    this.milestones = [];
    this.vertical = false;
    this.title = "milestones";
    this.urgent = false;
    this.labelIncomplete = "incomplete";
    this.labelComplete = "complete";
    this.holdAnim = false;
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);
    if (name === "milestones" && oldVal !== newVal) {
      this._renderMilestones();
    }
  }

  updated() {
    if (!this.holdAnim) {
      this._startAnimation();
    }
  }

  triggerAnimation() {
    if (this.holdAnim) {
      this._startAnimation();
    }
  }

  _renderMilestones() {
    const count = this.milestones.length - 1;
    const percent = 100 / count;
    const Info = this.milestones.map((info, i) => {
      const iStyle = {
        left: `calc(${i * percent}% - ${9.5 * baseUnit - 1}px)`,
      };
      return html`
        <h5 class="info ${this.urgent ? "urgent" : ""}" style=${styleMap(iStyle)}>
          <span class="label">${info.label}</span>
          <span class="date">${info.date}</span>
          <span class="sr-only">${info.complete ? this.labelComplete : this.labelIncomplete}</span>
        </h5>
      `;
    });
    const Dots = this.milestones.map((dot, i) => {
      if (!dot.start && i !== 0) {
        return html` <div class="dot ${this.urgent ? "urgent" : ""}"></div> `;
      }
    });

    const Bar = html`<div class="bar ${this.urgent ? "urgent" : ""}"></div>`;

    return {
      bar: Bar,
      dots: Dots,
      info: Info,
    };
  }

  _startAnimation() {
    const childNodes = this.shadowRoot.querySelector(".dot-container").childNodes;
    const bar = this.shadowRoot.querySelector(".bar");
    let complete = 0;
    const dots = Array.prototype.filter.call(childNodes, (node) => node.nodeType == Node.ELEMENT_NODE && node.nodeName === "DIV");
    const len = dots.length;
    this.shadowRoot.querySelector(".info-container").classList.add("start-anim");
    this.shadowRoot.querySelector(".dot-container").classList.add("start-anim");

    for (let d in dots) {
      let percent = 0;
      let num = Number(1 + +d);
      percent = (num / len) * 100;
      dots[d].style.left = "0%";
      // need a 'state' change to apply CSS transitions
      // half of dot width
      setTimeout(() => {
        dots[d].style.left = `calc(${percent}% - ${1.25 * baseUnit}px)`;
      }, 0);
    }

    this.milestones.forEach((c, i) => {
      if (c.complete && !c.start && i !== 0) complete++;
    });

    complete = (complete / (this.milestones.length - 1)) * 100;
    setTimeout(() => {
      bar.style.width = complete + "%";
    }, 0);

    //No dot for 1st milestone
    if (dots.length > 3) {
      console.error("Maximum of 4 Milestones has been exceeded. Component may not render properly.");
    }
  }

  render() {
    return html`
      <section class=${this.vertical ? "vertical" : ""}>
        <h4 class="sr-only">${this.title}</h4>
        <div class="info-container ${ this.holdAnim ? "" : "start-anim" }">${this._renderMilestones().info}</div>
        <div class="bar-container">${this._renderMilestones().bar}</div>
        <div class="dot-container ${ this.holdAnim ? "" : "start-anim" }">${this._renderMilestones().dots}</div>
      </section>
    `;
  }
}

class FwcTimeline extends FwcBase {
  constructor() {
    super("fwc-timeline", FwcTimelineComponent);
  }
}

class FwcTooltipComponent extends LitElement {
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          display: inline-block;
          width: auto;
        }
        :host *:not(h2) {
          font-family: var(--font-sans-serif);
        }
        .hidden {
          display: none;
        }
        section {
          display: none;
        }
        h2 {
          font-size: calc(3.25 * var(--fwcu));
          font-family: var(--font-serif);
          color: var(--color-gray-one);
          margin-top: 0;
          text-transform: capitalize;
        }
        .term {
          display: inline;
        }
        .term span {
          font-size: var(--default-font-size);
          padding-right: calc(0.5 * var(--fwcu));
        }
        .tip {
          opacity: 0;
          border: 1px solid var(--color-primary);
          border-radius: var(--border-radius);
          padding: calc(2 * var(--fwcu));
          max-width: calc(35 * var(--fwcu));
          max-height: calc(18 * var(--fwcu));
          background: white;
          box-shadow: var(--box-shadow);
          transition: all 500ms cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        .tip span {
          font-size: var(--default-font-size);
          color: var(--color-gray-one);
        }
        .tip-container {
          position: fixed;
          display: inline;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          pointer-events: none;
          z-index: 10;
          overflow: hidden;
        }
        .tip-container.show {
          overflow: visible;
        }
        .tip-container.show .tip {
          opacity: 1;
          pointer-events: all;
        }
        .tip-btn {
          border: none;
          background: none;
          padding: 0;
          cursor: pointer;
          display: inline;
        }
        .tip-btn span {
          color: var(--color-primary);
          border-bottom: 1px dotted var(--color-primary);
          font-size: var(--default-font-size);
          font-weight: 600;
        }

        .tip-btn.light span {
          color: var(--color-white);
          border-bottom: 1px dotted var(--color-white);
        }

        .tip-btn svg,
        ::slotted(svg) {
          height: calc(1.75 * var(--fwcu));
          width: calc(1.75 * var(--fwcu));
          fill: var(--color-primary);
        }
        .tip-btn.light svg,
        ::slotted(svg.light) {
          fill: var(--color-white);
        }

        .fwc-click-out {
          display: inline;
          position: fixed;
        }
        .fwc-click-out.show {
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 1;
        }
        .disabled {
          opacity: 0.3;
          cursor: default;
          pointer-events: none;
        }
      `,
    ];
  }

  static get properties() {
    return {
      icon: { type: Boolean },
      position: { type: Object },
      message: { type: String },
      show: { type: Boolean },
      term: { type: String },
      disabled: { attribute: "disabled", type: Boolean },
      light: { attribute: "light", type: Boolean },
      labelAriaTipTerm: { attribute: "label-aria-tip-term", type: String },
    };
  }

  constructor() {
    super();
    this.show = false;
    this.icon = false;
    this.message = "";
    this.tip = "";
    this.term = "";
    this.position = { transform: "translate(0,0)" };
    this._defaultSvg = "";
    this.labelAriaTipTerm = "Tooltip:";
    this.mobileBreak = breakpoints.sm;
    this.isMobile = () => window.innerWidth <= this.mobileBreak;

    this._handleWindowResize = debounce(() => {
      this.requestUpdate();
      this.updateComplete.then(() => {
        this._positionTooltip();
      });
    }, 250);
    this._handleScroll = debounce(() => {
      this.requestUpdate();
      this._positionTooltip();
    }, 150);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this._handleWindowResize);
    window.addEventListener("scroll", this._handleScroll);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._handleWindowResize);
    window.removeEventListener("scroll", this._handleScroll);
    super.disconnectedCallback();
  }

  firstUpdated() {
    this._positionTooltip();
    this.tip = this.message;
    this._setSvg();
  }

  _svg() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="51" height="50" viewBox="0 0 51 50" focusable="false" aria-hidden="true">
        <path d="M25,50 C11.2141927,50 0,38.7858073 0,25 C0,11.2141927 11.2141927,0 25,0 C38.7858073,0 50,11.2141927 50,25 C50,38.7858073 38.7858073,50 25,50 Z M25,45.8333333 C36.5315755,45.8333333 45.8333333,36.5315755 45.8333333,25 C45.8333333,13.4684245 36.5315755,4.16666667 25,4.16666667 C13.4684245,4.16666667 4.16666667,13.4684245 4.16666667,25 C4.16666667,36.5315755 13.4684245,45.8333333 25,45.8333333 Z M25,37.5 C20.4182943,37.5 16.6666667,33.7483724 16.6666667,29.1666667 L20.8333333,29.1666667 C20.8333333,31.4941406 22.672526,33.3333333 25,33.3333333 C27.327474,33.3333333 29.1666667,31.4941406 29.1666667,29.1666667 C29.1666667,27.5716146 28.141276,26.155599 26.6276042,25.6510417 L25.78125,25.390625 C24.0804036,24.8291016 22.9166667,23.2014974 22.9166667,21.4192708 L22.9166667,18.75 L27.0833333,18.75 L27.0833333,21.4192708 L27.9296875,21.6796875 C31.1360677,22.7457682 33.3333333,25.789388 33.3333333,29.1666667 C33.3333333,33.7483724 29.5817057,37.5 25,37.5 Z M22.9166667,16.6666667 L22.9166667,12.5 L27.0833333,12.5 L27.0833333,16.6666667 L22.9166667,16.6666667 Z" transform="matrix(1 0 0 -1 0 50)" />
      </svg>
    `;
  }

  _setSvg() {
    if (this._hasSlottedSvg() || !this.icon) {
      this._defaultSvg = "";
      return;
    }
    this._defaultSvg = this._svg();
  }

  _hasSlottedSvg() {
    const slot = this.shadowRoot.querySelector("slot");
    const childNodes = slot ? slot.assignedNodes({ flatten: true }) : [];
    return Array.prototype.some.call(childNodes, (node) => node.nodeName === "svg");
  }

  _handleBlur() {
    if (this.show) {
      this._closeTooltip();
    }
  }

  _handleKeyDown(event) {
    switch (event.code) {
      case "Escape":
        if (this.show) {
          this._closeTooltip();
        }
        break;
      case "Enter":
      case "Space":
        if (!this.show) {
          this._openTooltip();
        }
        break;
    }
  }

  _openTooltip() {
    this.tip = this.message;
    this._positionTooltip();
    this.show = true;
  }

  _closeTooltip() {
    this.show = false;
    const event = new CustomEvent("fwc-tooltip-close", {
      detail: this,
      bubbles: false,
    });
    this.dispatchEvent(event);
  }

  _positionTooltip() {
    this.isMobile();
    const host = this.getBoundingClientRect();
    // need to figure out why the tip getBoundingClientRect is off when resizing screen from mobile to desktop
    const tip = this.shadowRoot.querySelector(".tip").getBoundingClientRect();
    let left = 0;
    let top = 0;
    top = host.top - tip.height - 10;
    left = host.right - host.width;
    if (top <= 0) {
      top = host.top + host.height + 10;
    }
    // if starting host point and tip width extends beyond window. subtract tip width and -40 more pxs
    if (host.left + tip.width > window.innerWidth) {
      left = window.innerWidth - tip.width - 40;
    }
    top = top + "px";
    left = left + "px";
    this.position.transform = `translate(${left},${top})`;
    this.requestUpdate();
  }

  _toggleTooltip() {
    if (this.show) {
      this._closeTooltip();
    } else {
      this._openTooltip();
    }
  }

  _initTooltip() {
    return html` <div class="tip-container ${this.show ? "show" : ""}" aria-hidden="${!this.show}">
      <div class="tip" style=${styleMap(this.position)}>
        <span id="TooltipDef" role="tooltip" aria-label="${this.labelAriaTipTerm} ${this.tip}">${this.tip}</span>
      </div>
    </div>`;
  }

  _tooltipBtn() {
    return html`
      <div id="Tooltip" class="tip-btn ${this.light ? "light" : ""} ${this.disabled ? "disabled" : ""}" tabindex="${this.disabled ? -1 : 0}" aria-describedby="TooltipDef" @click=${this._toggleTooltip} @keydown=${this._handleKeyDown} @blur=${this._handleBlur} aria-hidden="${this.disabled}">
        <span class="${this.icon || this._hasSlottedSvg() ? "sr-only" : ""}">${this.term}</span>
        ${this._defaultSvg}
        <!-- slot is used for custom svg -->
        <slot></slot>
      </div>
    `;
  }

  render() {
    return html`
      ${this._tooltipBtn()}
      <div @click=${this._closeTooltip} class="fwc-click-out ${this.show ? "show" : ""}"></div>
      ${this._initTooltip()}
    `;
  }
}

class FwcTooltip extends FwcBase {
  constructor() {
    super("fwc-tooltip", FwcTooltipComponent);
  }
}

export { FwcAlert, FwcAlertSnackbar, FwcAnimBlob, FwcAnimColDrop, FwcBackToTop, FwcBars, FwcBarsStacked, FwcCardCircle, FwcCardMediaImage, FwcCarouselGeneric, FwcDatePicker, FwcDateTimePicker, FwcExpand, FwcExpandMobileCol, FwcExpandMulti, FwcFeaturette, FwcFooterSticky, FwcHeaderCompound, FwcHeaderPage, FwcHero, FwcIconText, FwcInputTypeahead, FwcLeftRailBase, FwcMilestones, FwcModal, FwcModalCelebratory, FwcModalIllustration, FwcModalSlide, FwcPagination, FwcPanel, FwcPanelNav, FwcPreloader, FwcProgressLinear, FwcProgressStandard, FwcScrubber, FwcTabs, FwcTimeline, FwcTooltip };
